import { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from "react";
import { Trans, useTranslation } from 'react-i18next';
import { tokenize, rankMatch, matchPosition } from '@/lib/searchMatch';
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { PriceRangeBand } from "@/pages/CPTCodes";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import BackgroundMedia from "@/components/BackgroundMedia";
import FlagBackground, { tunerTap } from "@/components/FlagBackground";
import ScenarioSectionRail from "@/components/ScenarioSectionRail";
import ScenarioSectionModal from "@/components/ScenarioSectionModal";
import { isNativeApp } from "@/lib/platform";
import { Heart, Baby, Ambulance, Activity, Pill, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Calendar, Building2, Search, X, Maximize2, Minimize2, DollarSign, TrendingDown, TrendingUp, ThumbsUp, Eye, Bookmark, CheckCircle2, User, Plus, ExternalLink, MapPin, UserPlus, Loader2, HandHeart, Headphones, Flame, Bone, Scissors, Ribbon, Shield, Wind, Replace, Dna, Layers, Receipt, Scale, Globe, Sparkles, ArrowRight, Download } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CreateScenarioDialog } from "@/components/CreateScenarioDialog";
import { scenarioStorage } from "@/lib/scenarioStorage";
import { batchedIn } from "@/lib/batchedQuery";
import { familyStorage } from "@/lib/familyStorage";
import { PlanSummaryFloatingWidget } from "@/components/family/PlanSummaryFloatingWidget";
import { toast } from "sonner";
import { ScenarioClassVideo } from "@/components/scenarios/ScenarioClassVideo";
import { Helmet } from "react-helmet-async";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useUserLocation } from "@/hooks/useUserLocation";
import { LocationChooser } from "@/components/scenarios/LocationChooser";
import { NearbyHospitalsTable } from "@/components/scenarios/NearbyHospitalsTable";
import DrgBundleExplainer from "@/components/scenarios/DrgBundleExplainer";
import { AdvisorNotes } from "@/components/scenarios/AdvisorNotes";
import { ScenarioRankings, type SeoRankingsData } from "@/components/scenarios/ScenarioRankings";
import { AiInsights } from "@/components/scenarios/AiInsights";
import { AssistancePrograms } from "@/components/scenarios/AssistancePrograms";
import { ScenarioCareResources, matchResources } from "@/components/scenarios/ScenarioCareResources";
import { JSONLDGenerator } from "@/utils/jsonLd";
import { ScenarioInternationalComparison } from "@/components/scenarios/ScenarioInternationalComparison";
import { useStateInternationalStats, type InternationalScenarioCost } from "@/hooks/useStateInternationalStats";
import { DignityEducationTab } from "@/components/dignity/DignityEducationTab";
import { fillCostTemplate, scenarioContext } from "../../functions/lib/cost-template";
import { DignityNarrationPlayer } from "@/components/dignity/DignityNarrationPlayer";
import { DignityAvatar } from "@/components/scenario-builder/DignityAvatar";
import { useLocalizedCategories, SCENARIO_CATEGORIES, scenarioMatchesCategory, getCategoryByKey } from '@/config/scenarioCategories';
import { ScenarioCard, type ScenarioCardData, type CardTheme } from "@/components/ScenarioCard";
import { useTranslatedContentBatch } from '@/hooks/useTranslatedContent';

// Card theme tokens — mirror ConsumerSearch's T_LIGHT/T_DARK so the full
// ScenarioCard renders identically on the /scenarios browse grid and the
// Search tab.
const SCENARIO_CARD_T_LIGHT: CardTheme = {
  cardBg: "#ffffff", border: "rgba(var(--civic-ink-rgb),0.12)", shadow: "rgba(0,0,0,0.08)",
  red: "var(--civic-red)", gold: "var(--civic-gold)", green: "var(--civic-money)", blue: "var(--civic-navy)",
  text: "var(--civic-ink)", textMuted: "rgba(var(--civic-ink-rgb),0.62)",
};
const SCENARIO_CARD_T_DARK: CardTheme = {
  cardBg: "var(--civic-navy-deeper)", border: "rgba(var(--civic-cream-rgb),0.12)", shadow: "rgba(0,0,0,0.35)",
  shadowTopHighlight: "rgba(255,255,255,0.06)",
  red: "var(--civic-red)", gold: "var(--civic-gold)", green: "#4ade80", blue: "#5B9BD5",
  text: "var(--civic-cream)", textMuted: "rgba(var(--civic-cream-rgb),0.55)",
};
// Sibling-brand card palettes. ScenarioCard already reads red/blue/gold for its
// phase bars (see the note above `phaseColors`), so the cards inherit each
// brand's identity from here rather than rendering AHP's flag palette on every
// domain. Both siblings are dark-ground brands, so there is no light variant.
const SCENARIO_CARD_T_LUIGIS: CardTheme = {
  cardBg: "#050d05", border: "rgba(0,255,0,0.22)", shadow: "rgba(0,0,0,0.55)",
  shadowTopHighlight: "rgba(0,255,0,0.07)",
  red: "#ff0000", gold: "#00ffff", green: "#00ff00", blue: "#00b140",
  text: "#d6ffd6", textMuted: "rgba(0,255,0,0.55)",
};
const SCENARIO_CARD_T_ANTIGREED: CardTheme = {
  cardBg: "#0f1d0b", border: "rgba(245,230,211,0.16)", shadow: "rgba(0,0,0,0.5)",
  shadowTopHighlight: "rgba(255,102,0,0.07)",
  red: "#ff6600", gold: "#e8d4b0", green: "#7fa86b", blue: "#c98b3a",
  text: "#f5e6d3", textMuted: "rgba(245,230,211,0.58)",
};

// Category → icon, matching the map embed's SCENARIO_CATEGORY_ICONS (HomeBeta).
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  emergency: Flame, orthopedic: Bone, spine: Activity, surgery: Scissors,
  "womens-health": Baby, cardiology: Heart, cancer: Ribbon, immune: Shield,
  diabetes: Pill, pulmonology: Wind, transplant: Replace, "gene-therapy": Dna,
};

// Reverse of ScenarioCard's ICON_MAP (which only renders these 4; anything else
// falls back to Activity). Lets us derive a card icon_name from the icon
// component our scenarios already carry, matching the Search tab exactly.
const ICON_COMP_TO_NAME = new Map<React.ElementType, string>([
  [Heart, "Heart"], [Baby, "Baby"], [Ambulance, "Ambulance"], [Activity, "Activity"],
]);

// Section-rail keys that open as a full-screen native modal (mobile) instead of
// scroll-jumping. Overview stays the hub; Variants opens the family explorer.
// Code page (CPT/HCPCS/DRG/REV) rendered inside a nested native modal so tapping
// "View CPT 99232" from the Code Breakdown sheet keeps the breadcrumb chain
// instead of navigating away to a full page. Lazy so its recharts/map deps don't
// bloat the scenarios bundle.
const ProcedureCodePageLazy = lazy(() => import("./CPTCodes"));
// codeType (as stored on a procedure) -> ProcedureCodePage codeType prop.
const CODE_MODAL_TYPE: Record<string, 'cpt' | 'hcpcs' | 'drg' | 'revenue'> = {
  CPT: 'cpt', HCPCS: 'hcpcs', 'MS-DRG': 'drg', DRG: 'drg', REV: 'revenue', RC: 'revenue',
};

const SECTION_MODAL_KEYS = new Set<string>([
  "severity-variants", "cost-timeline", "code-breakdown", "price-comparison", "nearby-hospitals", "dignity-guide", "international", "care-resources",
]);
import { trackScenarioView, trackScenarioBookmark, trackScenarioHospitalSelect } from "@/utils/analytics";
import { loadStaticData, loadTranslatedStaticData } from "@/lib/offlineData";
import { useDataMode } from "@/hooks/useDataMode";
import { loadScenarioById, extractNationalPricing } from "@/lib/scenarioDetailStore";
import type { ScenarioDetail } from "@/lib/scenarioDetailStore";
import { BacklinkArticleCard } from "@/components/BacklinkArticleCard";
import { BacklinkArticleEmbed } from "@/components/BacklinkArticleEmbed";
import { useIsMobile } from "@/hooks/use-mobile";
import ScenarioLocalPrices from "@/components/scenarios/ScenarioLocalPrices";
import ScenarioCardNearYou from "@/components/scenarios/ScenarioCardNearYou";
// Note: scenarioCoverageStore imports useLiveDb hook at module level, safe to import dynamically only
// import { loadScenarioCoverage, getCoverageForScenario, getHospitalInfo } from "@/lib/scenarioCoverageStore";

interface ScenarioProcedure {
  code: string;
  codeType: string;
  description: string;
  phase: string;
  quantity: number;
  estimatedCost: number;
  dayNumber?: number; // For timeline positioning
  setting?: 'inpatient' | 'outpatient' | 'unspecified'; // Care setting
  /** Cornerstone flag — codes that DEFINE the scenario. A hospital is only
   *  ranked for a scenario when it prices every cornerstone code AND ≥50%
   *  of the full code list (refresh_scenario_coverage cornerstone_pass +
   *  coverage>=50 gate). */
  is_required?: boolean;
}

interface Scenario {
  id: string;
  uuid: string;
  slug: string;
  name: string;
  icon: typeof Heart;
  color: string;
  description: string;
  duration: string;
  totalProcedures: number;
  estimatedCost: number;
  /** Median complete hospital bill. For coverage-strong scenarios (>=30
   *  hospitals pricing the near-complete pathway) the generator overrides
   *  scenario_costs_cache.real_median_cost with the real per-hospital median
   *  from scenario_hospital_coverage; thin-coverage scenarios fall back to
   *  the per-line sum (a line-by-line estimate, not any hospital's bill).
   *  Surfaced on the detail card's avg/median toggle. */
  medianCost?: number;
  /** Raw (uncapped) avg total from scenario_costs_cache.real_total_cost_raw.
   *  estimatedCost is the winsorized/capped default; this is the counterpart
   *  for the capped⇄raw toggle. */
  estimatedCostRaw?: number | null;
  minCost?: number;
  maxCost?: number;
  /** 10th/90th percentile across real per-hospital complete bills for this
   *  scenario (scenario_lane_stats, same cornerstone_pass + supplemental_score
   *  filter as minCost/maxCost). Undefined when the scenario doesn't qualify
   *  for the coverage override (thin data). */
  p10Cost?: number | null;
  p90Cost?: number | null;
  phaseBreakdown?: Record<string, number>;
  /** Median per-phase breakdown from scenario_costs_cache.phase_costs_median.
   *  Used alongside phaseBreakdown so the avg/median toggle can flip the
   *  family-explorer card bars without recomputing. */
  phaseBreakdownMedian?: Record<string, number> | null;
  /** Raw (uncapped) per-phase breakdown from scenario_costs_cache.phase_costs_raw. */
  phaseBreakdownRaw?: Record<string, number> | null;
  procedures: ScenarioProcedure[];
  phases: string[];
  // Community features
  createdBy?: string;
  createdById?: string;
  isVerified?: boolean;
  helpfulCount?: number;
  usageCount?: number;
  saveCount?: number;
  tags?: string[];
  createdAt?: Date;
  severityLevel?: number; // 0-4 scale: 0=no complications, 4=critical/worst-case
  scenarioFamilyId?: string;
  familyVariantLabel?: string;
  /** DRG crosswalk for the inpatient block (from cornerstone_drg_crosswalk).
   *  Present when this scenario's inpatient cornerstone maps to a DRG family, so
   *  hospitals that publish the DRG bundle are priced/labeled as such. */
  drgInpatient?: {
    cornerstone: string;
    cornerstoneType: string;
    drgFamily: string[];
    baseDrg: string;
    baseDrgTitle?: string | null;
  } | null;
  // AI-generated content ($1.99 value-add)
  category?: string;
  source?: 'curated' | 'manual' | 'ai_generated';
  aiConsumerMessage?: string;
  aiClinicalSummary?: string;
  aiCostAnalysis?: string;
  aiPreparationGuide?: string;
}

// (Removed 2026-07-11: `scenariosHardcoded` — a never-referenced legacy seed
// array with hand-typed estimatedCost values. Dead code; all scenario costs
// come from real chargemaster aggregates.)

const Scenarios = () => {
  const { uuid, slug, state: stateParam, level: severityParam, hospitalId: hospitalIdParam } = useParams<{ uuid: string; slug: string; state: string; level: string; hospitalId: string }>();
  const activeSeverityFilter = severityParam != null ? Math.min(Math.max(parseInt(severityParam, 10) || 0, 0), 4) : null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const brand = useBrand(); // Get brand config for theme colors
  const isAhp = brand.design === 'civic';
  const { isDark: isThemeDark } = useTheme();
  const ahpDark = isAhp && isThemeDark;
  // Card palette follows the brand first, then AHP's light/dark toggle. Before
  // this, every brand rendered AHP's red/navy/gold phase bars.
  const scenarioCardTheme =
    brand.key === 'luigis' ? SCENARIO_CARD_T_LUIGIS
    : brand.key === 'antigreed' ? SCENARIO_CARD_T_ANTIGREED
    : ahpDark ? SCENARIO_CARD_T_DARK : SCENARIO_CARD_T_LIGHT;
  const { t } = useTranslation(['scenarios', 'common']);
  const localizedCategories = useLocalizedCategories();

  // Severity level labels and colors (used in SEO meta + severity filter UI).
  // Labels live in common.json so every page reads the same translated strings.
  const COMP_LABELS = [
    t('common:costQuartile.base'),
    t('common:costQuartile.lowCost'),
    t('common:costQuartile.midCost'),
    t('common:costQuartile.highCost'),
    t('common:costQuartile.highest'),
  ];
  const COMP_COLORS_TOP = ['var(--civic-money)', '#5a9a3c', 'var(--civic-gold)', '#e67e22', 'var(--civic-red)'];

  // AHP patriotic palette for chart bars
  const ahpBarColors = [
    'var(--civic-red)', 'var(--civic-navy)', 'var(--civic-gold)', 'var(--civic-navy-deep)', '#8B2232',
    '#3D5A80', '#A0522D', '#4A6741',
  ];
  const ahpHospitalBarColors = [
    'var(--civic-money)', '#1a7a52', '#3d8b6e', '#548c6b', '#6b9e7f',
    'var(--civic-money)', '#1a7a52', '#3d8b6e',
  ];

  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [selectedHospitalId, setSelectedHospitalId] = useState<string | null>(null);
  const [collapsedPhases, setCollapsedPhases] = useState<Set<string>>(new Set());

  // Deep-link hash scrolling — when the URL has #nearby-hospitals / #international /
  // #care-resources (e.g. from a ScenarioCard quick-action button), scroll the
  // matching section into view once the scenario has loaded.
  // Auto-select hospital from /scenarios/:uuid/:slug/hospital/:hospitalId route
  const routerLocation = useLocation();
  useEffect(() => {
    if (!selectedScenario) return;
    if (hospitalIdParam && !selectedHospitalId) {
      const timer = setTimeout(() => handleHospitalSelect(hospitalIdParam), 600);
      return () => clearTimeout(timer);
    }
  }, [hospitalIdParam, selectedScenario?.id, selectedHospitalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // (Deep-link hash handling lives below goToSection's declaration — its
  //  dependency array must not reference the callback before it exists.)

  const [hospitalNameQuery, setHospitalNameQuery] = useState('');
  const [hospitalLocationQuery, setHospitalLocationQuery] = useState('');
  const [showHospitalDropdown, setShowHospitalDropdown] = useState(false);
  const [scenarioSearchQuery, setScenarioSearchQuery] = useState('');
  const [showScenarioDropdown, setShowScenarioDropdown] = useState(false);
  // Section opened as a full-screen native modal from the section rail (mobile).
  // Mirrored in the URL as ?section=KEY so it's a real history entry: opening a
  // section pushes, OS/hardware back closes it, and — crucially — navigating into
  // a sub-page (e.g. tapping "View CPT" → /cpt/123) and then pressing back lands
  // right back on the open section modal instead of losing your place.
  const openSection = searchParams.get('section');
  // Opening pushes a history entry (?section=KEY). setSearchParams' functional
  // form keeps this stable across renders, so the rail's memoised handler never
  // goes stale.
  const openSectionModal = useCallback((key: string | null) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (key) p.set('section', key); else p.delete('section');
      return p;
    });
  }, [setSearchParams]);
  // Closing pops the ?section entry (mirrors a hardware back) so the history
  // stack stays clean and a later "back" doesn't re-open the modal.
  const closeSection = useCallback(() => {
    if (searchParams.get('section')) navigate(-1);
  }, [navigate, searchParams]);

  // Nested code modal (?code=CPT:99232). Opening pushes a history entry so the
  // breadcrumb back / OS-back returns to the Code Breakdown sheet underneath.
  const codeParam = searchParams.get('code');
  const codeModal = useMemo(() => {
    if (!codeParam) return null;
    const [ctype, ccode] = codeParam.split(':');
    const mapped = CODE_MODAL_TYPE[(ctype || '').toUpperCase()];
    if (!mapped || !ccode) return null;
    return { codeType: mapped, code: ccode, rawType: (ctype || '').toUpperCase() };
  }, [codeParam]);
  const openCodeModal = useCallback((codeType: string, code: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('code', `${codeType}:${code}`);
      return p;
    });
  }, [setSearchParams]);
  const closeCodeModal = useCallback(() => {
    if (searchParams.get('code')) navigate(-1);
  }, [navigate, searchParams]);

  // Dignity hub — the two-guide visual-novel launcher that lives in the Overview
  // summary. Dignity sits between two guides: (1) a generic "what do severity
  // levels mean" walkthrough (page-key narration), and (2) the per-scenario
  // walkthrough (scenario narration). Both run the same VN engine
  // (DignityNarrationPlayer) via its slim `compact` trigger. "Connect to Care"
  // jumps to the Care Resources section.
  const renderDignityNarrator = useCallback((scn: Scenario | null) => {
    if (!scn?.uuid) return null;
    // Dignity is AHP's mascot — the narrator hub is identity, not layout.
    if (brand.key !== 'ahp') return null;
    const serifF = "'Instrument Serif', Georgia, serif";
    const careLinkCount = matchResources(scn.tags || [], scn.name).length;
    // Keep the scenario label short so the compact button stays one/two lines.
    const shortName = scn.name.length > 42 ? `${scn.name.slice(0, 40).trimEnd()}…` : scn.name;
    return (
      <div
        className="w-full rounded-xl overflow-hidden"
        style={{
          background: ahpDark
            ? 'linear-gradient(135deg, var(--civic-bg-dark) 0%, var(--civic-navy-deep) 60%, var(--civic-bg-dark) 100%)'
            : 'linear-gradient(135deg, var(--civic-bg-dark) 0%, var(--civic-navy) 60%, var(--civic-navy-deep) 100%)',
          border: `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.30)' : 'rgba(var(--civic-gold-rgb),0.55)'}`,
          boxShadow: ahpDark
            ? '0 8px 30px rgba(var(--civic-navy-deeper-rgb),0.6), inset 0 1px 0 rgba(var(--civic-gold-rgb),0.18)'
            : '0 8px 30px rgba(var(--civic-navy-deeper-rgb),0.25), inset 0 1px 0 rgba(var(--civic-gold-rgb),0.35)',
        }}
      >
        <div className="flex items-center justify-between px-3 py-1.5"
          style={{ background: 'linear-gradient(90deg, rgba(var(--civic-gold-rgb),0.18) 0%, rgba(var(--civic-gold-rgb),0.06) 100%)', borderBottom: '1px solid rgba(var(--civic-gold-rgb),0.35)' }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--civic-gold)', letterSpacing: '2.5px', textTransform: 'uppercase' as const }}>
            ✦ {t('scenarios:dignity.guideLabel', { defaultValue: "Dignity · Your Guide" })}
          </span>
          <span style={{ fontSize: 9, color: 'rgba(var(--civic-gold-rgb),0.7)', letterSpacing: '1px', textTransform: 'uppercase' as const }}>
            {t('scenarios:dignity.systemStatus', { defaultValue: 'NARRATOR: READY' })}
          </span>
        </div>
        <div className="flex gap-3 items-stretch p-3">
          {/* Avatar — Dignity presenting the two guides. Shown on mobile too
              (was hidden sm:flex): on native/iOS this renders his 3D GLB form,
              not a flat thumbnail. */}
          <div className="flex-shrink-0 self-stretch flex items-center justify-center"
            style={{ width: 92, borderRadius: 8, background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(var(--civic-gold-rgb),0.25)', padding: 6 }}>
            <DignityAvatar size="chat" />
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-2.5">
            <div style={{ fontSize: 12.5, lineHeight: 1.4, color: 'var(--civic-warm)', fontFamily: serifF }}>
              {t('scenarios:dignity.hubPrompt', { defaultValue: 'Two ways I can walk you through this — pick one to start.' })}
            </div>
            {/* Two guides, side by side on desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <DignityNarrationPlayer
                pageKey="scenario-severity"
                compact
                label={t('scenarios:dignity.severityGuideLabel', { defaultValue: 'What “severity levels” mean' }) as string}
              />
              <DignityNarrationPlayer
                scenarioId={scn.uuid}
                compact
                label={t('scenarios:dignity.scenarioGuideLabel', { scenario: shortName, defaultValue: 'Walk me through this scenario' }) as string}
              />
            </div>
            {careLinkCount > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => openSectionModal('care-resources')}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold transition-colors"
                  style={{ background: 'rgba(var(--civic-red-rgb),0.18)', color: '#ffb3bb', border: '1px solid rgba(var(--civic-red-soft-rgb),0.4)' }}
                  title={t('scenarios:actions.connectToCareTooltip', { defaultValue: 'Jump to support orgs, financial help, and patient advocacy resources' }) as string}
                >
                  <HandHeart className="w-3.5 h-3.5" />
                  {t('scenarios:actions.connectToCare', { defaultValue: 'Connect to Care' })}
                  <span className="inline-flex items-center justify-center min-w-[18px] px-1 rounded-full"
                    style={{ fontSize: 9, fontWeight: 700, background: 'var(--civic-red-soft)', color: '#fff', lineHeight: '14px', height: 14 }}>
                    {careLinkCount}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }, [ahpDark, t, openSectionModal]);

  const [isTimelineFullscreen, setIsTimelineFullscreen] = useState(false);
  // Lock body scroll while the fullscreen mobile timeline overlay is up
  // (same pattern as ScenarioSectionModal).
  useEffect(() => {
    if (!isTimelineFullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isTimelineFullscreen]);
  // DRG inpatient-bundle toggle. ON (default) shows the single bundled DRG price
  // for the inpatient stay (matches the methodology-correct headline); OFF shows
  // the itemized per-code inpatient line-items summed instead. Drives both the
  // timeline chart and the comparison totals.
  const [includeDrgBundle, setIncludeDrgBundle] = useState(true);
  // ?create=1 deep-links straight into the builder (free negotiation-sheet
  // path advertised from /price-plan).
  const [showCreateDialog, setShowCreateDialog] = useState(() => searchParams.get('create') === '1');
  const [showAddToPlan, setShowAddToPlan] = useState<string | null>(null); // scenario ID showing popover
  // Severity & Related: the full variant lists + severity card row collapse by
  // default (the 0–4 quartile chip strip stays as the summary); re-collapse on
  // scenario change.
  const [variantsExpanded, setVariantsExpanded] = useState(false);
  useEffect(() => { setVariantsExpanded(false); }, [uuid]);
  const [addToPlanMember, setAddToPlanMember] = useState<string>('');
  const [addToPlanDate, setAddToPlanDate] = useState(new Date().toISOString().split('T')[0]);
  const [localScenarios, setLocalScenarios] = useState<Scenario[]>([]);
  const [browseCategory, setBrowseCategory] = useState('all');
  const isMobileViewport = useIsMobile();
  // Jump-to a section from the Overview summary. Mobile: sections are modals.
  // Desktop: most sections scroll to their anchor; the Dignity guide is a modal
  // (its full content is launched on demand, not a permanent duplicate row).
  const goToSection = useCallback((key: string, anchorId?: string) => {
    const asModal = key === 'dignity-guide' || (isMobileViewport && SECTION_MODAL_KEYS.has(key));
    if (asModal) { openSectionModal(key); return; }
    const el = anchorId ? document.getElementById(anchorId) : null;
    if (!el) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    const headerH = (document.querySelector('header') as HTMLElement)?.offsetHeight ?? 70;
    window.scrollTo({ top: Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerH - 12), behavior: 'smooth' });
  }, [isMobileViewport, openSectionModal]);

  // Jump from a Nearby-Hospitals row into the hospital-specific content:
  // open the section (modal on mobile / scroll on desktop), then bring the
  // selected-hospital block into view — it sits BELOW the national content,
  // so without the second scroll the user only ever saw the top of the section.
  const jumpToHospitalSection = useCallback((key: 'price-comparison' | 'code-breakdown') => {
    goToSection(key, key);
    const target = key === 'price-comparison' ? 'selected-hospital-pricing' : 'code-breakdown-hospital-summary';
    setTimeout(() => {
      // Sections dual-render (inline + open modal), so the id can exist twice
      // while a modal is up — the LAST match is the modal's copy (portaled
      // after the page content); scrolling it scrolls the modal body.
      const matches = document.querySelectorAll<HTMLElement>(`[id="${target}"]`);
      matches[matches.length - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 700);
  }, [goToSection]);

  // Deep-link hash — #nearby-hospitals / #care-resources / legacy #hospitalUUID.
  // Routed through goToSection: on mobile these sections are ?section= MODALS
  // (no anchor div exists in the DOM, so a plain scrollIntoView silently
  // no-oped and ScenarioCard "Near Me" links dumped users on the Overview).
  useEffect(() => {
    if (!routerLocation.hash) return;
    if (!selectedScenario) return;
    const id = routerLocation.hash.replace('#', '');
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    if (isUUID) {
      const timer = setTimeout(() => handleHospitalSelect(id), 600);
      return () => clearTimeout(timer);
    }
    const timer = setTimeout(() => {
      if (SECTION_MODAL_KEYS.has(id) || document.getElementById(id)) {
        goToSection(id, id);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [routerLocation.hash, selectedScenario?.id, goToSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Overview summary — the unified top-of-page block: a "jump to" quick-nav over
  // the scenario's sections (scrolls on desktop, opens the modal on mobile) plus
  // the Dignity narrator CTA. Replaces the old loose Dignity row so Dignity lives
  // in ONE place and mobile gets fast in-page navigation.
  // International pricing data (shared cache with ScenarioInternationalComparison).
  // Declared here rather than lower down because the section nav below needs it to
  // decide whether the Global button has anywhere to go.
  const { scenarioCosts: intlScenarioCosts, countries: intlCountries } = useStateInternationalStats();

  // Which scenarios actually have international rows. international_scenario_costs
  // only covers scenarios whose CPT codes carry foreign prices, so most do not —
  // without this the Global button led to an empty "being compiled" card.
  const intlScenarioIds = useMemo(
    () => new Set((intlScenarioCosts || []).map((c: InternationalScenarioCost) => c.scenario_id)),
    [intlScenarioCosts]
  );
  const hasIntlData = useCallback(
    (...ids: (string | undefined | null)[]) => ids.some((id) => !!id && intlScenarioIds.has(id)),
    [intlScenarioIds]
  );

  const renderOverviewSummary = useCallback((scn: Scenario | null) => {
    if (!scn?.uuid || !isAhp) return null;
    const hasFamily = !!scn.scenarioFamilyId;
    const navItems = [
      hasFamily ? { key: 'severity-variants', id: 'severity-variants', label: 'Variants', icon: Layers } : null,
      { key: 'cost-timeline', id: 'cost-timeline', label: 'Timeline', icon: Calendar },
      { key: 'code-breakdown', id: 'code-breakdown', label: 'Code Breakdown', icon: Receipt },
      { key: 'price-comparison', id: 'price-comparison', label: 'Price Comparison', icon: Scale },
      { key: 'nearby-hospitals', id: 'nearby-hospitals', label: 'Hospitals', icon: Building2 },
      ...(brand.key === 'ahp' ? [{ key: 'dignity-guide', id: 'dignity-guide', label: 'Dignity', icon: Sparkles }] : []),
      hasIntlData(scn.id, scn.uuid) ? { key: 'international', id: 'international', label: 'Global', icon: Globe } : null,
      { key: 'care-resources', id: 'care-resources', label: 'Care', icon: HandHeart },
    ].filter(Boolean) as { key: string; id: string; label: string; icon: React.ElementType }[];

    const cardBg = ahpDark ? 'var(--civic-card-dark)' : '#ffffff';
    const cardBorder = ahpDark ? 'rgba(var(--civic-gold-rgb),0.22)' : 'rgba(var(--civic-ink-rgb),0.10)';
    const chipBg = ahpDark ? 'rgba(var(--civic-gold-rgb),0.08)' : 'rgba(var(--civic-navy-rgb),0.05)';
    const chipBorder = ahpDark ? 'rgba(var(--civic-gold-rgb),0.22)' : 'rgba(var(--civic-navy-rgb),0.12)';
    const chipText = ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)';
    const iconColor = ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)';
    const labelColor = ahpDark ? 'rgba(var(--civic-warm-rgb),0.55)' : 'rgba(var(--civic-ink-rgb),0.62)';

    return (
      <div className="w-full mb-4">
        {/* Section quick-nav — DESKTOP ONLY. On mobile the fixed bottom
            section rail navigates the same eight sections, so this grid was
            pure duplication burning the first screen; the freed space goes to
            the phase-breakdown scenario card rendered after this block. */}
        {!isMobileViewport && (
        <div
          className="rounded-xl p-2.5 sm:p-3 mb-2.5"
          style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}`, boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '2px', textTransform: 'uppercase', color: labelColor }}>
              Explore this scenario
            </span>
            <div className="h-px flex-1" style={{ backgroundColor: cardBorder }} />
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
            {navItems.map((it) => {
              const Icon = it.icon;
              return (
                <button
                  key={it.key}
                  onClick={() => goToSection(it.key, it.id)}
                  className="group flex flex-col items-center justify-center gap-1 px-1 py-2 rounded-lg transition-all"
                  style={{ backgroundColor: chipBg, border: `1px solid ${chipBorder}` }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = ahpDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(var(--civic-navy-rgb),0.09)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = chipBg; }}
                >
                  <Icon className="w-4 h-4 flex-shrink-0 transition-transform group-hover:scale-110" style={{ color: iconColor }} />
                  <span className="w-full truncate text-center" style={{ fontSize: 10.5, fontWeight: 600, color: chipText, fontFamily: "'Instrument Sans', system-ui, sans-serif", letterSpacing: '0.2px' }}>
                    {it.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        )}
        {renderDignityNarrator(scn)}
      </div>
    );
  }, [isAhp, ahpDark, goToSection, renderDignityNarrator, isMobileViewport, hasIntlData]);

  const carouselRef = useRef<HTMLDivElement>(null);
  const [helpfulVotes, setHelpfulVotes] = useState<Set<string>>(() => new Set(scenarioStorage.getHelpfulVotes()));
  const [followedScenarios, setFollowedScenarios] = useState<Set<string>>(() => new Set(scenarioStorage.getFollowedScenarios()));
  // Family-plan assignment: local-only (familyStorage/scenarioStorage in
  // localStorage — nothing leaves the device). assignTarget opens the
  // member-picker dialog; assignedNames maps catalog scenario name → memberId
  // so the Assign button can show its gold "assigned" state.
  const [assignTarget, setAssignTarget] = useState<Scenario | null>(null);
  const [assignedNames, setAssignedNames] = useState<Map<string, string>>(() => {
    const m = new Map<string, string>();
    scenarioStorage.getUserScenarios().forEach(s => { if (s.familyMemberId) m.set(s.name, s.familyMemberId); });
    return m;
  });
  const refreshAssignedNames = useCallback(() => {
    const m = new Map<string, string>();
    scenarioStorage.getUserScenarios().forEach(s => { if (s.familyMemberId) m.set(s.name, s.familyMemberId); });
    setAssignedNames(m);
  }, []);
  const handleAssignToMember = useCallback(async (scenario: Scenario, memberId: string | null) => {
    const existing = scenarioStorage.getUserScenarios().find(s => s.name === scenario.name);
    if (memberId === null) {
      if (existing) {
        scenarioStorage.assignToFamilyMember(existing.id, null);
        toast.success(t('scenarios:assign.removed', { defaultValue: 'Removed from family plan' }));
      }
    } else {
      let procsList = (scenario.procedures || []).map(p => ({
        code: p.code,
        codeType: p.codeType,
        description: p.description || '',
        phase: p.phase || '',
        quantity: p.quantity || 1,
        estimatedCost: Number(p.estimatedCost) || 0,
        dayNumber: p.dayNumber,
        setting: p.setting,
      }));
      // Static-index cards don't carry per-procedure rows — pull them from the
      // public catalog so the family plan copy matches FamilyPlanner's own
      // assign flow. The plan itself still lives only in localStorage.
      if (procsList.length === 0) {
        try {
          const { data } = await supabase
            .from('scenario_procedures')
            .select('code, code_type, description, phase, quantity, estimated_cost, day_number, setting')
            .eq('scenario_id', scenario.uuid || scenario.id)
            .order('sort_order', { ascending: true });
          procsList = (data || []).map((p: any) => ({
            code: p.code,
            codeType: p.code_type,
            description: p.description || '',
            phase: p.phase || '',
            quantity: p.quantity || 1,
            estimatedCost: Number(p.estimated_cost) || 0,
            dayNumber: p.day_number ?? undefined,
            setting: p.setting ?? undefined,
          }));
        } catch { /* offline is fine — assignment still saves with cost + name */ }
      }
      if (existing) {
        scenarioStorage.assignToFamilyMember(existing.id, memberId);
        scenarioStorage.updateScenario(existing.id, {
          totalProcedures: scenario.totalProcedures,
          estimatedCost: scenario.estimatedCost,
          procedures: procsList,
          duration: scenario.duration || existing.duration,
          description: scenario.description || existing.description,
          phases: Array.isArray(scenario.phases) ? scenario.phases : existing.phases,
        });
      } else {
        scenarioStorage.saveScenario({
          name: scenario.name,
          description: scenario.description || '',
          duration: scenario.duration || '1 month',
          totalProcedures: scenario.totalProcedures || procsList.length,
          estimatedCost: scenario.estimatedCost || 0,
          procedures: procsList,
          phases: Array.isArray(scenario.phases) ? scenario.phases : [],
          tags: scenario.tags || [],
          severityLevel: scenario.severityLevel,
          isDraft: false,
          visibility: 'unlisted',
          shareToken: '',
          source: 'template',
          familyMemberId: memberId,
        });
      }
      const member = familyStorage.getMemberById(memberId);
      toast.success(
        t('scenarios:assign.assigned', { defaultValue: 'Assigned to {{name}}', name: member ? familyStorage.getDisplayName(member) : 'family member' }),
        { action: { label: t('scenarios:assign.viewPlan', { defaultValue: 'View plan' }), onClick: () => navigate('/family') } }
      );
    }
    refreshAssignedNames();
    setAssignTarget(null);
  }, [t, navigate, refreshAssignedNames]);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  // Free-text filter inside the Family Explorer (large families can have 70+ variants).
  const [familySearch, setFamilySearch] = useState("");
  // When the family explorer is opened from a severity pill, the level to scroll to.
  const [familyModalLevel, setFamilyModalLevel] = useState<number | null>(null);
  useEffect(() => {
    if (!showFamilyModal || familyModalLevel == null) return;
    const t = setTimeout(() => {
      document.getElementById(`fam-level-${familyModalLevel}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 260);
    return () => clearTimeout(t);
  }, [showFamilyModal, familyModalLevel]);
  // Avg / median toggle — shared with ScenarioCard via the same localStorage
  // key so flipping the stat on one surface persists across the site. When
  // 'median' is active, the page derives `nationalAverages.price` from the
  // median field emitted by generate_static_cache.py (entry.m) — the totals,
  // phase chart, and per-procedure rows all read through that same map, so
  // one state controls everything downstream.
  const [costStat, setCostStat] = useState<'avg' | 'median'>(() => {
    if (typeof window === 'undefined') return 'avg';
    return (window.localStorage.getItem('ahp-card-cost-stat') as 'avg' | 'median') || 'avg';
  });
  const handleCostStatChange = useCallback((next: 'avg' | 'median') => {
    setCostStat(next);
    try { window.localStorage.setItem('ahp-card-cost-stat', next); } catch {}
  }, []);
  // Cost scope: 'capped' = winsorized national avg (outlier-protected, default),
  // 'raw' = true uncapped mean. Only meaningful when costStat === 'avg' (median
  // is inherently outlier-resistant). Persisted; default 'capped'.
  const [costScope, setCostScope] = useState<'capped' | 'raw'>(() => {
    if (typeof window === 'undefined') return 'capped';
    return (window.localStorage.getItem('ahp-card-cost-scope') as 'capped' | 'raw') || 'capped';
  });
  const handleCostScopeChange = useCallback((next: 'capped' | 'raw') => {
    setCostScope(next);
    try { window.localStorage.setItem('ahp-card-cost-scope', next); } catch {}
  }, []);
  // Track which severity level each card is displaying (key = card slot id, value = scenario uuid)
  const [cardSeverityOverrides, setCardSeverityOverrides] = useState<Record<string, string>>({});
  // Severity filter for the variants row: null = show top of each level, 0-4 = filter to that level
  const [severityFilter, setSeverityFilter] = useState<number | null>(null);
  const [bookmarkedScenarioIds, setBookmarkedScenarioIds] = useState<string[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [searchCategory, setSearchCategory] = useState('all');
  const [expandedSearchFamilies, setExpandedSearchFamilies] = useState<Set<string>>(new Set());
  const [expandedSeverityFilter, setExpandedSeverityFilter] = useState<Record<string, number | null>>({}); // familyUuid -> severity level filter (null = all)
  const [variantPage, setVariantPage] = useState<Record<string, number>>({}); // familyUuid -> page number
  const [settingOverrides, setSettingOverrides] = useState<Map<string, string>>(new Map()); // key: codeType:code, value: setting
  const [procedureModal, setProcedureModal] = useState<{ proc: ScenarioProcedure; hospitalPrice: number | null; nationalPrice: number | null; hospitalCount: number; consumerName: string } | null>(null);
  // 120 mi by default (Trent, 2026-09-29); the card's "near you" block widens
  // from the smallest radius that reaches 5 hospitals.
  const [radiusMiles, setRadiusMiles] = useState(120);
  // Listing cards whose nearby prices + quality table is open span the full grid.
  const [nearTableOpen, setNearTableOpen] = useState<Set<string>>(new Set());
  const { location: userLocation, isLocating, error: locationError, detectLocation, detectApproximate, lookupZip, clearLocation } = useUserLocation(false);
  // Location without a prompt first (Cloudflare's approximate city, /api/geo);
  // only if that is unavailable fall back to the browser prompt the page used
  // to open with. Once per visit, and never over a location the reader chose.
  const approxTried = useRef(false);
  // Runs on the listing too: its cards show "near you" prices (2026-09-29).
  useEffect(() => {
    if (userLocation || approxTried.current) return;
    approxTried.current = true;
    detectApproximate().then((ok) => { if (!ok) detectLocation(); });
  }, [userLocation, detectApproximate, detectLocation]);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debouncedNameQuery = useDebounce(hospitalNameQuery, 300);
  const debouncedLocationQuery = useDebounce(hospitalLocationQuery, 300);
  const scenarioSearchInputRef = useRef<HTMLInputElement>(null);
  // Sticky header measurement — the category-pill row pins right below it.
  const stickyHeadRef = useRef<HTMLDivElement>(null);
  const [stickyHeadH, setStickyHeadH] = useState(0);
  useEffect(() => {
    const el = stickyHeadRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => setStickyHeadH(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Is the header actually pinned (scrolled past its natural spot)? While it
  // is, the selected scenario's description sentence collapses so the bar
  // takes less of the viewport (Trent, 2026-09-16). Desktop only — the mobile
  // header is in-flow and never sticks.
  const [headStuck, setHeadStuck] = useState(false);
  useEffect(() => {
    if (isMobileViewport || typeof window === 'undefined') return;
    let raf = 0;
    const check = () => {
      raf = 0;
      const el = stickyHeadRef.current;
      if (!el) return;
      const chrome = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ahp-chrome-h')) || 0;
      setHeadStuck(el.getBoundingClientRect().top <= chrome + 1 && window.scrollY > 8);
    };
    const onScroll = () => { if (!raf) raf = window.requestAnimationFrame(check); };
    window.addEventListener('scroll', onScroll, { passive: true });
    check();
    return () => { window.removeEventListener('scroll', onScroll); if (raf) window.cancelAnimationFrame(raf); };
  }, [isMobileViewport]);
  const staticNationalPricingRef = useRef<Record<string, { p: number; h: number; s: string; n?: string }> | null>(null);
  const useLiveDb = useDataMode();
  const scenarioDropdownRef = useRef<HTMLDivElement>(null);

  // ── Fast-path: load single scenario detail for direct URL navigation ──
  const { data: directScenarioDetail } = useQuery({
    queryKey: ['scenario-direct', uuid],
    queryFn: () => loadScenarioById(uuid!),
    // Stay enabled until procedures are ACTUALLY loaded — not merely until a
    // same-uuid placeholder exists. On SPA-nav from home the warm index sets a
    // procedure-less placeholder first; if we disabled on uuid-match alone, the
    // detail (procedures) would never fetch and the breakdown shows "0 / $0".
    enabled: !!uuid && (!selectedScenario || selectedScenario.uuid !== uuid || !(selectedScenario.procedures && selectedScenario.procedures.length > 0)),
    staleTime: Infinity,
  });

  // Fetch scenarios from database (lightweight index for browse grid)
  const { data: dbScenarios = [], isLoading: scenariosLoading } = useQuery({
    queryKey: ['scenarios-from-db-v5'], // v5: Lightweight index + per-scenario detail
    queryFn: async () => {
      // Map icon names to components
      const iconMap: Record<string, any> = {
        Heart,
        Baby,
        Ambulance,
        Activity,
        Pill,
      };

      // Try lightweight index first (no procedures, no AI text — ~1.5MB vs 18MB)
      try {
        const indexData = await loadTranslatedStaticData<{ generated_at: string; data: any[]; language?: string }>('scenarios_index.json');
        if (indexData && Array.isArray(indexData.data) && indexData.data.length > 0) {
          // Filter out scenarios hidden from catalog (e.g., DRG base scenarios used only for hospital pricing)
          const visible = indexData.data.filter((s: any) => !s.hidden);
          console.log(`[Scenarios] Using scenarios_index.json (${visible.length} visible / ${indexData.data.length} total)`);
          return visible.map((s: any) => ({
            ...s,
            // Index emits estimatedCostMedian / phaseBreakdownMedian; alias to the
            // medianCost shorthand the avg/median toggle reads. phaseBreakdownMedian
            // rides through via the spread above.
            medianCost: s.medianCost ?? s.estimatedCostMedian,
            icon: iconMap[s.iconName] || Activity,
            procedures: [], // Index has no procedures — loaded on demand via loadScenarioById
            phases: Array.isArray(s.phases)
              ? s.phases.map((p: any) => typeof p === 'string' ? p : (p?.name || String(p)))
              : [],
          })) as Scenario[];
        }
      } catch (e) {
        console.warn('[Scenarios] Index load error, trying full bundle:', e);
      }

      // Fallback: try full scenarios.json (backward compat)
      try {
        const staticData = await loadStaticData<{ generated_at: string; data: any[]; nationalPricing?: Record<string, any> }>('scenarios.json');
        if (staticData && Array.isArray(staticData.data) && staticData.data.length > 0) {
          const visible = staticData.data.filter((s: any) => !s.hidden);
          console.log(`[Scenarios] Fallback to scenarios.json (${visible.length} visible / ${staticData.data.length} total)`);
          if (staticData.nationalPricing) {
            staticNationalPricingRef.current = staticData.nationalPricing;
          }
          return visible.map((s: any) => ({
            ...s,
            // `generate_static_cache.py` historically emitted `estimatedCostMedian`,
            // `refresh_scenario_cache.py` emits `medianCost` — accept either so
            // the avg/median toggle resolves regardless of which generator
            // wrote the row. Eventually both should converge on `medianCost`
            // (matching the `estimatedCost` shorthand); aliasing here keeps
            // the page working through that transition.
            medianCost: s.medianCost ?? s.estimatedCostMedian,
            icon: iconMap[s.iconName] || Activity,
            phases: Array.isArray(s.phases)
              ? s.phases.map((p: any) => typeof p === 'string' ? p : (p?.name || String(p)))
              : [],
          })) as Scenario[];
        }
      } catch (e) {
        console.warn('[Scenarios] Static data error, falling back to API:', e);
      }

      // Fetch scenarios.
      //
      // Explicit column list, NOT select('*'). The four ai_* columns are ~51 MB
      // uncompressed across the catalog (they TOAST-compress ~1000:1, so they
      // look tiny in pg_column_size but not on the wire) and take this response
      // from 12 MB to 68 MB. The grid never renders them: they are read only off
      // `selectedScenario`, and the detail view loads its own
      // /data/scenarios/{uuid}.json — which carries all four, plus procedures —
      // via loadScenarioById. The index path above already omits them, so
      // dropping them here makes this fallback consistent with the primary path
      // rather than richer than it.
      //
      // Keep this list in sync with the fields the mapper below reads.
      const { data: scenariosData, error: scenariosError } = await supabase
        .from('scenarios')
        .select('id,name,slug,description,icon_name,color,duration,total_procedures,estimated_cost,phases,severity_level,tags,created_by,is_verified,source,category,created_at,helpful_count,usage_count,save_count,scenario_family_id,family_variant_label')
        .eq('is_active', true)
        .order('created_at', { ascending: true });

      if (scenariosError) throw scenariosError;

      // Fetch all procedures and real costs for these scenarios (batched to avoid URL length limits)
      const scenarioIds = scenariosData.map(s => s.id);
      const [{ data: proceduresData, error: proceduresError }, { data: costsData }] = await Promise.all([
        batchedIn(
          'scenario_procedures',
          'scenario_id',
          scenarioIds,
          '*',
          { column: 'sort_order', ascending: true }
        ),
        batchedIn(
          'scenario_costs_cache',
          'scenario_id',
          scenarioIds,
          'scenario_id, real_total_cost, real_median_cost'
        ),
      ]);

      if (proceduresError) throw proceduresError;

      // Build real cost lookups — both avg and median, drives the card toggle.
      const realCostMap = new Map<string, number>();
      const realMedianMap = new Map<string, number>();
      costsData?.forEach(c => {
        if (c.real_total_cost) realCostMap.set(c.scenario_id, Math.round(Number(c.real_total_cost)));
        if (c.real_median_cost) realMedianMap.set(c.scenario_id, Math.round(Number(c.real_median_cost)));
      });

      // Fetch quick_names for CPT/HCPCS codes from procedures table
      const cptHcpcsCodes = proceduresData
        .filter(p => p.code_type === 'CPT' || p.code_type === 'HCPCS')
        .map(p => p.code);

      const { data: procedureQuickNames, error: quickNamesError } = cptHcpcsCodes.length > 0
        ? await batchedIn('procedures', 'code', cptHcpcsCodes, 'code, llm_quick_name')
        : { data: [], error: null };

      if (quickNamesError) console.error('Error fetching quick names:', quickNamesError);

      // Create a map of code -> quick_name
      const quickNameMap = new Map<string, string>();
      procedureQuickNames?.forEach(p => {
        if (p.llm_quick_name) quickNameMap.set(p.code, p.llm_quick_name);
      });

      // Combine scenarios with their procedures
      const combined: Scenario[] = scenariosData.map(scenario => {
        const scenarioProcedures = proceduresData
          .filter(p => p.scenario_id === scenario.id)
          .map(p => ({
            code: p.code,
            codeType: p.code_type,
            description: (p.code_type === 'CPT' || p.code_type === 'HCPCS')
              ? (quickNameMap.get(p.code) || p.description || '')
              : (p.description || ''),
            phase: p.phase,
            quantity: p.quantity || 1,
            estimatedCost: Number(p.estimated_cost) || 0,
            dayNumber: p.day_number || undefined,
            setting: p.setting || undefined,
          }));

        return {
          id: scenario.id,
          uuid: scenario.id, // Use id as uuid
          slug: scenario.slug || scenario.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
          name: scenario.name,
          icon: iconMap[scenario.icon_name] || Activity,
          color: scenario.color || '#60a5fa',
          description: scenario.description || '',
          duration: scenario.duration || '',
          totalProcedures: scenario.total_procedures || scenarioProcedures.length,
          estimatedCost: realCostMap.get(scenario.id) || Number(scenario.estimated_cost) || 0,
          medianCost: realMedianMap.get(scenario.id),
          procedures: scenarioProcedures,
          phases: Array.isArray(scenario.phases) ? scenario.phases.map((p: any) => typeof p === 'string' ? p : (p?.name || String(p))) : [],
          // Community features
          createdBy: scenario.created_by || undefined,
          isVerified: scenario.is_verified || false,
          helpfulCount: scenario.helpful_count || undefined,
          usageCount: scenario.usage_count || undefined,
          saveCount: scenario.save_count || undefined,
          tags: scenario.tags || [],
          severityLevel: scenario.severity_level != null ? scenario.severity_level : undefined,
          scenarioFamilyId: scenario.scenario_family_id || undefined,
          familyVariantLabel: scenario.family_variant_label || undefined,
          createdAt: scenario.created_at ? new Date(scenario.created_at) : undefined,
          // AI content
          category: scenario.category || undefined,
          source: scenario.source || undefined,
          // ai* deliberately not mapped here: the catalog query no longer selects
          // those four columns (see the select above — they are ~51 MB across the
          // catalog and nothing in the grid renders them). The detail view reads
          // them off /data/scenarios/{uuid}.json via loadScenarioById, which is
          // also how the index path — the normal path — supplies them. Adding
          // them back here would re-inflate this response ~5.5x for no UI gain.
        };
      });

      return combined;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Find the current scenario's family ID (from static data or direct detail)
  const currentFamilyId = useMemo(() => {
    if (!uuid) return null;
    const fromStatic = dbScenarios.find(s => s.id === uuid || s.uuid === uuid);
    if (fromStatic?.scenarioFamilyId) return fromStatic.scenarioFamilyId;
    if (directScenarioDetail?.scenarioFamilyId) return directScenarioDetail.scenarioFamilyId;
    return null;
  }, [uuid, dbScenarios, directScenarioDetail]);

  // Load family data ONLY for the current scenario's family (not all 11K scenarios)
  const { data: familyDataResult } = useQuery({
    queryKey: ['scenario-family', currentFamilyId],
    queryFn: async () => {
      if (!currentFamilyId) return { map: {} as Record<string, any>, fullScenarios: [] as any[] };

      // 1. Get only siblings in the same family
      const { data: scenarioData } = await supabase
        .from('scenarios')
        .select('*')
        .eq('scenario_family_id', currentFamilyId);
      if (!scenarioData || scenarioData.length === 0) return { map: {} as Record<string, any>, fullScenarios: [] as any[] };

      // 2. Load procedures for just these siblings (1 batch, not 368)
      const ids = scenarioData.map(s => s.id);
      const { data: procData } = await supabase
        .from('scenario_procedures')
        .select('*')
        .in('scenario_id', ids);

      const procMap: Record<string, any[]> = {};
      for (const p of (procData || [])) {
        if (!procMap[p.scenario_id]) procMap[p.scenario_id] = [];
        procMap[p.scenario_id].push(p);
      }

      // Attach procedures to scenarios
      const fullScenarios = scenarioData.map(s => ({
        ...s,
        scenario_procedures: procMap[s.id] || [],
      }));

      const map = Object.fromEntries(fullScenarios.map(f => [f.id, f]));
      return { map, fullScenarios };
    },
    enabled: !!currentFamilyId,
    staleTime: 10 * 60 * 1000,
  });

  const familyDataMap = familyDataResult?.map || {};
  const familyFullScenarios = familyDataResult?.fullScenarios || [];

  // SEO rankings data — populated from ScenarioRankings component via onRankingsReady callback
  const [seoRankings, setSeoRankings] = useState<SeoRankingsData | null>(null);

  // Enrich scenarios with family data AND inject family members not in static cache
  const scenarios = useMemo(() => {
    const enriched = dbScenarios.map(s => {
      const live = familyDataMap[s.id] || familyDataMap[s.uuid];
      if (!live) return s;
      // Procedures come from the live DB; COSTS keep the static-index value when
      // present. The index (scenarios.json) is the canonical price artifact — it
      // carries the real-complete-bill stats ("one set of numbers", 2026-08-25).
      // scenarios.estimated_cost in the DB drifted stale once already and put
      // $13k/$43k/$309k chips beside a $39,994 header on the same card; the DB
      // is a fallback for members the static cache doesn't have, never an
      // override. Handles camelCase (static JSON) and snake_case (Supabase).
      const liveProcs = (live.scenario_procedures || []).map((p: any) => ({
        code: p.code, codeType: p.codeType || p.code_type, description: p.description,
        phase: p.phase, quantity: p.quantity || 1,
        estimatedCost: Number(p.estimatedCost ?? p.estimated_cost) || 0,
        dayNumber: p.dayNumber ?? p.day_number, setting: p.setting,
      }));
      const liveCost = Number(live.estimated_cost) || liveProcs.reduce((sum: number, p: any) => sum + p.estimatedCost * p.quantity, 0);
      const staticCost = Number(s.estimatedCost) || 0;
      return {
        ...s,
        name: live.name || s.name,
        scenarioFamilyId: live.scenario_family_id || s.scenarioFamilyId,
        familyVariantLabel: live.family_variant_label || s.familyVariantLabel,
        severityLevel: live.severity_level != null ? live.severity_level : s.severityLevel,
        ...(staticCost > 0 ? {} : (liveCost > 0 ? { estimatedCost: liveCost } : {})),
        ...(liveProcs.length > 0 ? { procedures: liveProcs, totalProcedures: liveProcs.length } : {}),
        ...(live.phases && Array.isArray(live.phases) ? { phases: live.phases.map((p: any) => typeof p === 'string' ? p : (p?.name || String(p))) } : {}),
      };
    });

    // Inject family members that aren't in the static cache
    const existingIds = new Set(enriched.map(s => s.id || s.uuid));
    const iconMap: Record<string, typeof Heart> = { Heart, Baby, Ambulance, Activity, Pill };
    for (const fs of familyFullScenarios) {
      if (!existingIds.has(fs.id) && fs.name && fs.is_active !== false) {
        const procs = (fs.scenario_procedures || []).filter((p: any) => p.code && (p.codeType || p.code_type)).map((p: any) => ({
          code: p.code, codeType: p.codeType || p.code_type || 'CPT', description: p.description || '',
          phase: p.phase || 'General', quantity: p.quantity || 1,
          estimatedCost: Number(p.estimatedCost ?? p.estimated_cost) || 0,
          dayNumber: p.dayNumber ?? p.day_number, setting: p.setting || 'unspecified',
        }));
        enriched.push({
          id: fs.id, uuid: fs.id,
          slug: fs.slug || fs.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          name: fs.name, icon: iconMap[fs.icon_name] || Activity,
          color: fs.color || '#60a5fa',
          description: fs.description || '',
          duration: fs.duration || '',
          totalProcedures: procs.length,
          estimatedCost: Number(fs.estimated_cost) || procs.reduce((sum: number, p: any) => sum + p.estimatedCost * p.quantity, 0),
          procedures: procs,
          phases: Array.isArray(fs.phases) ? fs.phases.map((p: any) => typeof p === 'string' ? p : (p?.name || String(p))) : [],
          tags: fs.tags || [],
          severityLevel: fs.severity_level != null ? fs.severity_level : undefined,
          scenarioFamilyId: fs.scenario_family_id || undefined,
          familyVariantLabel: fs.family_variant_label || undefined,
          createdBy: fs.created_by, isVerified: fs.is_verified,
          helpfulCount: fs.helpful_count, usageCount: fs.usage_count, saveCount: fs.save_count,
          category: fs.category, source: fs.source,
        } as Scenario);
        existingIds.add(fs.id);
      }
    }
    return enriched;
  }, [dbScenarios, familyDataMap, familyFullScenarios]);

  // Load local scenarios, bookmarks, and recent searches on mount
  useEffect(() => {
    const loadLocalScenarios = () => {
      const userScenarios = scenarioStorage.getUserScenarios();

      // Convert local scenarios to Scenario format
      const converted: Scenario[] = userScenarios.map(ls => ({
        id: ls.id,
        uuid: ls.uuid,
        slug: ls.slug,
        name: ls.name,
        icon: Heart, // Default icon for now - TODO: Store icon type in localStorage
        color: '#60a5fa',
        description: ls.description,
        duration: ls.duration,
        totalProcedures: ls.totalProcedures,
        estimatedCost: ls.estimatedCost,
        procedures: ls.procedures,
        phases: ls.phases,
        createdBy: 'You',
        isVerified: false,
        tags: ls.tags,
        severityLevel: ls.severityLevel,
      }));

      setLocalScenarios(converted);
    };

    loadLocalScenarios();

    // Load bookmarks and recent searches
    setBookmarkedScenarioIds(scenarioStorage.getBookmarks());
    setRecentSearches(scenarioStorage.getRecentSearches());
  }, [showCreateDialog]); // Reload when dialog closes

  // Fast-path: when directScenarioDetail loads, show it immediately
  useEffect(() => {
    // Also re-apply when a procedure-less placeholder for this uuid is already
    // set (the SPA-nav-from-home case) — otherwise the breakdown stays at "0 / $0".
    const needsHydration = !selectedScenario || selectedScenario.uuid !== uuid || !(selectedScenario.procedures && selectedScenario.procedures.length > 0);
    if (directScenarioDetail && uuid && needsHydration) {
      const iconMap: Record<string, any> = { Heart, Baby, Ambulance, Activity, Pill };
      const asScenario: Scenario = {
        ...directScenarioDetail,
        icon: iconMap[directScenarioDetail.iconName] || Activity,
        procedures: directScenarioDetail.procedures || [],
        phases: Array.isArray(directScenarioDetail.phases)
          ? directScenarioDetail.phases.map((p: any) => typeof p === 'string' ? p : (p?.name || String(p)))
          : [],
        // Detail files emit estimatedCostMedian; the avg/median toggle reads
        // medianCost. The index loaders alias this but on a direct URL load
        // there is no index placeholder to inherit from — without this line
        // the cost-summary total silently falls back to avg in median mode.
        medianCost: (directScenarioDetail as any).medianCost
          ?? (directScenarioDetail as any).estimatedCostMedian,
      };
      // Merge over any index-derived placeholder so index-only fields (medianCost,
      // tags, family) survive while full detail + procedures overlay on top.
      setSelectedScenario(prev => (prev && prev.uuid === uuid) ? { ...prev, ...asScenario } : asScenario);
      setScenarioSearchQuery(asScenario.name);
      // Populate national pricing from per-scenario detail
      const pricing = extractNationalPricing(directScenarioDetail);
      if (pricing) {
        staticNationalPricingRef.current = pricing;
      }
    }
  }, [directScenarioDetail, uuid]);

  // Handle URL params on initial load or when URL changes
  useEffect(() => {
    if (uuid) {
      // If fast-path already set a scenario (or is loading it), don't override with index data (which lacks procedures)
      // Note: directScenarioDetail check prevents race condition where batched setState from fast-path
      // hasn't been processed yet but this effect already runs and overwrites with 0-procedure index data
      if (selectedScenario?.uuid === uuid && selectedScenario.procedures?.length > 0) return;
      if (directScenarioDetail?.id === uuid && directScenarioDetail?.procedures?.length > 0) return;
      const allScenarios = [...scenarios, ...localScenarios];
      const scenario = allScenarios.find(s => s.uuid === uuid);
      if (scenario) {
        // If this is from the index (no procedures), hydrate with full detail
        if (!scenario.procedures || scenario.procedures.length === 0) {
          loadScenarioById(uuid).then(detail => {
            if (detail) {
              const iconMap: Record<string, any> = { Heart, Baby, Ambulance, Activity, Pill };
              setSelectedScenario({
                ...scenario,
                ...detail,
                icon: iconMap[detail.iconName] || Activity,
                phases: Array.isArray(detail.phases)
                  ? detail.phases.map((p: any) => typeof p === 'string' ? p : (p?.name || String(p)))
                  : scenario.phases,
                procedures: detail.procedures || [],
              });
              const pricing = extractNationalPricing(detail);
              if (pricing) staticNationalPricingRef.current = pricing;
            }
          });
          // Set immediately with what we have (name, cost, etc. render instantly)
          setSelectedScenario(scenario);
        } else {
          setSelectedScenario(scenario);
        }
      }
    } else {
      // No UUID in URL — clear selection to show browse grid
      setSelectedScenario(null);
      setScenarioSearchQuery('');
      setCardSeverityOverrides({});
    }
  }, [uuid]);

  // Helper function to select a scenario and update URL
  const selectScenario = (scenario: Scenario) => {
    // Set immediately with what we have (shows name, cost, etc.)
    setSelectedScenario(scenario);
    // Clear any stale severity-slider overrides so the "Current" card and severity
    // cards reflect the newly-selected scenario, not a previous slide position.
    setCardSeverityOverrides({});
    const newUrl = `/scenarios/${scenario.uuid}/${scenario.slug}`;
    navigate(newUrl, { replace: false });
    // If scenario lacks procedures (from index), hydrate with full detail
    if (!scenario.procedures || scenario.procedures.length === 0) {
      loadScenarioById(scenario.uuid).then(detail => {
        if (detail) {
          const iconMap: Record<string, any> = { Heart, Baby, Ambulance, Activity, Pill };
          setSelectedScenario(prev => prev?.uuid === scenario.uuid ? {
            ...prev,
            ...detail,
            icon: iconMap[detail.iconName] || prev.icon,
            phases: Array.isArray(detail.phases)
              ? detail.phases.map((p: any) => typeof p === 'string' ? p : (p?.name || String(p)))
              : prev.phases,
            procedures: detail.procedures || [],
          } : prev);
          const pricing = extractNationalPricing(detail);
          if (pricing) staticNationalPricingRef.current = pricing;
        }
      });
    }
  };

  // Fetch hospitals for dropdown with coverage data via RPC
  const { data: hospitals, isLoading: hospitalsLoading } = useQuery({
    queryKey: ['hospitals-for-scenarios-autocomplete', debouncedNameQuery, debouncedLocationQuery, selectedScenario?.id],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_hospitals_for_scenario', {
        p_scenario_id: selectedScenario?.id || null,
        p_name_query: debouncedNameQuery || null,
        p_location_query: debouncedLocationQuery || null,
        p_limit: 50,
      });

      if (error) throw error;

      // Compute total estimated cost from scenario procedures (used as fallback for unmatched codes)
      const totalEstimatedCost = selectedScenario?.procedures.reduce(
        (sum, p) => sum + (p.estimatedCost * p.quantity), 0
      ) || 0;

      return (data || []).map((h: any) => {
        const totalHospitalPrice = Number(h.total_hospital_price) || 0;
        const totalNationalPrice = Number(h.total_national_price) || 0;
        const matchedCodes = h.matched_codes || 0;
        const totalCodes = h.total_codes || 0;

        // Blended price: hospital prices for matched codes + estimated costs for unmatched codes
        // This matches how calculateTotalCost() works in the detail view (falls back to estimatedCost)
        let blendedPrice = totalHospitalPrice;
        if (selectedScenario && matchedCodes < totalCodes && totalEstimatedCost > 0) {
          // totalNationalPrice ≈ sum of national avg prices for matched codes
          // (totalEstimatedCost - totalNationalPrice) ≈ estimated cost for unmatched codes
          const unmatchedEstimate = Math.max(0, totalEstimatedCost - totalNationalPrice);
          blendedPrice = totalHospitalPrice + unmatchedEstimate;
        }

        return {
          id: h.hospital_id,
          name: h.hospital_name,
          city: h.city,
          state: h.state,
          procedureCount: h.procedure_count || 0,
          matchedCodes,
          totalCodes,
          coveragePercentage: Number(h.coverage_percentage) || 0,
          totalHospitalPrice,
          blendedPrice,
          totalNationalPrice,
          avgMarkupPercentage: Number(h.avg_markup_percentage) || 0,
        };
      });
    },
    enabled: debouncedNameQuery.length >= 2 || debouncedLocationQuery.length >= 2 || (useLiveDb && debouncedNameQuery === '' && debouncedLocationQuery === ''),
    staleTime: 30000,
  });

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Hospital dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          nameInputRef.current && !nameInputRef.current.contains(event.target as Node) &&
          locationInputRef.current && !locationInputRef.current.contains(event.target as Node)) {
        setShowHospitalDropdown(false);
      }
      // Scenario dropdown
      if (scenarioDropdownRef.current && !scenarioDropdownRef.current.contains(event.target as Node) &&
          scenarioSearchInputRef.current && !scenarioSearchInputRef.current.contains(event.target as Node)) {
        setShowScenarioDropdown(false);
        setSearchCategory('all');
        setExpandedSearchFamilies(new Set());
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update search queries when selections change
  useEffect(() => {
    if (selectedHospitalId && hospitals) {
      const hospital = hospitals.find(h => h.id === selectedHospitalId);
      if (hospital) {
        setHospitalNameQuery(hospital.name);
        setHospitalLocationQuery(`${hospital.city}, ${hospital.state}`);
      }
    }
  }, [selectedHospitalId, hospitals]);

  useEffect(() => {
    // Keep the "Select Scenario" field showing the ACTUAL selected scenario —
    // including the specific family variant (e.g. "First Trimester Miscarriage"),
    // not the family root. Previously this forced the root family name, which made
    // the page look like it never changed when picking a variant.
    if (selectedScenario) {
      setScenarioSearchQuery(selectedScenario.name);
    }
  }, [selectedScenario]);

  // Fetch national average prices for selected scenario
  // Uses static JSON pricing data by default; only hits Supabase with ?live param
  const { data: nationalAverages, isLoading: loadingNational } = useQuery({
    queryKey: ['scenario-national-averages', selectedScenario?.id, selectedScenario?.procedures?.length || 0, useLiveDb, costStat],
    queryFn: async () => {
      if (!selectedScenario) return null;

      // Use static pricing data if available (default path — no Supabase needed)
      if (!useLiveDb && staticNationalPricingRef.current) {
        const staticPricing = staticNationalPricingRef.current;
        const priceMap = new Map<string, any>();

        for (const proc of selectedScenario.procedures) {
          const codeType = proc.codeType.toUpperCase();
          const code = proc.code;
          const setting = proc.setting || 'unspecified';

          // Try exact setting, then unspecified, then all_settings
          const keysToTry = [
            `${codeType}:${code}:${setting}`,
            `${codeType}:${code}:unspecified`,
            `${codeType}:${code}:all_settings`,
          ];

          for (const key of keysToTry) {
            const entry = staticPricing[key];
            if (entry && entry.p > 0) {
              // Median-anchored per-code price is emitted by generate_static_cache.py
              // when the underlying pricing-averages table has a non-zero median
              // (entry.m). The avg/median toggle selects which one becomes `.price`
              // here, so every downstream consumer (totals, phase chart, per-
              // procedure rows, getProcedurePrice) sees a coherent map without
              // having to thread costStat through every call site.
              const avgPrice = entry.p;
              const medianPrice = entry.m ?? entry.p;
              const effectivePrice = costStat === 'median' ? medianPrice : avgPrice;
              priceMap.set(key, {
                price: effectivePrice,
                avgPrice,
                medianPrice,
                hospitalCount: entry.h || 0,
                setting: entry.s || 'unspecified',
                consumerName: entry.n || '',
              });
            }
          }
        }

        if (priceMap.size > 0) {
          console.log(`[Scenarios] Using static national pricing (${priceMap.size} entries)`);
          return priceMap;
        }
      }

      // Group procedure codes by type
      const codesByType = new Map<string, Set<string>>();
      selectedScenario.procedures.forEach(proc => {
        const codeType = proc.codeType.toUpperCase();
        if (!codesByType.has(codeType)) {
          codesByType.set(codeType, new Set());
        }
        codesByType.get(codeType)!.add(proc.code);
      });

      const priceMap = new Map();
      const consumerNames = new Map<string, string>();

      // First, fetch consumer-friendly names for all codes
      for (const [codeType, codes] of codesByType.entries()) {
        if (codeType === 'CPT' || codeType === 'HCPCS') {
          const { data } = await supabase
            .from('procedures')
            .select('code, llm_quick_name')
            .eq('code_type', codeType)
            .in('code', Array.from(codes))
            .not('llm_quick_name', 'is', null);
          // Use a Set to track which codes we've already processed to avoid duplicates
          const seen = new Set<string>();
          data?.forEach(row => {
            if (!seen.has(row.code) && row.llm_quick_name) {
              consumerNames.set(`${codeType}:${row.code}`, row.llm_quick_name);
              seen.add(row.code);
            }
          });
        } else if (codeType === 'DRG') {
          const { data } = await supabase
            .from('drg_codes')
            .select('drg_code, llm_quick_name')
            .in('drg_code', Array.from(codes))
            .not('llm_quick_name', 'is', null);
          data?.forEach(row => {
            if (row.llm_quick_name) {
              consumerNames.set(`${codeType}:${row.drg_code}`, row.llm_quick_name);
            }
          });
        } else if (codeType === 'DRUGS' || codeType === 'DRUG') {
          const { data } = await supabase
            .from('drug_codes')
            .select('drug_code, llm_quick_name')
            .in('drug_code', Array.from(codes))
            .not('llm_quick_name', 'is', null);
          data?.forEach(row => {
            if (row.llm_quick_name) {
              consumerNames.set(`${codeType}:${row.drug_code}`, row.llm_quick_name);
            }
          });
        } else if (codeType === 'SUPPLY') {
          const { data } = await supabase
            .from('supply_codes')
            .select('supply_code, llm_quick_name')
            .in('supply_code', Array.from(codes))
            .not('llm_quick_name', 'is', null);
          data?.forEach(row => {
            if (row.llm_quick_name) {
              consumerNames.set(`${codeType}:${row.supply_code}`, row.llm_quick_name);
            }
          });
        }
      }

      // Query each code type from its appropriate table
      for (const [codeType, codes] of codesByType.entries()) {
        let tableName = 'cpt_pricing_averages';
        let codeColumn = 'cpt_code';
        let avgColumn = 'avg_gross_charge';

        if (codeType === 'CPT') {
          tableName = 'cpt_pricing_averages';
          codeColumn = 'cpt_code';
        } else if (codeType === 'DRG') {
          tableName = 'drg_pricing_averages';
          codeColumn = 'drg_code';
        } else if (codeType === 'DRUGS' || codeType === 'DRUG') {
          tableName = 'drug_pricing_averages';
          codeColumn = 'drug_code';
          avgColumn = 'avg_charge';
        } else if (codeType === 'SUPPLY') {
          tableName = 'supply_pricing_averages';
          codeColumn = 'supply_code';
          avgColumn = 'avg_charge';
        } else if (codeType === 'REV' || codeType === 'REVENUE') {
          tableName = 'revenue_pricing_averages';
          codeColumn = 'revenue_code';
          avgColumn = 'avg_charge';
        } else if (codeType === 'HCPCS') {
          tableName = 'hcpcs_pricing_averages';
          codeColumn = 'hcpcs_code';
        }

        // DRG table doesn't have setting_type or normalized_unit columns
        const hasSetting = tableName !== 'drg_pricing_averages';
        const selectCols = hasSetting
          ? `${codeColumn}, setting_type, normalized_unit, ${avgColumn}, total_hospitals`
          : `${codeColumn}, ${avgColumn}, total_hospitals`;
        const { data, error } = await supabase
          .from(tableName)
          .select(selectCols)
          .in(codeColumn, Array.from(codes));

        if (error) {
          console.error(`Error fetching ${codeType} pricing:`, error);
          continue;
        }

        // Group by code+setting and pick the variation with most hospitals
        const bestPrices = new Map<string, any>();
        data?.forEach(row => {
          const code = row[codeColumn];
          const setting = row.setting_type || 'unspecified';
          const key = `${code}:${setting}`;
          const currentBest = bestPrices.get(key);

          // Pick row with highest total_hospitals (most commonly reported variation)
          if (!currentBest || (row.total_hospitals > currentBest.total_hospitals)) {
            bestPrices.set(key, row);
          }
        });

        // Add best prices to map with hospital count, setting, and consumer name
        bestPrices.forEach((row) => {
          const code = row[codeColumn];
          const setting = row.setting_type || 'unspecified';
          const price = Math.round(row[avgColumn] || 0); // Round to whole dollars
          const hospitalCount = row.total_hospitals || 0;
          const consumerName = consumerNames.get(`${codeType}:${code}`) || '';
          const key = `${codeType}:${code}:${setting}`;
          priceMap.set(key, { price, hospitalCount, setting, consumerName });
        });
      }

      return priceMap;
    },
    enabled: !!selectedScenario,
  });

  // Fetch hospital-specific prices for selected scenario and hospital
  const { data: hospitalPrices, isLoading: loadingHospital } = useQuery({
    queryKey: ['scenario-hospital-prices', selectedScenario?.id, selectedHospitalId],
    queryFn: async () => {
      if (!selectedScenario || !selectedHospitalId) return null;

      const procedureCodes = selectedScenario.procedures.map(p => p.code);

      // Build set of expected (code, type) pairs for filtering
      // Maps scenario code_type to analytics procedure_type
      const typeMap: Record<string, string[]> = {
        'CPT': ['cpt', 'hcpcs'], 'HCPCS': ['hcpcs', 'cpt'],
        'DRG': ['drg'], 'REV': ['revenue'],
        'NDC': ['ndc'], 'DRUG': ['ndc'],
        'SUPPLY': ['supply'],
      };
      const allowedPairs = new Set<string>();
      for (const proc of selectedScenario.procedures) {
        const types = typeMap[proc.codeType.toUpperCase()] || [proc.codeType.toLowerCase()];
        for (const t of types) {
          allowedPairs.add(`${proc.code}:${t}`);
        }
      }

      const { data, error } = await supabase
        .from('hospital_procedure_analytics')
        .select('procedure_code, procedure_type, hospital_gross_charge, avg_gross_charge, inpatient_outpatient')
        .eq('hospital_id', selectedHospitalId)
        .in('procedure_code', procedureCodes);

      if (error) throw error;

      // Create a map of code:setting -> hospital price
      const priceMap = new Map();

      data?.forEach(row => {
        // Filter: only accept rows whose (code, type) matches the scenario's expected types
        // This prevents DRG 0200 (pneumothorax) from matching REV 0200 (newborn ICU)
        const dbType = row.procedure_type?.toLowerCase() || '';
        if (!allowedPairs.has(`${row.procedure_code}:${dbType}`)) return;

        const price = row.hospital_gross_charge || row.avg_gross_charge || 0;
        if (price > 0) {
          // Normalize procedure type to match scenario codeType format
          let normalizedType = row.procedure_type?.toUpperCase() || '';
          if (normalizedType === 'REVENUE') {
            normalizedType = 'REV';
          } else if (normalizedType === 'NDC') {
            normalizedType = 'DRUGS';
          }
          const setting = row.inpatient_outpatient || 'unspecified';
          const key = `${normalizedType}:${row.procedure_code}:${setting}`;
          priceMap.set(key, price);

          // Store 'unspecified' fallback key (max price across settings)
          const unspecifiedKey = `${normalizedType}:${row.procedure_code}:unspecified`;
          if (!priceMap.has(unspecifiedKey) || price > priceMap.get(unspecifiedKey)) {
            priceMap.set(unspecifiedKey, price);
          }

          // CPT/HCPCS cross-matching
          if (normalizedType === 'CPT' || normalizedType === 'HCPCS') {
            const altType = normalizedType === 'CPT' ? 'HCPCS' : 'CPT';
            const altKey = `${altType}:${row.procedure_code}:${setting}`;
            if (!priceMap.has(altKey)) priceMap.set(altKey, price);
            const altUnspecified = `${altType}:${row.procedure_code}:unspecified`;
            if (!priceMap.has(altUnspecified) || price > priceMap.get(altUnspecified)) {
              priceMap.set(altUnspecified, price);
            }
          }

          // NDC as DRUG for compatibility
          if (row.procedure_type?.toUpperCase() === 'NDC') {
            const drugKey = `DRUG:${row.procedure_code}:${setting}`;
            priceMap.set(drugKey, price);
            const drugUnspecifiedKey = `DRUG:${row.procedure_code}:unspecified`;
            if (!priceMap.has(drugUnspecifiedKey) || price > priceMap.get(drugUnspecifiedKey)) {
              priceMap.set(drugUnspecifiedKey, price);
            }
          }
        }
      });

      // Crosswalk-DRG scenarios (no DRG line item of their own): fetch the hospital's
      // published bundle price for the scenario's DRG family so the breakdown can
      // SHOW the line that drives a drg_blended total. Without this, a hospital like
      // UT Health Athens ranks #1-worst off a $634k DRG 474 row the page never
      // displayed — the total looked uncountable ("just looked like a bug").
      const drgMeta = (scenarios?.find((s: any) => s.uuid === selectedScenario?.uuid) as any)?.drgInpatient
        || (selectedScenario as any)?.drgInpatient;
      const familyCodes: string[] = Array.isArray(drgMeta?.drgFamily) ? drgMeta.drgFamily : [];
      if (familyCodes.length > 0) {
        const { data: drgRows } = await supabase
          .from('hospital_procedure_analytics')
          .select('procedure_code, hospital_gross_charge, avg_gross_charge')
          .eq('hospital_id', selectedHospitalId)
          .eq('procedure_type', 'drg')
          .in('procedure_code', familyCodes);
        // Family is ordered base-first; take the first family code the hospital
        // actually publishes (mirrors the coverage pipeline's pick).
        for (const code of familyCodes) {
          const r = drgRows?.find(d => d.procedure_code === code);
          const price = Number(r?.hospital_gross_charge || r?.avg_gross_charge) || 0;
          if (price > 0) {
            priceMap.set('__DRG_BUNDLE__', { code, price });
            break;
          }
        }
      }

      return priceMap;
    },
    enabled: !!selectedScenario && !!selectedHospitalId,
  });

  const togglePhase = (phase: string) => {
    const newCollapsed = new Set(collapsedPhases);
    if (newCollapsed.has(phase)) {
      newCollapsed.delete(phase);
    } else {
      newCollapsed.add(phase);
    }
    setCollapsedPhases(newCollapsed);
  };

  const getPhaseTotal = (scenario: Scenario, phase: string, priceMap: Map<string, any> | null | undefined = null, isHospitalData: boolean = false, fallbackMap?: Map<string, any> | null) => {
    return scenario.procedures
      .filter(p => p.phase === phase)
      .reduce((sum, p) => {
        const normalizedCodeType = p.codeType.toUpperCase();
        // No seed fallback: codes without real chargemaster pricing contribute 0,
        // matching scenario_costs_cache.real_total_cost so the phase breakdown
        // always reconciles to the headline. Never sum p.estimatedCost (seed).
        let unitPrice = 0;

        if (priceMap) {
          const setting = p.setting || 'unspecified';
          const keyAllSettings = `${normalizedCodeType}:${p.code}:all_settings`;
          const keyWithSetting = `${normalizedCodeType}:${p.code}:${setting}`;
          const keyUnspecified = `${normalizedCodeType}:${p.code}:unspecified`;
          let found = false;
          // Try all_settings first (most comprehensive), then exact setting, then unspecified
          for (const priceData of [priceMap.get(keyAllSettings), priceMap.get(keyWithSetting), priceMap.get(keyUnspecified)]) {
            if (priceData === undefined || priceData === null) continue;
            if (typeof priceData === 'number' && priceData > 0) {
              unitPrice = priceData;
              found = true;
              break;
            } else if (priceData?.price > 0) {
              unitPrice = priceData.price;
              found = true;
              break;
            }
          }

          // For hospital data: fall back to national average for unmatched procedures
          if (!found && isHospitalData && fallbackMap) {
            for (const priceData of [fallbackMap.get(keyAllSettings), fallbackMap.get(keyWithSetting), fallbackMap.get(keyUnspecified)]) {
              if (priceData === undefined || priceData === null) continue;
              if (typeof priceData === 'number' && priceData > 0) {
                unitPrice = priceData;
                break;
              } else if (priceData?.price > 0) {
                unitPrice = priceData.price;
                break;
              }
            }
          }
        }

        return sum + (unitPrice * p.quantity);
      }, 0);
  };

  // Helper to get available settings for a procedure from pricing data
  const getAvailableSettings = (proc: ScenarioProcedure, priceMap: Map<string, any> | null | undefined): string[] => {
    if (!priceMap) return [];
    const normalizedCodeType = proc.codeType.toUpperCase();
    const settings: string[] = [];

    ['inpatient', 'outpatient', 'unspecified', 'all_settings'].forEach(setting => {
      const key = `${normalizedCodeType}:${proc.code}:${setting}`;
      if (priceMap.has(key)) {
        settings.push(setting);
      }
    });

    return settings;
  };

  const getProcedurePrice = (proc: ScenarioProcedure, priceMap: Map<string, any> | null | undefined = null, isHospitalData: boolean = false, settingOverride?: string, fallbackMap?: Map<string, any> | null) => {
    const normalizedCodeType = proc.codeType.toUpperCase();
    const setting = settingOverride || proc.setting || 'unspecified';
    const keyWithSetting = `${normalizedCodeType}:${proc.code}:${setting}`;
    const keyAllSettings = `${normalizedCodeType}:${proc.code}:all_settings`;
    const keyUnspecified = `${normalizedCodeType}:${proc.code}:unspecified`;

    // No seed fallback — 0 until a real chargemaster price is found. foundInDatabase
    // stays false so callers can render an em-dash instead of a fabricated number.
    let unitPrice = 0;
    let foundInDatabase = false;
    let usedNationalFallback = false;
    let hospitalCount = 0;
    let dataSetting = '';
    let consumerName = '';

    if (priceMap) {
      // Try multiple fallbacks: all_settings first (most comprehensive), then exact setting, then unspecified
      // all_settings aggregates across all settings and has the most hospitals/best data
      const candidates = [
        priceMap.get(keyAllSettings),
        priceMap.get(keyWithSetting),
        priceMap.get(keyUnspecified),
      ];

      for (const priceData of candidates) {
        if (priceData === undefined || priceData === null) continue;

        if (typeof priceData === 'number') {
          // Hospital prices are stored as plain numbers
          if (priceData > 0) {
            unitPrice = priceData;
            foundInDatabase = true;
            break;
          }
        } else if (priceData.price !== undefined && priceData.price > 0) {
          // National averages are stored as objects with .price property
          unitPrice = priceData.price;
          foundInDatabase = true;
          hospitalCount = priceData.hospitalCount || 0;
          dataSetting = priceData.setting || 'unspecified';
          consumerName = priceData.consumerName || '';
          break;
        }
      }

      // For hospital data: if no hospital price, fall back to national average
      if (!foundInDatabase && isHospitalData && fallbackMap) {
        const fallbackCandidates = [
          fallbackMap.get(keyAllSettings),
          fallbackMap.get(keyWithSetting),
          fallbackMap.get(keyUnspecified),
        ];
        for (const priceData of fallbackCandidates) {
          if (priceData === undefined || priceData === null) continue;
          if (typeof priceData === 'number' && priceData > 0) {
            unitPrice = priceData;
            foundInDatabase = true;
            usedNationalFallback = true;
            break;
          } else if (priceData?.price > 0) {
            unitPrice = priceData.price;
            foundInDatabase = true;
            usedNationalFallback = true;
            hospitalCount = priceData.hospitalCount || 0;
            consumerName = priceData.consumerName || '';
            break;
          }
        }
      }
    }

    return { unitPrice, foundInDatabase, usedNationalFallback, hospitalCount, dataSetting, consumerName };
  };

  // Get timeline data for selected scenario with real pricing - grouped by individual procedures
  const getTimelineData = (scenario: Scenario | null, priceMap: Map<string, any> | null | undefined, isHospitalData: boolean = false, nationalPriceMap?: Map<string, any> | null) => {
    if (!scenario) return [];

    // Group procedures by day number, keeping individual procedure details
    const dailyData: { [key: number]: { day: number; procedures: Array<{ description: string; cost: number; code: string; codeType: string; consumerName: string; phase: string; setting?: string; hasHospitalData?: boolean; nationalCost?: number }> } } = {};

    scenario.procedures.forEach(proc => {
      const day = proc.dayNumber || 1;
      const normalizedCodeType = proc.codeType.toUpperCase();

      // No seed fallback — real chargemaster prices only (0 until found).
      let unitPrice = 0;
      let hasHospitalData = false;
      let nationalPrice = 0;
      let consumerName = '';

      if (priceMap) {
        const setting = proc.setting || 'unspecified';
        const keyWithSetting = `${normalizedCodeType}:${proc.code}:${setting}`;
        const keyAllSettings = `${normalizedCodeType}:${proc.code}:all_settings`;
        const keyUnspecified = `${normalizedCodeType}:${proc.code}:unspecified`;

        // Try all_settings first (most comprehensive), then exact setting, then unspecified
        let foundPrice = false;
        for (const priceData of [priceMap.get(keyAllSettings), priceMap.get(keyWithSetting), priceMap.get(keyUnspecified)]) {
          if (priceData === undefined || priceData === null) continue;
          if (typeof priceData === 'number' && priceData > 0) {
            unitPrice = priceData;
            if (isHospitalData) hasHospitalData = true;
            foundPrice = true;
            break;
          } else if (priceData.price !== undefined && priceData.price > 0) {
            unitPrice = priceData.price;
            consumerName = priceData.consumerName || '';
            if (isHospitalData) hasHospitalData = true;
            foundPrice = true;
            break;
          }
        }
        if (!foundPrice && isHospitalData && nationalPriceMap) {
          // Fallback to national average for hospital data
          for (const nationalPriceData of [nationalPriceMap.get(keyAllSettings), nationalPriceMap.get(keyWithSetting), nationalPriceMap.get(keyUnspecified)]) {
            if (nationalPriceData === undefined || nationalPriceData === null) continue;
            if (typeof nationalPriceData === 'number' && nationalPriceData > 0) {
              unitPrice = nationalPriceData;
              break;
            } else if (nationalPriceData.price !== undefined && nationalPriceData.price > 0) {
              unitPrice = nationalPriceData.price;
              consumerName = nationalPriceData.consumerName || '';
              break;
            }
          }
        }
      }

      // Get national price for comparison
      if (isHospitalData && nationalPriceMap) {
        const setting = proc.setting || 'unspecified';
        const keyWithSetting = `${normalizedCodeType}:${proc.code}:${setting}`;
        const keyAllSettings = `${normalizedCodeType}:${proc.code}:all_settings`;
        const keyUnspecified = `${normalizedCodeType}:${proc.code}:unspecified`;
        for (const nationalPriceData of [nationalPriceMap.get(keyAllSettings), nationalPriceMap.get(keyWithSetting), nationalPriceMap.get(keyUnspecified)]) {
          if (nationalPriceData === undefined || nationalPriceData === null) continue;
          if (typeof nationalPriceData === 'number' && nationalPriceData > 0) {
            nationalPrice = nationalPriceData;
            break;
          } else if (nationalPriceData.price !== undefined && nationalPriceData.price > 0) {
            nationalPrice = nationalPriceData.price;
            break;
          }
        }
      }

      const totalCost = unitPrice * proc.quantity;
      const nationalTotalCost = nationalPrice * proc.quantity;

      if (!dailyData[day]) {
        dailyData[day] = { day, procedures: [] };
      }

      dailyData[day].procedures.push({
        description: proc.description,
        cost: totalCost,
        code: proc.code,
        codeType: normalizedCodeType,
        consumerName: consumerName,
        phase: proc.phase,
        setting: proc.setting || 'unspecified',
        hasHospitalData: isHospitalData ? hasHospitalData : undefined,
        nationalCost: isHospitalData ? nationalTotalCost : undefined,
      });
    });

    // Convert to sorted array and calculate totals
    return Object.keys(dailyData)
      .map(day => parseInt(day))
      .sort((a, b) => a - b)
      .map(day => dailyData[day]);
  };

  const nationalTimelineData = getTimelineData(selectedScenario, nationalAverages, false);
  const hospitalTimelineData = getTimelineData(selectedScenario, hospitalPrices, true, nationalAverages);

  const maxDailyTotal = Math.max(
    nationalTimelineData.length > 0 ? Math.max(...nationalTimelineData.map(d => d.procedures.reduce((sum, p) => sum + p.cost, 0))) : 0,
    hospitalTimelineData.length > 0 ? Math.max(...hospitalTimelineData.map(d => d.procedures.reduce((sum, p) => sum + p.cost, 0))) : 0
  );

  // Calculate hospital data coverage
  const getHospitalCoverage = () => {
    if (!selectedScenario || !hospitalPrices) return { hasData: 0, total: 0, percentage: 0 };

    const total = selectedScenario.procedures.length;
    let hasData = 0;

    selectedScenario.procedures.forEach(proc => {
      const normalizedCodeType = proc.codeType.toUpperCase();
      const setting = proc.setting || 'unspecified';
      const keyWithSetting = `${normalizedCodeType}:${proc.code}:${setting}`;
      const keyUnspecified = `${normalizedCodeType}:${proc.code}:unspecified`;

      if (hospitalPrices.get(keyWithSetting) || hospitalPrices.get(keyUnspecified)) {
        hasData++;
      }
    });

    return { hasData, total, percentage: total > 0 ? (hasData / total) * 100 : 0 };
  };

  const hospitalCoverage = getHospitalCoverage();

  // Check if hospital comparison is active (used by chart)
  const hasHospitalComparison = selectedHospitalId && hospitalTimelineData.length > 0 && hospitalCoverage.percentage > 0;

  // Combine hardcoded and local scenarios, deduplicating by name (DB wins over local)
  const allScenarios = useMemo(() => {
    const dbNames = new Set(scenarios.map(s => s.name));
    const uniqueLocal = localScenarios.filter(ls => !dbNames.has(ls.name));
    return [...scenarios, ...uniqueLocal];
  }, [scenarios, localScenarios]);

  // Top 50 scenarios by usage for dropdown (sorted once, memoized)
  const popularScenarios = useMemo(() =>
    [...allScenarios]
      .filter(s => !s.scenarioFamilyId || (s.severityLevel ?? 0) === 0)
      .sort((a, b) => (b.usageCount ?? 0) - (a.usageCount ?? 0))
      .slice(0, 50),
    [allScenarios]
  );

  // Compute severity: base (level 0) from DB severity_level, levels 1-4 dynamically from cost percentiles
  const familySeverityMap = useMemo((): Map<string, number> => {
    const map = new Map<string, number>();
    if (!selectedScenario?.scenarioFamilyId) return map;
    const allFamily = allScenarios.filter(s => s.scenarioFamilyId === selectedScenario.scenarioFamilyId);
    if (allFamily.length <= 1) return map;

    // Base = scenarios with DB severity_level 0 (enriched from live query)
    const baseIds = new Set<string>();
    allFamily.forEach(s => { if ((s.severityLevel ?? 0) === 0) baseIds.add(s.uuid); });

    // Fallback: if ALL are 0 (stale data), use cheapest as base
    if (baseIds.size === 0 || baseIds.size === allFamily.length) {
      baseIds.clear();
      const cheapest = [...allFamily].sort((a, b) => (a.estimatedCost ?? 0) - (b.estimatedCost ?? 0))[0];
      baseIds.add(cheapest.uuid);
    }

    baseIds.forEach(id => map.set(id, 0));

    // Compute levels 1-4 for non-base from cost percentiles
    const nonBase = allFamily
      .filter(s => !baseIds.has(s.uuid))
      .sort((a, b) => (a.estimatedCost ?? 0) - (b.estimatedCost ?? 0));
    const n = nonBase.length;
    nonBase.forEach((s, idx) => {
      if (n <= 1) { map.set(s.uuid, 4); return; }
      const pctRank = idx / (n - 1);
      map.set(s.uuid, pctRank <= 0.25 ? 1 : pctRank <= 0.50 ? 2 : pctRank <= 0.75 ? 3 : 4);
    });
    return map;
  }, [selectedScenario, allScenarios]);

  // Family members: same scenario_family_id, with dynamically computed severity
  const familyScenarios = useMemo(() => {
    if (!selectedScenario?.scenarioFamilyId) return [];
    return allScenarios
      .filter(s => s.scenarioFamilyId === selectedScenario.scenarioFamilyId && s.uuid !== selectedScenario.uuid)
      .map(s => ({ ...s, severityLevel: familySeverityMap.get(s.uuid) ?? s.severityLevel ?? 0 }))
      .sort((a, b) => (a.severityLevel ?? 0) - (b.severityLevel ?? 0));
  }, [selectedScenario, allScenarios, familySeverityMap]);

  // Base procedure codes: identifies procedures from the severity-0 (base) scenario in the current family
  // Used to visually differentiate "standard" vs "complication-added" procedures on variant views
  const baseProcedureCodes = useMemo((): Set<string> | null => {
    if (!selectedScenario?.scenarioFamilyId) return null;
    const computedSev = familySeverityMap.get(selectedScenario.uuid) ?? selectedScenario.severityLevel ?? 0;
    if (computedSev === 0) return null; // Don't differentiate on the base scenario itself
    const baseScenario = allScenarios.find(s =>
      s.scenarioFamilyId === selectedScenario.scenarioFamilyId &&
      (familySeverityMap.get(s.uuid) ?? s.severityLevel ?? 0) === 0
    );
    if (!baseScenario?.procedures?.length) return null;
    const codes = new Set<string>();
    for (const p of baseScenario.procedures) codes.add(`${p.codeType}:${p.code}`);
    return codes;
  }, [selectedScenario, allScenarios, familySeverityMap]);

  // Related scenarios: tag-based, excluding family members
  const relatedScenarios = useMemo(() => {
    if (!selectedScenario) return [];
    const familyIds = new Set(familyScenarios.map(s => s.uuid));
    const selectedTags = new Set(selectedScenario.tags || []);
    const selectedCategory = selectedScenario.category;
    // Generic tags that shouldn't drive relatedness on their own
    const GENERIC_TAGS = new Set(['inpatient', 'outpatient', 'surgery', 'emergency', 'chronic', 'acute', 'preventive', 'screening']);
    return allScenarios
      .filter(s => s.uuid !== selectedScenario.uuid && !familyIds.has(s.uuid))
      .map(s => {
        const sTags = s.tags || [];
        // Count meaningful tag overlaps (excluding generic tags)
        const meaningfulOverlap = sTags.filter(t => selectedTags.has(t) && !GENERIC_TAGS.has(t)).length;
        const genericOverlap = sTags.filter(t => selectedTags.has(t) && GENERIC_TAGS.has(t)).length;
        // Category match is a strong signal
        const categoryMatch = selectedCategory && s.category === selectedCategory ? 20 : 0;
        // Score: category match >> meaningful tag overlap >> generic overlap
        const score = categoryMatch + meaningfulOverlap * 10 + genericOverlap * 1;
        return { ...s, _score: score };
      })
      .sort((a, b) => (b as any)._score - (a as any)._score)
      .filter(s => (s as any)._score >= 10) // Require at least category match OR meaningful tag overlap
      .slice(0, 12);
  }, [selectedScenario, allScenarios, familyScenarios]);

  // Severity-filtered scenarios: all scenarios at a given severity level across the database
  const severityFilteredScenarios = useMemo(() => {
    if (activeSeverityFilter === null || !selectedScenario) return [];
    return allScenarios
      .filter(s => (s.severityLevel ?? 0) === activeSeverityFilter && s.uuid !== selectedScenario.uuid)
      .sort((a, b) => (b.usageCount ?? 0) - (a.usageCount ?? 0))
      .slice(0, 60);
  }, [activeSeverityFilter, selectedScenario, allScenarios]);

  // Browse-filtered scenarios for card grid (when no scenario selected)
  // Curated consumer order for the default browse grid: the everyday conditions
  // a person would actually expect to pre-research, base (severity-0) scenario of
  // each family first. Anything not listed falls to the alphabetical long tail.
  const CONSUMER_PRIORITY = [
    "Uncomplicated Pregnancy & Vaginal Delivery", "C-Section Delivery",
    "Broken Arm Treatment", "Broken Wrist Repair", "Broken Ankle Repair", "Broken Hip Repair",
    "Ankle Sprain Treatment", "Concussion Evaluation", "Chest Pain Workup", "Heart Attack Treatment",
    "Appendectomy (Emergency", "Gallbladder Removal (Laparoscopic", "Hernia Repair (Inguinal",
    "Knee Arthroscopy", "Kidney Stone Prevention", "Urinary Tract Infection (UTI)",
    "Colonoscopy (Screening", "Mammogram (Screening", "Cataract Surgery",
    "Wisdom Teeth Extraction", "Tonsillectomy (Adult", "Strep Throat Treatment",
    "Community-Acquired Pneumonia — Outpatient", "Childhood Asthma", "COVID-19 — Outpatient",
    "Food Poisoning Treatment", "Gallstone Attack", "Vasectomy (Male Sterilization", "Annual Wellness Visit",
  ];
  const browseFilteredScenarios = useMemo(() => {
    const cat = getCategoryByKey(browseCategory) || SCENARIO_CATEGORIES[0];
    const prio = (name: string) => {
      const lower = name.toLowerCase();
      const i = CONSUMER_PRIORITY.findIndex(k => lower.includes(k.toLowerCase()));
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return allScenarios
      // Base / standalone only — one card per family (its base), no severity variants
      .filter(s => !s.scenarioFamilyId || (s.severityLevel ?? 0) === 0)
      .filter(s => scenarioMatchesCategory(s.tags || [], cat))
      .sort((a, b) => {
        const pa = prio(a.name), pb = prio(b.name);
        if (pa !== pb) return pa - pb;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 60);
  }, [allScenarios, browseCategory]);

  // Auto-select scenario from URL parameters on page load
  useEffect(() => {
    if (uuid && allScenarios.length > 0 && (!selectedScenario || selectedScenario.uuid !== uuid)) {
      const scenarioFromUrl = allScenarios.find(s => s.uuid === uuid);
      if (scenarioFromUrl) {
        console.log('Auto-selecting scenario from URL:', scenarioFromUrl.name);
        // Functional updater reads the LATEST state, not this effect's stale
        // closure. This effect re-runs when `allScenarios` changes (e.g. late
        // localScenarios load) and can fire in the same commit that the detail
        // fetch hydrated procedures — a plain set would clobber the hydrated
        // scenario back to the procedure-less index row ("0 procedures / $0").
        // Never downgrade a same-uuid scenario that already has procedures.
        setSelectedScenario(prev =>
          (prev && prev.uuid === uuid && prev.procedures && prev.procedures.length > 0)
            ? prev
            : scenarioFromUrl
        );
        setScenarioSearchQuery(scenarioFromUrl.name);
      }
    }
  }, [uuid, allScenarios]);

  // Auto-focus hospital search input when focus=hospital parameter is present
  useEffect(() => {
    const focusParam = searchParams.get('focus');
    if (focusParam === 'hospital' && nameInputRef.current) {
      // Wait a bit for the page to render and scenario to be selected
      setTimeout(() => {
        nameInputRef.current?.focus();
        setShowHospitalDropdown(true);
      }, 500);
    }
  }, [searchParams]);

  // Auto-open the Family Explorer modal when ?family=open is set in the URL.
  // Makes the modal a shareable, deep-linkable view (e.g. send a friend
  // /scenarios/{uuid}/{slug}?family=open and they land directly on the family explorer).
  useEffect(() => {
    if (searchParams.get('family') === 'open' && selectedScenario?.scenarioFamilyId) {
      setShowFamilyModal(true);
    }
  }, [searchParams, selectedScenario]);

  // Search index: lowercase each scenario's searchable text ONCE per list, not
  // once per keystroke. This used to be a bare .filter() over ~12k scenarios
  // that re-lowercased five fields each — and, being outside useMemo, it also
  // re-ran on every unrelated render. That was the typing lag.
  const scenarioSearchIndex = useMemo(
    () => allScenarios.map(s => ({
      s,
      name: (s.name || '').toLowerCase(),
      hay: [
        s.name || '',
        s.description || '',
        Array.isArray(s.phases) ? s.phases.filter(p => typeof p === 'string').join(' ') : '',
        Array.isArray(s.tags) ? s.tags.filter(t => typeof t === 'string').join(' ') : '',
        s.familyVariantLabel || '',
      ].join(' ').toLowerCase(),
    })),
    [allScenarios],
  );

  // Match on WORDS, in any order — "Hip MRI" has to find "Hip Bursitis —
  // Refractory with MRI and PRP Injection". A contiguous includes() never could.
  const filteredScenarios = useMemo(() => {
    const phrase = scenarioSearchQuery.trim().toLowerCase();
    if (!phrase) return allScenarios;
    const tokens = tokenize(phrase);
    const hits: { s: Scenario; rank: number; pos: number }[] = [];
    for (const e of scenarioSearchIndex) {
      const rank = rankMatch(e.name, e.hay, phrase, tokens);
      if (rank >= 0) hits.push({ s: e.s, rank, pos: matchPosition(e.name, tokens) });
    }
    hits.sort((a, b) => a.rank - b.rank || a.pos - b.pos || (a.s.name || '').localeCompare(b.s.name || ''));
    return hits.map(h => h.s);
  }, [allScenarios, scenarioSearchIndex, scenarioSearchQuery]);

  // Grouped search results: family-aware grouping with category filter
  interface SearchGroup {
    type: 'standalone' | 'family';
    baseScenario: Scenario;
    matchedVariants: Scenario[];
    allFamilyMembers: Scenario[]; // ALL variants in the family (not just search-matched)
    totalFamilySize: number;
    costMin: number;
    costMax: number;
  }

  const groupedSearchResults = useMemo((): SearchGroup[] => {
    if (scenarioSearchQuery === '') return [];

    // 1. Apply category filter
    const catFiltered = searchCategory === 'all'
      ? filteredScenarios
      : filteredScenarios.filter(s => scenarioMatchesCategory(s.tags || [], SCENARIO_CATEGORIES.find(c => c.key === searchCategory)!));

    // 2. Group by scenarioFamilyId
    const familyGroups = new Map<string, Scenario[]>();
    const standalone: Scenario[] = [];

    for (const s of catFiltered) {
      if (s.scenarioFamilyId) {
        const arr = familyGroups.get(s.scenarioFamilyId) || [];
        arr.push(s);
        familyGroups.set(s.scenarioFamilyId, arr);
      } else {
        standalone.push(s);
      }
    }

    const results: SearchGroup[] = [];

    // Standalone scenarios
    for (const s of standalone) {
      results.push({ type: 'standalone', baseScenario: s, matchedVariants: [], allFamilyMembers: [], totalFamilySize: 1, costMin: s.estimatedCost ?? 0, costMax: s.estimatedCost ?? 0 });
    }

    // Family groups
    for (const [familyId, members] of familyGroups) {
      // Find the base (severity 0) scenario
      let base = members.find(s => (s.severityLevel ?? 0) === 0);
      if (!base) {
        base = allScenarios.find(s => s.scenarioFamilyId === familyId && (s.severityLevel ?? 0) === 0);
      }
      if (!base) {
        base = [...members].sort((a, b) => (a.estimatedCost ?? 0) - (b.estimatedCost ?? 0))[0];
      }

      const allFamily = allScenarios.filter(s => s.scenarioFamilyId === familyId);
      const variants = members.filter(s => s.uuid !== base!.uuid);
      const totalFamilySize = allFamily.length;
      const costs = allFamily.map(s => s.estimatedCost ?? 0).filter(c => c > 0);
      const costMin = costs.length > 0 ? Math.min(...costs) : 0;
      const costMax = costs.length > 0 ? Math.max(...costs) : 0;

      results.push({ type: 'family', baseScenario: base, matchedVariants: variants, allFamilyMembers: allFamily.filter(s => s.uuid !== base!.uuid).sort((a, b) => (a.severityLevel ?? 0) - (b.severityLevel ?? 0)), totalFamilySize, costMin, costMax });
    }

    // Sort: direct name match first, then by usage
    const q = scenarioSearchQuery.toLowerCase();
    results.sort((a, b) => {
      const aNameMatch = a.baseScenario.name.toLowerCase().includes(q) ? 1 : 0;
      const bNameMatch = b.baseScenario.name.toLowerCase().includes(q) ? 1 : 0;
      if (bNameMatch !== aNameMatch) return bNameMatch - aNameMatch;
      return (b.baseScenario.usageCount ?? 0) - (a.baseScenario.usageCount ?? 0);
    });

    return results.slice(0, 50);
  }, [filteredScenarios, searchCategory, scenarioSearchQuery, allScenarios]);

  // Hospitals are now filtered server-side in the query, so no need for client-side filtering

  // Get selected hospital details for display
  const selectedHospital = hospitals?.find(h => h.id === selectedHospitalId);

  // Handle scenario selection
  const handleScenarioSelect = (scenario: Scenario) => {
    selectScenario(scenario);
    setScenarioSearchQuery(scenario.name);
    setShowScenarioDropdown(false);
    trackScenarioView(scenario.id, scenario.name);

    // Add to recent views and searches
    scenarioStorage.addRecentView(scenario.id);
    scenarioStorage.addRecentSearch(scenario.name);
    setRecentSearches(scenarioStorage.getRecentSearches());
  };

  // Handle recent search click
  const handleRecentSearchClick = (searchTerm: string) => {
    setScenarioSearchQuery(searchTerm);
    setShowScenarioDropdown(true);
  };

  // Clear recent searches
  const handleClearRecentSearches = (e: React.MouseEvent) => {
    e.stopPropagation();
    scenarioStorage.clearRecentSearches();
    setRecentSearches([]);
  };

  // Toggle bookmark for scenario
  const toggleBookmark = (scenarioId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent selecting the scenario
    const isNowBookmarked = scenarioStorage.toggleBookmark(scenarioId);
    setBookmarkedScenarioIds(scenarioStorage.getBookmarks());
    trackScenarioBookmark(scenarioId, '', isNowBookmarked);
  };

  // Add to My Plan handler
  const handleAddToPlan = (scenario: Scenario) => {
    if (!addToPlanMember) return;

    // Check if already exists locally
    const existing = scenarioStorage.getUserScenarios().find(s => s.name === scenario.name);
    if (existing) {
      scenarioStorage.assignToFamilyMember(existing.id, addToPlanMember);
      scenarioStorage.updateScenario(existing.id, { plannedDate: addToPlanDate || undefined });
    } else {
      scenarioStorage.saveScenario({
        name: scenario.name,
        description: scenario.description || '',
        duration: scenario.duration || '1 month',
        totalProcedures: scenario.totalProcedures || scenario.procedures?.length || 0,
        estimatedCost: scenario.estimatedCost || 0,
        procedures: (scenario.procedures || []).map(p => ({
          code: p.code,
          codeType: p.codeType,
          description: p.description,
          phase: p.phase,
          quantity: p.quantity,
          estimatedCost: p.estimatedCost,
          dayNumber: p.dayNumber,
          setting: p.setting,
        })),
        phases: scenario.phases || [],
        tags: scenario.tags || [],
        severityLevel: scenario.severityLevel,
        isDraft: false,
        visibility: 'unlisted',
        shareToken: '',
        source: 'template',
        familyMemberId: addToPlanMember,
        plannedDate: addToPlanDate || undefined,
      });
    }

    setShowAddToPlan(null);
    toast.success(`Added "${scenario.name}" to your plan`, {
      action: {
        label: 'View Plan',
        onClick: () => navigate('/family'),
      },
    });
  };

  // Handle hospital selection
  const handleHospitalSelect = (hospitalId: string) => {
    setSelectedHospitalId(hospitalId);
    const hospital = hospitals?.find(h => h.id === hospitalId);
    if (hospital) {
      setHospitalNameQuery(hospital.name);
      setHospitalLocationQuery(`${hospital.city}, ${hospital.state}`);
      trackScenarioHospitalSelect(selectedScenario?.id || '', hospitalId, hospital.name);
    }
    setShowHospitalDropdown(false);

    // Update URL to deep-linkable hospital route (without triggering navigation)
    if (selectedScenario) {
      const scenarioSlug = slug || selectedScenario.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const newPath = `/scenarios/${selectedScenario.id}/${scenarioSlug}/hospital/${hospitalId}`;
      if (routerLocation.pathname !== newPath) {
        navigate(newPath, { replace: true });
      }
    }
  };

  // Remove hospital from URL when deselected
  useEffect(() => {
    if (!selectedHospitalId && selectedScenario && hospitalIdParam) {
      const scenarioSlug = slug || selectedScenario.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      navigate(`/scenarios/${selectedScenario.id}/${scenarioSlug}`, { replace: true });
    }
  }, [selectedHospitalId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate total costs
  // When computing hospital totals, fall back to national average for missing procedures
  // (matches the "blended" price shown in rankings cards)
  const calculateTotalCost = (scenario: Scenario | null, priceMap: Map<string, any> | null | undefined, isHospitalData: boolean = false, fallbackMap?: Map<string, any> | null) => {
    if (!scenario || !scenario.procedures) return 0;
    return scenario.procedures.reduce((total, proc) => {
      // No seed fallback — codes without a code or without real pricing add 0.
      if (!proc.codeType || !proc.code) return total;
      const normalizedCodeType = proc.codeType.toUpperCase();

      let unitPrice = 0;

      if (priceMap) {
        const setting = proc.setting || 'unspecified';
        const keyAllSettings = `${normalizedCodeType}:${proc.code}:all_settings`;
        const keyWithSetting = `${normalizedCodeType}:${proc.code}:${setting}`;
        const keyUnspecified = `${normalizedCodeType}:${proc.code}:unspecified`;

        let found = false;
        // Try all_settings first (most comprehensive), then exact setting, then unspecified
        for (const priceData of [priceMap.get(keyAllSettings), priceMap.get(keyWithSetting), priceMap.get(keyUnspecified)]) {
          if (priceData === undefined || priceData === null) continue;
          if (typeof priceData === 'number' && priceData > 0) {
            unitPrice = priceData;
            found = true;
            break;
          } else if (priceData?.price > 0) {
            unitPrice = priceData.price;
            found = true;
            break;
          }
        }

        // For hospital data: if no hospital price found, use national average as fallback
        if (!found && isHospitalData && fallbackMap) {
          for (const priceData of [fallbackMap.get(keyAllSettings), fallbackMap.get(keyWithSetting), fallbackMap.get(keyUnspecified)]) {
            if (priceData === undefined || priceData === null) continue;
            if (typeof priceData === 'number' && priceData > 0) {
              unitPrice = priceData;
              break;
            } else if (priceData?.price > 0) {
              unitPrice = priceData.price;
              break;
            }
          }
        }
      }

      const safePrice = (typeof unitPrice === 'number' && isFinite(unitPrice)) ? unitPrice : (proc.estimatedCost || 0);
      return total + (safePrice * (proc.quantity || 1));
    }, 0);
  };

  // ── Single source of truth for the headline totals ──
  //
  // The previous implementation called calculateTotalCost() which loops
  // procedure × nationalPricing[code].price for EVERY procedure. For
  // scenarios that resolve to a DRG bundle (C-Section, Heart Attack,
  // Total Knee, etc.), the inpatient line items already FOLD INTO the
  // DRG — summing the DRG row + the per-line inpatient items double-
  // counted the bundle contents and produced ~$37K/$23K against a
  // methodology-correct $29K for C-Section.
  //
  // scenario_costs_cache.real_total_cost / real_median_cost is computed
  // server-side with DRG fold-in already applied (see
  // refresh_scenario_costs.py CTE). The R2 export carries those values
  // into selectedScenario.estimatedCost / .medianCost, so the page just
  // renders them. Per-procedure cells lower in the page continue to use
  // nationalAverages.get(code) — that's per-code data, not subject to
  // bundle fold-in.
  //
  // Hospital total: scenario_hospital_coverage.total_hospital_price is
  // also DRG-aware (the same refresh script populates it with the bundle
  // accounting). selectedHospital.blendedPrice falls back to estimatedCost
  // for codes the hospital doesn't price, which is the right behavior.
  const cacheAvgTotal = selectedScenario?.estimatedCost ?? 0;        // capped/default avg
  const cacheAvgRawTotal = selectedScenario?.estimatedCostRaw ?? 0;  // uncapped avg
  const cacheMedianTotal = selectedScenario?.medianCost ?? 0;
  // 3-way: median → median; avg+raw → raw (fallback to capped); avg+capped → capped.
  const nationalTotal = (costStat === 'median' && cacheMedianTotal > 0)
    ? cacheMedianTotal
    : (costScope === 'raw' && cacheAvgRawTotal > 0 ? cacheAvgRawTotal : cacheAvgTotal);
  const hospitalTotal = (selectedHospital && (selectedHospital.blendedPrice ?? 0) > 0)
    ? Number(selectedHospital.blendedPrice)
    : 0;
  const priceDifference = hospitalTotal - nationalTotal;
  const priceDifferencePercent = nationalTotal > 0 ? ((priceDifference / nationalTotal) * 100) : 0;

  // ── DRG inpatient-bundle accounting ──
  //
  // Inpatient scenarios like C-Section list BOTH the itemized codes for a phase
  // (e.g. "Surgery & Delivery") AND a single bundled DRG code that, per the
  // curated crosswalk (scenario_drg_mappings.coversPhases), REPLACES those codes.
  // Summing both double-counts. The toggle flips between:
  //   • Bundle (ON, default): the DRG line carries the real DRG national/hospital
  //     price; the itemized codes in covered phases fold into it (not summed).
  //   • Itemized (OFF): the covered-phase codes show individually; the DRG line
  //     is dropped. Everything downstream reads the *display* totals below.
  //
  // drgInpatient lives on the lightweight index entry (not the per-scenario
  // detail file), so look it up there first, then fall back to selectedScenario.
  const drgInfo = useMemo(() => {
    const fromIndex = scenarios?.find((s: any) => s.uuid === selectedScenario?.uuid)?.drgInpatient;
    return fromIndex || (selectedScenario as any)?.drgInpatient || null;
  }, [scenarios, selectedScenario]);
  const isDrgLineProc = (p: { codeType?: string }) => (p.codeType || '').toUpperCase() === 'DRG';
  // Phases the DRG bundle replaces. Prefer the curated coversPhases; fall back to
  // whichever phase(s) the DRG line item itself sits in.
  const coveredPhases = useMemo(() => {
    const cp = drgInfo?.coversPhases;
    if (Array.isArray(cp) && cp.length) return new Set<string>(cp);
    const fromProc = (selectedScenario?.procedures || [])
      .filter(isDrgLineProc).map(p => p.phase).filter(Boolean) as string[];
    return new Set<string>(fromProc);
  }, [drgInfo, selectedScenario]);
  const scenarioHasDrgLine = !!selectedScenario?.procedures?.some(isDrgLineProc);
  const isDrgScenario = scenarioHasDrgLine && coveredPhases.size > 0;

  // Classify each procedure and sum the two totals. A procedure is either the DRG
  // bundle line (bundle-only), a covered-phase itemized code (itemized-only), or
  // neither (counts in both).
  const drgSums = useMemo(() => {
    const r = { natBundle: 0, natItemized: 0, hospBundle: 0, hospItemized: 0 };
    if (!selectedScenario) return r;
    for (const proc of selectedScenario.procedures) {
      const nat = getProcedurePrice(proc, nationalAverages, false).unitPrice * proc.quantity;
      const hosp = (selectedHospitalId && hospitalPrices)
        ? getProcedurePrice(proc, hospitalPrices, true, undefined, nationalAverages).unitPrice * proc.quantity
        : 0;
      if (isDrgLineProc(proc)) {
        r.natBundle += nat; r.hospBundle += hosp;             // bundle only
      } else if (coveredPhases.has(proc.phase)) {
        r.natItemized += nat; r.hospItemized += hosp;         // itemized only
      } else {
        r.natBundle += nat; r.natItemized += nat;             // both
        r.hospBundle += hosp; r.hospItemized += hosp;
      }
    }
    return r;
  }, [selectedScenario, nationalAverages, hospitalPrices, selectedHospitalId, coveredPhases]); // eslint-disable-line react-hooks/exhaustive-deps

  // Active display mode. Everything on this page is summed from the SAME per-code
  // source (real DRG avg + itemized line-items) so the breakdown phases, footer,
  // price spread and chart always reconcile to each other. Bundle = DRG line +
  // non-covered itemized; itemized = every line-item (DRG line dropped). Non-DRG
  // scenarios fall through to the cached headline totals unchanged.
  const showItemizedInpatient = isDrgScenario && !includeDrgBundle;
  const displayNationalTotal = !isDrgScenario ? nationalTotal : (includeDrgBundle ? drgSums.natBundle : drgSums.natItemized);
  const displayHospitalTotal = !isDrgScenario ? hospitalTotal : (includeDrgBundle ? drgSums.hospBundle : drgSums.hospItemized);
  const displayPriceDifference = displayHospitalTotal - displayNationalTotal;
  const displayPriceDiffPercent = displayNationalTotal > 0 ? ((displayPriceDifference / displayNationalTotal) * 100) : 0;

  // Distinct chart phase/legend label for the DRG bundle bar.
  const DRG_BUNDLE_PHASE = 'Inpatient DRG Bundle';

  // Reshape the timeline for the chart per mode. Bundle: keep the DRG line
  // (relabelled to its own series) and drop the covered-phase itemized codes.
  // Itemized: drop the DRG line, keep everything else. Non-DRG: untouched.
  const bundleTransformTimeline = (data: Array<{ day: number; procedures: any[] }>) => {
    if (!isDrgScenario) return data;
    return data.map(d => ({
      ...d,
      procedures: d.procedures.reduce((acc: any[], p: any) => {
        if (isDrgLineProc(p)) {
          if (includeDrgBundle) acc.push({ ...p, phase: DRG_BUNDLE_PHASE });
          // itemized mode: drop the DRG line
        } else if (coveredPhases.has(p.phase)) {
          if (!includeDrgBundle) acc.push(p); // itemized mode: keep the line-items
          // bundle mode: folded into the DRG bar
        } else {
          acc.push(p); // non-covered: always shown
        }
        return acc;
      }, []),
    }));
  };
  const chartNationalData = bundleTransformTimeline(nationalTimelineData);
  const chartHospitalData = bundleTransformTimeline(hospitalTimelineData);

  // State name lookup for SEO
  const STATE_NAMES: Record<string, string> = { AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',CT:'Connecticut',DE:'Delaware',DC:'District of Columbia',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming' };
  const stateName = stateParam ? STATE_NAMES[stateParam.toUpperCase()] : null;
  const stateCode = stateParam?.toUpperCase();

  return (
    <>
      <Helmet>
        <title>{selectedScenario
          ? activeSeverityFilter !== null
            ? `${COMP_LABELS[activeSeverityFilter]} ${selectedScenario.name} Scenarios — Severity Level ${activeSeverityFilter} | ${brand.name}`
            : stateName
              ? `${selectedScenario.name} in ${stateName} — Hospital Prices & Cheapest Options | ${brand.name}`
              : `${selectedScenario.name} — Cost Breakdown & Hospital Prices | ${brand.name}`
          : t('scenarios:meta.pageTitle', { brandName: brand.name })
        }</title>
        <meta name="description" content={selectedScenario
          ? activeSeverityFilter !== null
            ? `Browse all ${COMP_LABELS[activeSeverityFilter].toLowerCase()} severity medical scenarios. ${severityFilteredScenarios.length} scenarios at severity level ${activeSeverityFilter}/4 with real hospital pricing data on ${brand.name}.`
            : stateName
              ? `Compare ${selectedScenario.name} hospital prices in ${stateName}. Find the cheapest and most expensive hospitals in ${stateCode}. National average: $${Math.round(nationalTotal > 0 ? nationalTotal : selectedScenario.estimatedCost).toLocaleString()} across ${selectedScenario.totalProcedures} procedures.`
              : `${selectedScenario.name}: $${Math.round(nationalTotal > 0 ? nationalTotal : selectedScenario.estimatedCost).toLocaleString()} average cost. ${(selectedScenario.aiConsumerMessage || selectedScenario.description || '').slice(0, 140).replace(/\s+\S*$/, '')}. Compare hospital prices near you.`
          : `Browse ${allScenarios.length.toLocaleString()}+ medical cost scenarios with real hospital pricing data. From routine checkups to complex surgeries — see what procedures actually cost at ${brand.name}.`
        } />
        <meta name="keywords" content={selectedScenario
          ? stateName
            ? `${selectedScenario.name} ${stateName}, ${selectedScenario.name} cost ${stateCode}, cheapest hospital ${selectedScenario.name} ${stateName}, hospital prices ${stateName}, ${(selectedScenario.tags || []).join(', ')}`
            : `${selectedScenario.name}, ${selectedScenario.name} cost, ${selectedScenario.name} hospital price, ${(selectedScenario.tags || []).join(', ')}, medical cost estimate, hospital price transparency`
          : 'medical cost scenarios, hospital treatment costs, healthcare price transparency, surgery cost estimate, US hospital prices, procedure cost breakdown'
        } />
        <link rel="canonical" href={(() => {
          // langPrefix branch is dead code: middleware 301s /es/, /fr/, /de/, etc.
          // to bare paths (functions/_middleware.ts I18N_PREFIXES), so this code
          // never executes with a lang prefix. Kept for transition safety.
          const langMatch = routerLocation.pathname.match(/^\/([a-z]{2,3})\//);
          const langPrefix = langMatch ? `/${langMatch[1]}` : '';
          if (selectedScenario) {
            if (activeSeverityFilter !== null) {
              return `${brand.primaryDomain}${langPrefix}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}${stateParam ? `/${stateParam.toUpperCase()}` : ''}/severity/${activeSeverityFilter}`;
            }
            // 2026-06-14: scenario+state URL-segment form (`/scenarios/{uuid}/{slug}/{state}`)
            // is now a 301 in middleware → `/hospital-rankings/{state}?scenario={uuid}` (the
            // sitemap form). The redirect fires for ALL UAs incl Google's JS render pass,
            // so this Helmet canonical is only reached if someone disables the worker.
            // Point it at the sitemap form directly anyway as defense-in-depth so no
            // canonical-vs-sitemap split exists at any rendering layer.
            if (stateName) {
              return `${brand.primaryDomain}/hospital-rankings/${stateCode!.toLowerCase()}?scenario=${selectedScenario.uuid}`;
            }
            return `${brand.primaryDomain}${langPrefix}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`;
          }
          return `${brand.primaryDomain}${langPrefix}/scenarios`;
        })()} />
        <meta property="og:title" content={selectedScenario
          ? stateName
            ? `${selectedScenario.name} in ${stateName} — Hospital Price Comparison`
            : `${selectedScenario.name} — $${Math.round(nationalTotal > 0 ? nationalTotal : selectedScenario.estimatedCost).toLocaleString()} Average Cost`
          : t('scenarios:meta.ogTitle')
        } />
        <meta property="og:description" content={selectedScenario
          ? stateName
            ? `Find the cheapest hospitals for ${selectedScenario.name} in ${stateName}. Compare prices across ${stateCode} hospitals on ${brand.name}.`
            : `${(selectedScenario.aiConsumerMessage || selectedScenario.description || '').slice(0, 180).replace(/\s+\S*$/, '')}. Compare pricing across 9,000+ US hospitals.`
          : t('scenarios:meta.description')
        } />
        <meta property="og:type" content={selectedScenario ? 'article' : 'website'} />
        <meta property="og:url" content={selectedScenario
          ? stateName
            ? `${brand.primaryDomain}/hospital-rankings/${stateCode!.toLowerCase()}?scenario=${selectedScenario.uuid}`
            : `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`
          : `${brand.primaryDomain}/scenarios`
        } />
        <meta property="og:image" content={brand.key === 'ahp' ? 'https://americanhospitalprices.com/consumer/og-calculator.png' : brand.social.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={brand.social.twitter} />
        <meta name="twitter:title" content={selectedScenario ? `${selectedScenario.name} — Cost Breakdown` : t('scenarios:meta.ogTitle')} />
        <meta name="twitter:description" content={selectedScenario ? `$${Math.round(nationalTotal > 0 ? nationalTotal : (selectedScenario?.estimatedCost || 0)).toLocaleString()} estimated cost across ${selectedScenario?.totalProcedures || 0} procedures` : t('scenarios:meta.description')} />
        <meta name="twitter:image" content={brand.key === 'ahp' ? 'https://americanhospitalprices.com/consumer/og-calculator.png' : brand.social.ogImage} />

        {/* JSON-LD: Scenario detail page = MedicalCondition + Pricing */}
        {selectedScenario ? (<>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalCondition",
                "name": selectedScenario.name,
                "description": selectedScenario.aiConsumerMessage || selectedScenario.description,
                "url": `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`,
                "associatedAnatomy": selectedScenario.category ? { "@type": "AnatomicalStructure", "name": selectedScenario.category } : undefined,
                ...(selectedScenario.aiClinicalSummary ? { "signOrSymptom": { "@type": "MedicalSignOrSymptom", "name": "Clinical Pathway", "description": selectedScenario.aiClinicalSummary } } : {}),
                "typicalTest": selectedScenario.procedures?.slice(0, 10).map(p => ({
                  "@type": "MedicalTest",
                  "name": p.description,
                  "code": { "@type": "MedicalCode", "codeValue": p.code, "codingSystem": p.codeType === 'CPT' ? 'CPT-4' : p.codeType === 'HCPCS' ? 'HCPCS' : p.codeType },
                })),
              },
              {
                "@type": "Product",
                "name": `${selectedScenario.name} — Medical Cost Estimate`,
                "description": `Complete cost breakdown for ${selectedScenario.name} with ${selectedScenario.totalProcedures} procedures across ${selectedScenario.phases?.length || 0} phases`,
                "url": `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`,
                "brand": { "@type": "Organization", "name": brand.name },
                "offers": {
                  "@type": "AggregateOffer",
                  "priceCurrency": "USD",
                  "lowPrice": seoRankings?.cheapest?.[0]?.price ? Math.round(seoRankings.cheapest[0].price) : Math.round((nationalTotal > 0 ? nationalTotal : selectedScenario.estimatedCost) * 0.3),
                  "highPrice": seoRankings?.mostExpensive?.[0]?.price ? Math.round(seoRankings.mostExpensive[0].price) : Math.round((nationalTotal > 0 ? nationalTotal : selectedScenario.estimatedCost) * 3.5),
                  "offerCount": seoRankings?.total || 9000,
                },
              },
              // Hospital price rankings — cheapest and most expensive
              ...(seoRankings ? [
                {
                  "@type": "ItemList",
                  "name": `Cheapest Hospitals for ${selectedScenario.name}`,
                  "description": `Top 10 lowest-cost hospitals for ${selectedScenario.name} based on chargemaster data`,
                  "numberOfItems": seoRankings.cheapest.length,
                  "itemListElement": seoRankings.cheapest.map((h, i) => ({
                    "@type": "ListItem",
                    "position": i + 1,
                    "item": {
                      "@type": "Hospital",
                      "name": h.name,
                      "address": { "@type": "PostalAddress", "addressLocality": h.city, "addressRegion": h.state, "addressCountry": "US" },
                      "url": `${brand.primaryDomain}/hospital/${h.id}`,
                      "priceRange": `$${Math.round(h.price).toLocaleString()}`,
                    },
                  })),
                },
                {
                  "@type": "ItemList",
                  "name": `Most Expensive Hospitals for ${selectedScenario.name}`,
                  "description": `Top 10 highest-cost hospitals for ${selectedScenario.name} based on chargemaster data`,
                  "numberOfItems": seoRankings.mostExpensive.length,
                  "itemListElement": seoRankings.mostExpensive.map((h, i) => ({
                    "@type": "ListItem",
                    "position": i + 1,
                    "item": {
                      "@type": "Hospital",
                      "name": h.name,
                      "address": { "@type": "PostalAddress", "addressLocality": h.city, "addressRegion": h.state, "addressCountry": "US" },
                      "url": `${brand.primaryDomain}/hospital/${h.id}`,
                      "priceRange": `$${Math.round(h.price).toLocaleString()}`,
                    },
                  })),
                },
              ] : []),
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": `${brand.primaryDomain}` },
                  { "@type": "ListItem", "position": 2, "name": "Medical Scenarios", "item": `${brand.primaryDomain}/scenarios` },
                  { "@type": "ListItem", "position": 3, "name": selectedScenario.name, "item": `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}` },
                  ...(activeSeverityFilter !== null ? [{ "@type": "ListItem" as const, "position": 4, "name": `Severity ${activeSeverityFilter} — ${COMP_LABELS[activeSeverityFilter]}`, "item": `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}/severity/${activeSeverityFilter}` }] : []),
                ],
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": `How much does ${selectedScenario.name} cost?`,
                    "acceptedAnswer": { "@type": "Answer", "text": `The estimated national average cost for ${selectedScenario.name} is approximately $${Math.round(nationalTotal > 0 ? nationalTotal : selectedScenario.estimatedCost).toLocaleString()}, based on chargemaster data for ${selectedScenario.totalProcedures} medical billing codes across ${seoRankings?.total || 'thousands of'} US hospitals. ${seoRankings ? `The lowest-listed hospital is approximately $${Math.round(seoRankings.cheapest[0]?.price || 0).toLocaleString()} (${seoRankings.cheapest[0]?.name}, ${seoRankings.cheapest[0]?.state}) while the highest-listed is approximately $${Math.round(seoRankings.mostExpensive[0]?.price || 0).toLocaleString()} (${seoRankings.mostExpensive[0]?.name}, ${seoRankings.mostExpensive[0]?.state}).` : 'Actual costs vary significantly by hospital, location, and insurance coverage.'} These are estimated gross charges and actual out-of-pocket costs may differ based on insurance, negotiated rates, and individual circumstances.` },
                  },
                  ...(selectedScenario.aiConsumerMessage ? [{
                    "@type": "Question" as const,
                    "name": `What do I need to know about ${selectedScenario.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": fillCostTemplate(selectedScenario.aiConsumerMessage, scenarioContext(selectedScenario)).replace(/<[^>]*>/g, '').slice(0, 1500) },
                  }] : []),
                  ...(selectedScenario.aiClinicalSummary ? [{
                    "@type": "Question" as const,
                    "name": `What is the medical process for ${selectedScenario.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": fillCostTemplate(selectedScenario.aiClinicalSummary, scenarioContext(selectedScenario)).replace(/<[^>]*>/g, '').slice(0, 1500) },
                  }] : []),
                  ...(selectedScenario.aiCostAnalysis ? [{
                    "@type": "Question" as const,
                    "name": `Where does the money go for ${selectedScenario.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": fillCostTemplate(selectedScenario.aiCostAnalysis, scenarioContext(selectedScenario)).replace(/<[^>]*>/g, '').slice(0, 1500) },
                  }] : []),
                  ...(selectedScenario.aiPreparationGuide ? [{
                    "@type": "Question" as const,
                    "name": `How should I prepare for ${selectedScenario.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": fillCostTemplate(selectedScenario.aiPreparationGuide, scenarioContext(selectedScenario)).replace(/<[^>]*>/g, '').slice(0, 1500) },
                  }] : []),
                  {
                    "@type": "Question",
                    "name": `What procedures are typically included in ${selectedScenario.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": `${selectedScenario.name} typically includes approximately ${selectedScenario.totalProcedures} billing codes across ${selectedScenario.phases?.length || 0} phases: ${(selectedScenario.phases || []).map((p: any) => typeof p === 'string' ? p : p?.name || '').filter(Boolean).join(', ') || 'various phases'}. Common procedure codes include ${selectedScenario.procedures?.slice(0, 5).map(p => `${p.codeType}:${p.code}`).join(', ')}. Actual procedures may vary based on clinical circumstances and physician judgment.` },
                  },
                  {
                    "@type": "Question",
                    "name": `How can I save money on ${selectedScenario.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": `Compare estimated prices across hospitals — the same procedure can vary ${seoRankings ? `from approximately $${Math.round(seoRankings.cheapest[0]?.price || 0).toLocaleString()} to $${Math.round(seoRankings.mostExpensive[0]?.price || 0).toLocaleString()}` : 'by 2-10x'} based on published chargemaster rates. Ask for the hospital's cash-pay or self-pay discount (often 40-60% off listed prices). Request an itemized estimate before your procedure and compare facilities. Outpatient surgery centers are often less expensive than inpatient hospitals for the same procedure.` },
                  },
                  // FAQ: 5 cheapest hospitals (estimated)
                  ...(seoRankings && seoRankings.cheapest.length >= 3 ? [{
                    "@type": "Question" as const,
                    "name": `Which hospitals have the lowest estimated prices for ${selectedScenario.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": `Based on published chargemaster data, the 5 hospitals with the lowest estimated prices for ${selectedScenario.name} are: ${seoRankings.cheapest.slice(0, 5).map((h, i) => `${i + 1}. ${h.name} (${h.city}, ${h.state}) — approximately $${Math.round(h.price).toLocaleString()}`).join('; ')}. These are estimated gross charges from hospital price transparency files. Actual out-of-pocket costs depend on insurance coverage, negotiated rates, and individual clinical needs. Contact hospitals directly for a personalized estimate.` },
                  }] : []),
                  // FAQ: 5 most expensive hospitals (estimated)
                  ...(seoRankings && seoRankings.mostExpensive.length >= 3 ? [{
                    "@type": "Question" as const,
                    "name": `Which hospitals have the highest estimated prices for ${selectedScenario.name}?`,
                    "acceptedAnswer": { "@type": "Answer", "text": `Based on published chargemaster data, the 5 hospitals with the highest estimated prices for ${selectedScenario.name} are: ${seoRankings.mostExpensive.slice(0, 5).map((h, i) => `${i + 1}. ${h.name} (${h.city}, ${h.state}) — approximately $${Math.round(h.price).toLocaleString()}`).join('; ')}. These represent estimated chargemaster rates, which are the list prices before insurance adjustments or discounts. Insured patients typically pay significantly less. Always request a good-faith estimate and ask about cash-pay discounts before receiving care.` },
                  }] : []),
                  // FAQ: Cheapest US states (estimated, from seoRankings hospital data)
                  ...(seoRankings && seoRankings.cheapest.length >= 3 ? (() => {
                    const stateMap = new Map<string, { prices: number[]; hospitals: string[] }>();
                    for (const h of seoRankings.cheapest) {
                      if (!stateMap.has(h.state)) stateMap.set(h.state, { prices: [], hospitals: [] });
                      stateMap.get(h.state)!.prices.push(h.price);
                      stateMap.get(h.state)!.hospitals.push(h.name);
                    }
                    const cheapStates = [...stateMap.entries()]
                      .map(([st, d]) => ({ state: st, avg: d.prices.reduce((a, b) => a + b, 0) / d.prices.length, cheapest: Math.min(...d.prices), hospital: d.hospitals[0] }))
                      .sort((a, b) => a.avg - b.avg)
                      .slice(0, 5);
                    return [{
                      "@type": "Question" as const,
                      "name": `Can I save money by traveling to another state for ${selectedScenario.name}?`,
                      "acceptedAnswer": { "@type": "Answer", "text": `Yes — medical tourism doesn't have to mean leaving the country. Traveling across state or even county lines for ${selectedScenario.name} can save thousands of dollars. Based on estimated chargemaster prices, the most affordable states include ${cheapStates.map(s => `${s.state} (from approximately $${Math.round(s.cheapest).toLocaleString()} at ${s.hospital})`).join(', ')}. Many patients near state borders find significantly lower prices just a short drive away. These estimates are derived from hospital price transparency data and actual out-of-pocket costs may vary based on insurance and individual circumstances.` },
                    }];
                  })() : []),
                  // FAQ: Mexico & Canada pricing (estimated, from international data)
                  ...(() => {
                    const scenarioId = selectedScenario.id || selectedScenario.uuid;
                    const costs = (intlScenarioCosts || []).filter((c: InternationalScenarioCost) => c.scenario_id === scenarioId && ['MX', 'CA'].includes(c.country_code));
                    const mx = costs.find((c: InternationalScenarioCost) => c.country_code === 'MX');
                    const ca = costs.find((c: InternationalScenarioCost) => c.country_code === 'CA');
                    if (!mx && !ca) return [];
                    const usTotal = Math.round(nationalTotal > 0 ? nationalTotal : selectedScenario.estimatedCost);
                    const parts: string[] = [];
                    if (mx) {
                      const mxTotal = Math.round(Number(mx.intl_total_cost));
                      const savings = usTotal > 0 ? Math.round((1 - mxTotal / usTotal) * 100) : 0;
                      parts.push(`In Mexico, the estimated cost is approximately $${mxTotal.toLocaleString()} USD${savings > 0 ? `, roughly ${savings}% less than the estimated U.S. average` : ''}`);
                    }
                    if (ca) {
                      const caTotal = Math.round(Number(ca.intl_total_cost));
                      const savings = usTotal > 0 ? Math.round((1 - caTotal / usTotal) * 100) : 0;
                      parts.push(`In Canada, the estimated cost is approximately $${caTotal.toLocaleString()} USD${savings > 0 ? `, roughly ${savings}% less than the estimated U.S. average` : ''}`);
                    }
                    return [{
                      "@type": "Question" as const,
                      "name": `How much does ${selectedScenario.name} cost in Mexico or Canada compared to the US?`,
                      "acceptedAnswer": { "@type": "Answer", "text": `${parts.join('. ')}. The estimated U.S. national average is approximately $${usTotal.toLocaleString()}. These international estimates are based on official government fee schedules and may not include all facility fees, travel costs, or follow-up care. Medical tourism is increasingly common for elective procedures, but patients should verify facility accreditation and plan for post-procedure follow-up at home.` },
                    }];
                  })(),
                ],
              },
              // MedicalProcedure with per-hospital Offer pricing
              ...(seoRankings && seoRankings.cheapest.length > 0 ? [{
                "@type": "MedicalProcedure",
                "name": selectedScenario.name,
                "procedureType": "https://schema.org/TherapeuticProcedure",
                "url": `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`,
                "description": selectedScenario.aiConsumerMessage || selectedScenario.description,
                "howPerformed": selectedScenario.aiClinicalSummary || undefined,
                "offers": [...seoRankings.cheapest.slice(0, 5), ...seoRankings.mostExpensive.slice(0, 5)].map(h => ({
                  "@type": "Offer",
                  "priceCurrency": "USD",
                  "price": Math.round(h.price),
                  "offeredBy": {
                    "@type": "Hospital",
                    "name": h.name,
                    "address": { "@type": "PostalAddress", "addressLocality": h.city, "addressRegion": h.state, "addressCountry": "US" },
                    "url": `${brand.primaryDomain}/hospital/${h.id}`,
                  },
                })),
              }] : []),
              // Dataset schema for Google Dataset Search
              {
                "@type": "Dataset",
                "name": `${selectedScenario.name} — Hospital Price Comparison Data`,
                "description": `Hospital chargemaster pricing data for ${selectedScenario.name} across ${seoRankings?.total || 'thousands of'} US hospitals. Includes ${selectedScenario.totalProcedures} procedure codes (CPT/HCPCS/DRG).`,
                "url": `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`,
                "license": "https://creativecommons.org/licenses/by-nc/4.0/",
                "creator": { "@type": "Organization", "name": brand.name, "url": `${brand.primaryDomain}` },
                "distribution": {
                  "@type": "DataDownload",
                  "encodingFormat": "text/html",
                  "contentUrl": `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`,
                },
                "variableMeasured": [
                  { "@type": "PropertyValue", "name": "Hospital Chargemaster Price", "unitText": "USD" },
                  { "@type": "PropertyValue", "name": "National Average Price", "unitText": "USD" },
                  { "@type": "PropertyValue", "name": "Price Markup Percentage", "unitText": "%" },
                ],
                "spatialCoverage": "United States",
                "temporalCoverage": "2024/..",
              },
              // HowTo schema — prep guide as step-by-step (Google renders expandable steps)
              ...(selectedScenario.aiPreparationGuide ? (() => {
                const rawGuide = selectedScenario.aiPreparationGuide.replace(/<[^>]*>/g, '');
                const sections = rawGuide.split(/\*\*([^*]+)\*\*/).filter(Boolean);
                const steps: { "@type": string; name: string; text: string }[] = [];
                for (let i = 0; i < sections.length - 1; i += 2) {
                  const name = sections[i].replace(/^[-•\s]+/, '').replace(/:$/, '').trim();
                  const text = (sections[i + 1] || '').trim();
                  if (name && text) steps.push({ "@type": "HowToStep", "name": name, "text": text.slice(0, 500) });
                }
                if (steps.length < 2) return [];
                return [{
                  "@type": "HowTo",
                  "name": `How to prepare for ${selectedScenario.name}`,
                  "description": `Step-by-step preparation guide for ${selectedScenario.name}, including what to do before, during, and after your procedure.`,
                  "step": steps,
                  "totalTime": selectedScenario.duration ? `P${selectedScenario.duration.match(/\d+/)?.[0] || 1}D` : undefined,
                }];
              })() : []),
              // MedicalWebPage — signals authoritative health content to Google
              {
                "@type": "MedicalWebPage",
                "name": `${selectedScenario.name} — Cost Breakdown & Hospital Comparison`,
                "about": { "@type": "MedicalCondition", "name": selectedScenario.name },
                "url": `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`,
                "lastReviewed": new Date().toISOString().split('T')[0],
                "medicalAudience": { "@type": "MedicalAudience", "audienceType": "Patient" },
                "speakable": {
                  "@type": "SpeakableSpecification",
                  "cssSelector": [".text-3xl", ".text-muted-foreground"],
                },
              },
            ],
          })}</script>
          {/* JSON-LD: Connect to Care — featured NGOs */}
          {(() => {
            const careResources = matchResources(selectedScenario.tags || [], selectedScenario.name);
            const featured = careResources.filter(r => r.featured);
            if (featured.length === 0) return null;
            return (
              <script type="application/ld+json">{JSON.stringify(
                JSONLDGenerator.generateConnectToCareSchema({
                  scenarioName: selectedScenario.name,
                  scenarioUrl: `${brand.primaryDomain}/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`,
                  organizations: featured.map(r => ({
                    name: r.name,
                    description: r.description,
                    url: r.url,
                    phone: r.phone,
                    mission: r.mission,
                  })),
                })
              )}</script>
            );
          })()}
        </>) : (
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "WebApplication",
                "name": `Medical Cost Scenarios — ${brand.name}`,
                "description": `Browse ${allScenarios.length.toLocaleString()}+ medical cost scenarios with real hospital pricing data across 9,000+ US hospitals`,
                "url": `${brand.primaryDomain}/scenarios`,
                "applicationCategory": "HealthApplication",
                "operatingSystem": "Any",
                "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
                "creator": { "@type": "Organization", "name": brand.name },
              },
              {
                "@type": "ItemList",
                "name": "Medical Cost Scenarios",
                "description": "Real-world medical scenarios with pricing from thousands of hospitals",
                "numberOfItems": allScenarios.length,
                "itemListElement": allScenarios.slice(0, 20).map((s, i) => ({
                  "@type": "ListItem",
                  "position": i + 1,
                  "name": s.name,
                  "url": `${brand.primaryDomain}/scenarios/${s.uuid}/${s.slug}`,
                })),
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": `${brand.primaryDomain}` },
                  { "@type": "ListItem", "position": 2, "name": "Medical Scenarios", "item": `${brand.primaryDomain}/scenarios` },
                ],
              },
            ],
          })}</script>
        )}

        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover" />
        <meta name="theme-color" content={brand.colors.background} />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      </Helmet>

      <div className={`min-h-screen bg-background overflow-x-clip ${isNativeApp() ? 'pb-32' : 'pb-0'}`}>
        {/* pb-32 cleared the fixed tab bars when nothing followed the sections. On the
            web SiteFooter follows (with its own clearance), so the padding only opened a
            dead band between the care-resources CTA and the footer (Trent, 2026-09-05). */}
        {/* Identity, not layout: the flag is AHP's, the video wall is Luigi's;
            AntiGreed sits on its own body gradient from BrandTheme. */}
        {brand.key === 'ahp' ? <FlagBackground dark={ahpDark} /> : brand.key === 'luigis' ? <BackgroundMedia /> : null}
        {/* Secondary "section rail" footer. Native app: rides above the tab bar,
            sections open as modals. Web: rides above the AppFooter — on mobile
            web sections are still modals; on desktop they're inline so the rail
            scroll-spies + scrolls to them (no modalKeys). */}
        {isAhp && selectedScenario && (() => {
          const native = isNativeApp();
          const railBottom = native
            ? undefined // rail's default (above NativeTabBar)
            : isMobileViewport ? "80px" : "64px"; // AppFooter: h-20 mobile / h-16 desktop
          return (
            <ScenarioSectionRail
              dark={ahpDark}
              scenarioKey={selectedScenario.uuid}
              // Mobile: all sections are modals. Desktop: sections scroll inline,
              // except the Dignity guide which is modal-only (no inline row).
              modalKeys={isMobileViewport ? SECTION_MODAL_KEYS : new Set(['dignity-guide'])}
              onOpenSection={openSectionModal}
              activeModal={openSection}
              bottomOffset={railBottom}
            />
          );
        })()}

      {/* Translucent page sheet over the background art (Trent, 2026-09-16):
          82% white in light mode so the body reads without fighting the
          backdrop; the navy-ink equivalent at the same alpha in dark mode. */}
      <div className="relative z-10 w-full px-6 pt-8 pb-0" style={{ background: ahpDark ? 'rgba(var(--civic-navy-ink-rgb),0.82)' : '#ffffffd1' }}>
        {/* Header with Scenario Selector */}
        {/* Desktop only: title + scenario search stay pinned under the global
            chrome while the browse grid / scenario details scroll beneath
            (Trent, 2026-09-03). Mobile keeps the plain in-flow header — the
            pinned block ate the small viewport and broke the layout there.
            --ahp-chrome-h is published by AhpGlobalHeader; the measured
            height feeds the category-pill row's own sticky offset. */}
        <div
          ref={stickyHeadRef}
          className={isMobileViewport
            ? "w-full mb-8 relative z-50"
            : "w-[calc(100%+3rem)] mb-8 sticky z-50 -mx-6 px-6 pt-2 pb-3 backdrop-blur-md"}
          style={isMobileViewport ? undefined : {
            top: 'var(--ahp-chrome-h, 0px)',
            backgroundColor: ahpDark ? 'rgba(var(--civic-navy-ink-rgb),0.94)' : 'rgba(255,255,255,0.94)',
          }}
        >
          <div className="flex flex-col min-[1200px]:flex-row items-start gap-4 min-[1200px]:gap-6 mb-4">
            <div className="flex-shrink-0 w-full min-[1200px]:w-auto">
              <div className="flex items-center gap-3 mb-3">
                <h1
                  onClick={tunerTap}
                  className={isAhp ? "text-4xl font-bold" : "text-4xl font-bold text-matrix-green font-mono"}
                  style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)', fontWeight: 400 } : undefined}
                >
                  {t('scenarios:pageTitle')}
                </h1>
              </div>
              <p className={ahpDark ? "text-gray-300" : "text-muted-foreground"} style={isAhp ? { fontFamily: "'Instrument Sans', sans-serif" } : undefined}>
                {t('scenarios:pageSubtitle')}
              </p>
            </div>

            {/* Scenario Selector with Details. On mobile the card chrome +
                padding are dropped so the search input spans the full content
                width (matching the cards below); desktop keeps the card. */}
            <div
              className={isAhp
                ? `w-full min-[1200px]:flex-1 relative z-50 ${isMobileViewport ? 'p-0' : 'rounded-lg p-4'}`
                : "w-full min-[1200px]:flex-1 border border-matrix-green/20 rounded-lg bg-black/40 backdrop-blur-sm p-4 relative z-50"}
              style={isAhp
                ? (isMobileViewport ? undefined : { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' })
                : undefined}
            >
              <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-3 sm:gap-4">
                <div className="flex-1 relative z-[100] min-w-0">
                  <label className={`text-xs mb-2 block ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>{t('scenarios:browse.selectScenario', { defaultValue: 'Select Scenario:' })}</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                    <Input
                      ref={scenarioSearchInputRef}
                      type="text"
                      placeholder={t('scenarios:filters.searchPlaceholder')}
                      value={scenarioSearchQuery}
                      onChange={(e) => {
                        setScenarioSearchQuery(e.target.value);
                        setShowScenarioDropdown(true);
                      }}
                      onFocus={() => setShowScenarioDropdown(true)}
                      className={isAhp
                        ? `w-full text-sm pl-10 pr-10 h-12 ${ahpDark ? 'border-civic-gold/30 bg-civic-card-dark text-civic-warm' : 'border-gray-200 bg-gray-50/50'}`
                        : "w-full bg-black/60 border-matrix-green/20 font-mono text-sm pl-10 pr-10 h-12"
                      }
                    />
                    {scenarioSearchQuery && (
                      <button
                        onClick={() => {
                          setScenarioSearchQuery('');
                          setShowScenarioDropdown(true);
                          scenarioSearchInputRef.current?.focus();
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-matrix-green transition-colors"
                        aria-label={t('scenarios:browse.clearScenarioSearch')}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* Dropdown Results */}
                  {showScenarioDropdown && (scenarioSearchQuery === '' || filteredScenarios.length > 0) && (
                    <div
                      ref={scenarioDropdownRef}
                      className={isAhp
                        ? `absolute z-[9999] w-full mt-1 ${ahpDark ? 'bg-civic-card-dark border border-civic-gold/30' : 'bg-white border border-gray-200'} rounded-lg shadow-xl max-h-[500px] overflow-y-auto thin-scrollbar`
                        : "absolute z-[9999] w-full mt-1 bg-black/95 border border-matrix-green/20 rounded-lg shadow-lg max-h-[500px] overflow-y-auto thin-scrollbar"
                      }
                    >
                      {/* Create New Scenario Button */}
                      <button
                        onClick={() => {
                          setShowScenarioDropdown(false);
                          setShowCreateDialog(true);
                        }}
                        className={`w-full px-4 py-3 text-left transition-colors sticky top-0 z-10 ${
                          ahpDark
                            ? 'bg-civic-card-dark hover:bg-[var(--civic-navy-deep)] border-b-2 border-civic-gold/30'
                            : isAhp
                              ? 'bg-white hover:bg-gray-50 border-b-2 border-gray-200'
                              : 'bg-matrix-green/10 hover:bg-matrix-green/20 border-b-2 border-matrix-green/30'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded flex items-center justify-center ${
                            ahpDark ? 'bg-civic-gold/15' : isAhp ? 'bg-civic-red/10' : 'bg-matrix-green/20'
                          }`}>
                            <Plus className={`w-4 h-4 ${ahpDark ? 'text-civic-gold' : isAhp ? 'text-civic-red' : 'text-matrix-green'}`} />
                          </div>
                          <div>
                            <div className={`font-bold text-sm ${
                              ahpDark ? 'text-civic-gold' : isAhp ? 'text-civic-red font-medium' : 'font-mono text-matrix-green'
                            }`}>
                              {t('scenarios:browse.createNew')}
                            </div>
                            <div className={`text-[10px] ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
                              {t('scenarios:browse.createNewSubtitle')}
                            </div>
                          </div>
                        </div>
                      </button>

                      {/* Category Filter Pills */}
                      {scenarioSearchQuery !== '' && (
                        <div className={`px-3 py-2 sticky top-[52px] z-10 border-b ${ahpDark ? 'bg-civic-card-dark border-civic-gold/20' : isAhp ? 'bg-white border-gray-200' : 'bg-black/95 border-matrix-green/20'}`}>
                          <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
                            {SCENARIO_CATEGORIES.map(cat => {
                              const isActive = searchCategory === cat.key;
                              const catColor = cat.key === 'all' ? (ahpDark ? 'var(--civic-gold)' : isAhp ? 'var(--civic-ink)' : '#22c55e') : cat.color;
                              return (
                                <button
                                  key={cat.key}
                                  onClick={() => {
                                    setSearchCategory(cat.key);
                                    setExpandedSearchFamilies(new Set());
                                  }}
                                  className="flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all"
                                  style={{
                                    backgroundColor: isActive ? catColor : 'transparent',
                                    color: isActive ? '#fff' : catColor,
                                    border: `1.5px solid ${isActive ? catColor : `${catColor}40`}`,
                                  }}
                                >
                                  {cat.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Recent Searches Section */}
                      {scenarioSearchQuery === '' && recentSearches.length > 0 && (
                        <div className={`border-b-2 ${ahpDark ? 'border-civic-gold/20' : isAhp ? 'border-gray-200' : 'border-matrix-green/20'}`}>
                          <div className={`px-4 py-2 flex items-center justify-between ${ahpDark ? 'bg-civic-navy-ink' : isAhp ? 'bg-gray-50' : 'bg-black/30'}`}>
                            <div className={`flex items-center gap-2 text-[10px] font-bold ${ahpDark ? 'text-civic-gold/60' : isAhp ? 'text-gray-500 font-medium' : 'font-mono text-matrix-green/60'}`}>
                              <Search className="w-3 h-3" />
                              {t('scenarios:browse.recentSearches')}
                            </div>
                            <button
                              onClick={handleClearRecentSearches}
                              className={`text-[9px] transition-colors ${ahpDark ? 'text-gray-500 hover:text-red-400' : 'text-muted-foreground hover:text-destructive'}`}
                            >
                              {t('scenarios:browse.clear')}
                            </button>
                          </div>
                          <div className="px-4 py-2 space-y-1">
                            {recentSearches.slice(0, 5).map((searchTerm, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleRecentSearchClick(searchTerm)}
                                className={`w-full text-left px-3 py-1.5 rounded transition-colors text-xs ${
                                  ahpDark ? 'text-gray-400 hover:text-civic-warm hover:bg-white/5' : isAhp ? 'text-gray-500 hover:text-gray-900 hover:bg-gray-100' : 'text-muted-foreground hover:text-foreground hover:bg-matrix-green/10'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <Search className="w-3 h-3 opacity-50" />
                                  {searchTerm}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Bookmarked Scenarios Section */}
                      {scenarioSearchQuery === '' && bookmarkedScenarioIds.length > 0 && (
                        <div className={`border-b-2 ${ahpDark ? 'border-civic-gold/20' : isAhp ? 'border-gray-200' : 'border-matrix-green/20'}`}>
                          <div className={`px-4 py-2 ${ahpDark ? 'bg-civic-navy-ink' : isAhp ? 'bg-gray-50' : 'bg-black/40'}`}>
                            <div className={`flex items-center gap-2 text-[10px] font-bold ${ahpDark ? 'text-civic-gold/60' : isAhp ? 'text-gray-500 font-medium' : 'font-mono text-matrix-green/60'}`}>
                              <Bookmark className="w-3 h-3" />
                              {t('scenarios:browse.bookmarkedScenarios')}
                            </div>
                          </div>
                          {allScenarios.filter(s => bookmarkedScenarioIds.includes(s.id)).map((scenario) => {
                            const Icon = scenario.icon || Activity;

                            // Use real cost from scenario_costs_cache (loaded into estimatedCost)
                            const displayCost = scenario.estimatedCost;
                            const hasRealData = displayCost > 0;

                            return (
                              <div
                                role="button"
                                tabIndex={0}
                                key={`bookmark-${scenario.uuid}`}
                                onClick={() => handleScenarioSelect(scenario)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleScenarioSelect(scenario); } }}
                                className={`w-full px-4 py-2.5 text-left transition-colors cursor-pointer ${
                                  ahpDark
                                    ? `hover:bg-white/5 border-b border-white/5 ${selectedScenario?.uuid === scenario.uuid ? 'bg-white/10' : ''}`
                                    : isAhp
                                      ? `hover:bg-gray-100 border-b border-gray-100 ${selectedScenario?.uuid === scenario.uuid ? 'bg-gray-100' : ''}`
                                      : `hover:bg-matrix-green/10 border-b border-matrix-green/10 ${selectedScenario?.uuid === scenario.uuid ? 'bg-matrix-green/20' : ''}`
                                }`}
                              >
                                <div className="flex items-start gap-2.5 w-full">
                                  <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: scenario.color }} />
                                  <div className="flex-1 min-w-0">
                                    <div className={`font-bold text-xs truncate ${ahpDark ? 'text-civic-warm' : isAhp ? 'text-civic-ink' : 'font-mono text-matrix-green'}`}>
                                      {scenario.name}
                                    </div>
                                    <div className={`text-[9px] truncate ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
                                      {typeof scenario.description === 'string' ? scenario.description : JSON.stringify(scenario.description)}
                                    </div>
                                  </div>
                                  <button
                                    onClick={(e) => toggleBookmark(scenario.id, e)}
                                    className={`flex-shrink-0 p-1 rounded transition-colors ${ahpDark ? 'hover:bg-white/10' : 'hover:bg-matrix-green/20'}`}
                                  >
                                    <Bookmark className={`w-3.5 h-3.5 ${ahpDark ? 'text-civic-gold fill-civic-gold' : 'text-matrix-green fill-matrix-green'}`} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* All Scenarios Section — show top 50 popular, type to search all */}
                      {scenarioSearchQuery === '' && (
                        <div className={`px-4 py-2 ${ahpDark ? 'bg-civic-navy-ink' : isAhp ? 'bg-gray-50' : 'bg-black/20'}`}>
                          <div className={`text-[10px] font-bold ${ahpDark ? 'text-civic-gold/60' : isAhp ? 'text-gray-500 font-medium' : 'font-mono text-matrix-green/60'}`}>
                            {t('scenarios:browse.popularScenarios')}
                          </div>
                          <div className={`text-[9px] mt-0.5 ${ahpDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {t('scenarios:browse.typeToSearchAll', { value: allScenarios.length.toLocaleString() })}
                          </div>
                        </div>
                      )}

                      {/* Search results header */}
                      {scenarioSearchQuery !== '' && groupedSearchResults.length > 0 && (
                        <div className={`px-4 py-2 ${ahpDark ? 'bg-civic-navy-ink' : isAhp ? 'bg-gray-50' : 'bg-black/20'}`}>
                          <div className={`text-[10px] font-bold ${ahpDark ? 'text-civic-gold/60' : isAhp ? 'text-gray-500 font-medium' : 'font-mono text-matrix-green/60'}`}>
                            {t('scenarios:browse.resultCount', { count: groupedSearchResults.length })}
                            {searchCategory !== 'all' && ` ${t('scenarios:browse.inCategory', { category: SCENARIO_CATEGORIES.find(c => c.key === searchCategory)?.label.toUpperCase() })}`}
                          </div>
                        </div>
                      )}

                      {/* Grouped search results with family expansion */}
                      {scenarioSearchQuery !== '' && groupedSearchResults.map((group) => {
                        const scenario = group.baseScenario;
                        const Icon = scenario.icon || Activity;
                        const displayCost = scenario.estimatedCost;
                        const hasRealData = displayCost > 0;
                        const isExpanded = expandedSearchFamilies.has(scenario.uuid);
                        const hasRange = group.type === 'family' && group.costMax > group.costMin && group.costMin > 0;
                        const SEV_COLORS = ['var(--civic-money)', '#3b7a57', 'var(--civic-gold)', '#e67e22', 'var(--civic-red)'];
                        const SEV_LABELS = [
                          t('common:costQuartile.base'),
                          t('common:costQuartile.lowCost'),
                          t('common:costQuartile.midCost'),
                          t('common:costQuartile.highCost'),
                          t('common:costQuartile.highest'),
                        ];

                        return (
                          <div key={scenario.uuid}>
                            {/* Main row */}
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => handleScenarioSelect(scenario)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleScenarioSelect(scenario); } }}
                              className={`w-full px-4 py-3 text-left transition-colors cursor-pointer ${
                                ahpDark
                                  ? `hover:bg-white/5 border-b border-white/5 ${selectedScenario?.uuid === scenario.uuid ? 'bg-white/10' : ''}`
                                  : isAhp
                                    ? `hover:bg-gray-50 border-b border-gray-100 ${selectedScenario?.uuid === scenario.uuid ? 'bg-civic-red/5' : ''}`
                                    : `hover:bg-matrix-green/10 border-b border-matrix-green/10 ${selectedScenario?.uuid === scenario.uuid ? 'bg-matrix-green/20' : ''}`
                              }`}
                            >
                              <div className="flex items-start gap-3 w-full">
                                <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy-deep)') : scenario.color }} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                    <div className={`font-bold text-sm break-words max-w-full`} style={{ color: isAhp ? (ahpDark ? 'var(--civic-warm)' : 'var(--civic-navy-deep)') : scenario.color }}>
                                      {scenario.name}
                                    </div>
                                    {/* Severity: 4 bars for levels 1-4, or "Base" for level 0 */}
                                    {scenario.severityLevel != null && (() => {
                                      const si = Math.min(Math.max(Math.round(scenario.severityLevel), 0), 4);
                                      if (si === 0) {
                                        return (
                                          <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded" style={{ color: 'var(--civic-money)', backgroundColor: '#2b5e4910' }}>
                                            {t('common:costQuartile.noComplications')}
                                          </span>
                                        );
                                      }
                                      return (
                                        <div className="flex items-center gap-1" title={t('scenarios:browse.severityTitle', { label: SEV_LABELS[si], level: si })}>
                                          <div className="flex gap-[2px]">
                                            {[1,2,3,4].map(lvl => (
                                              <div key={lvl} style={{
                                                width: 6, height: 10, borderRadius: 2,
                                                backgroundColor: lvl <= si ? SEV_COLORS[si] : 'rgba(var(--civic-ink-rgb),0.08)',
                                              }} />
                                            ))}
                                          </div>
                                          <span className="text-[8px] font-semibold" style={{ color: SEV_COLORS[si] }}>
                                            {SEV_LABELS[si]}
                                          </span>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <div className={`text-[10px] line-clamp-1 mb-1.5 ${ahpDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {typeof scenario.description === 'string' ? scenario.description : JSON.stringify(scenario.description)}
                                  </div>
                                  {scenario.tags && scenario.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-1.5">
                                      {scenario.tags.slice(0, 3).map((tag, idx) => (
                                        <span key={idx} className="px-1.5 py-0.5 text-[9px] rounded border" style={{
                                          backgroundColor: isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.08)' : '#00214706') : undefined,
                                          color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy-deep)') : undefined,
                                          borderColor: isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.25)' : '#00214715') : undefined,
                                        }}>
                                          {tag}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  {/* Variant expand button inline */}
                                  {group.type === 'family' && group.totalFamilySize > 1 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedSearchFamilies(prev => {
                                          const next = new Set(prev);
                                          if (next.has(scenario.uuid)) {
                                            next.delete(scenario.uuid);
                                          } else {
                                            next.add(scenario.uuid);
                                            // Reset filter & page when expanding
                                            setExpandedSeverityFilter(p => ({ ...p, [scenario.uuid]: null }));
                                            setVariantPage(p => ({ ...p, [scenario.uuid]: 0 }));
                                          }
                                          return next;
                                        });
                                      }}
                                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold transition-colors"
                                      style={{
                                        backgroundColor: isAhp ? '#B2223410' : `${scenario.color}15`,
                                        color: isAhp ? 'var(--civic-red)' : scenario.color,
                                        border: `1px solid ${isAhp ? '#B2223425' : `${scenario.color}30`}`,
                                      }}
                                    >
                                      {t('scenarios:browse.variantCount', { count: group.totalFamilySize - 1 })}
                                      <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                    </button>
                                  )}
                                </div>
                                {/* Cost display: base cost large + range */}
                                <div className="flex flex-col items-end flex-shrink-0 gap-0.5">
                                  <div className="font-mono text-[15px] font-bold" style={{ color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy-deep)') : 'var(--civic-red)' }}>
                                    ${displayCost >= 1000 ? `${(displayCost / 1000).toFixed(0)}k` : Math.round(displayCost).toLocaleString()}
                                  </div>
                                  {hasRange && (
                                    <div className="text-[9px] font-mono" style={{ color: isAhp ? (ahpDark ? '#e67e22' : 'var(--civic-red)') : 'var(--civic-red)' }}>
                                      <span style={{ color: isAhp ? (ahpDark ? '#9ca3af' : '#6b7280') : '#6b7280' }}>{t('scenarios:browse.range')} </span>
                                      ${(group.costMin / 1000).toFixed(0)}k–${group.costMax >= 1000000 ? `${(group.costMax / 1000000).toFixed(1)}M` : `${(group.costMax / 1000).toFixed(0)}k`}
                                    </div>
                                  )}
                                  <div className={`text-[9px] ${hasRealData ? 'text-blue-500' : 'text-gray-400'}`}>
                                    {hasRealData ? t('scenarios:browse.nationalAvg') : t('scenarios:browse.estimated')}
                                  </div>
                                </div>
                                <div className="flex flex-col gap-1 flex-shrink-0 ml-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const members = familyStorage.getMembers();
                                      setAddToPlanMember(members[0]?.id || '');
                                      setShowAddToPlan(showAddToPlan === scenario.id ? null : scenario.id);
                                    }}
                                    className="p-1.5 rounded transition-colors hover:bg-gray-100"
                                    title={t('scenarios:detail.addToFamilyPlan')}
                                  >
                                    <Calendar className="w-3.5 h-3.5 text-gray-400 hover:text-civic-navy-deep" />
                                  </button>
                                  <button
                                    onClick={(e) => toggleBookmark(scenario.id, e)}
                                    className="p-1.5 rounded transition-colors hover:bg-gray-100"
                                    title={bookmarkedScenarioIds.includes(scenario.id) ? t('scenarios:browse.removeBookmark') : t('scenarios:browse.bookmark')}
                                  >
                                    <Bookmark
                                      className={`w-3.5 h-3.5 ${
                                        bookmarkedScenarioIds.includes(scenario.id)
                                          ? 'text-civic-red fill-civic-red'
                                          : 'text-gray-400'
                                      }`}
                                    />
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Expanded variant rows with severity tabs + pagination */}
                            {group.type === 'family' && isExpanded && (() => {
                              const allMembers = group.allFamilyMembers;
                              const sevFilter = expandedSeverityFilter[scenario.uuid] ?? null;
                              const filtered = sevFilter === null ? allMembers : allMembers.filter(s => {
                                const sl = Math.min(Math.max(Math.round(s.severityLevel ?? 0), 0), 4);
                                return sl === sevFilter;
                              });
                              const PAGE_SIZE = 6;
                              const page = variantPage[scenario.uuid] ?? 0;
                              const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
                              const pageVariants = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

                              // Count per severity level for tabs
                              const countByLevel: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
                              allMembers.forEach(s => {
                                const sl = Math.min(Math.max(Math.round(s.severityLevel ?? 0), 0), 4);
                                if (sl >= 1 && sl <= 4) countByLevel[sl]++;
                              });

                              return (
                                <div className={`${ahpDark ? 'bg-civic-navy-ink' : isAhp ? 'bg-[#f8f7f5]' : 'bg-black/30'} border-b ${isAhp ? 'border-gray-200' : ''}`}>
                                  {/* Severity filter tabs */}
                                  <div className={`flex items-center gap-1 px-4 py-2 border-b ${isAhp ? 'border-gray-200' : (ahpDark ? 'border-white/5' : '')}`}>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setExpandedSeverityFilter(p => ({ ...p, [scenario.uuid]: null }));
                                        setVariantPage(p => ({ ...p, [scenario.uuid]: 0 }));
                                      }}
                                      className="px-2 py-1 rounded text-[9px] font-semibold transition-colors"
                                      style={{
                                        backgroundColor: sevFilter === null ? 'var(--civic-navy-deep)' : 'transparent',
                                        color: sevFilter === null ? '#fff' : '#6b7280',
                                        border: sevFilter === null ? 'none' : '1px solid #e5e7eb',
                                      }}
                                    >
                                      {t('scenarios:browse.allWithCount', { count: allMembers.length })}
                                    </button>
                                    {[1, 2, 3, 4].map(lvl => (
                                      <button
                                        key={lvl}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedSeverityFilter(p => ({ ...p, [scenario.uuid]: lvl }));
                                          setVariantPage(p => ({ ...p, [scenario.uuid]: 0 }));
                                        }}
                                        className="px-2 py-1 rounded text-[9px] font-semibold transition-colors"
                                        style={{
                                          backgroundColor: sevFilter === lvl ? SEV_COLORS[lvl] : 'transparent',
                                          color: sevFilter === lvl ? '#fff' : (countByLevel[lvl] > 0 ? SEV_COLORS[lvl] : '#d1d5db'),
                                          border: sevFilter === lvl ? 'none' : `1px solid ${countByLevel[lvl] > 0 ? `${SEV_COLORS[lvl]}30` : '#e5e7eb'}`,
                                          opacity: countByLevel[lvl] > 0 ? 1 : 0.5,
                                        }}
                                        disabled={countByLevel[lvl] === 0}
                                      >
                                        {SEV_LABELS[lvl]} ({countByLevel[lvl]})
                                      </button>
                                    ))}
                                  </div>

                                  {/* Variant rows */}
                                  {pageVariants.map(variant => {
                                    const vCost = (typeof variant.estimatedCost === 'number' && isFinite(variant.estimatedCost)) ? variant.estimatedCost : 0;
                                    const baseCost = (typeof scenario.estimatedCost === 'number' && isFinite(scenario.estimatedCost)) ? scenario.estimatedCost : 0;
                                    const costDelta = vCost - baseCost;
                                    const si = Math.min(Math.max(Math.round(variant.severityLevel ?? 0), 0), 4);
                                    let variantName = variant.name;
                                    const baseNamePrefix = scenario.name.replace(/\s*\(.*\)$/, '');
                                    if (variantName.startsWith(baseNamePrefix) && variantName.length > baseNamePrefix.length) {
                                      variantName = variantName.slice(baseNamePrefix.length).replace(/^\s*[—–\-:]\s*/, '').replace(/^\s*with\s+/i, '');
                                    }
                                    if (!variantName) variantName = variant.familyVariantLabel || variant.name;

                                    return (
                                      <div
                                        key={variant.uuid}
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => handleScenarioSelect(variant)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleScenarioSelect(variant); } }}
                                        className={`w-full pl-10 pr-4 py-2 text-left transition-colors cursor-pointer border-b ${
                                          isAhp ? 'hover:bg-white border-gray-100' : (ahpDark ? 'hover:bg-white/5 border-white/5' : 'hover:bg-matrix-green/10 border-matrix-green/10')
                                        }`}
                                      >
                                        <div className="flex items-center gap-2.5 w-full">
                                          {/* 4-bar severity indicator */}
                                          {si === 0 ? (
                                            <span className="text-[8px] font-semibold flex-shrink-0" style={{ color: 'var(--civic-money)' }}>{t('common:costQuartile.base')}</span>
                                          ) : (
                                            <div className="flex gap-[2px] flex-shrink-0">
                                              {[1,2,3,4].map(lvl => (
                                                <div key={lvl} style={{
                                                  width: 5, height: 8, borderRadius: 1.5,
                                                  backgroundColor: lvl <= si ? SEV_COLORS[si] : 'rgba(var(--civic-ink-rgb),0.06)',
                                                }} />
                                              ))}
                                            </div>
                                          )}
                                          <span className={`text-xs font-medium truncate flex-1`} style={{ color: isAhp ? (ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)') : undefined }}>
                                            {variantName}
                                          </span>
                                          <span className="font-mono text-[11px] font-bold flex-shrink-0" style={{
                                            color: costDelta > 0 ? 'var(--civic-red)' : costDelta < 0 ? (ahpDark ? '#4ade80' : 'var(--civic-money)') : (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy-deep)'),
                                          }}>
                                            {costDelta > 0 ? '+' : ''}{costDelta !== 0 ? `$${Math.abs(costDelta) >= 1000000 ? `${(Math.abs(costDelta) / 1000000).toFixed(1)}M` : `${(Math.abs(costDelta) / 1000).toFixed(1)}k`}` : `$${(vCost / 1000).toFixed(0)}k`}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* Pagination */}
                                  {totalPages > 1 && (
                                    <div className="flex items-center justify-between px-4 py-1.5">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setVariantPage(p => ({ ...p, [scenario.uuid]: Math.max(0, page - 1) })); }}
                                        disabled={page === 0}
                                        className="text-[10px] font-medium px-2 py-0.5 rounded transition-colors disabled:opacity-30"
                                        style={{ color: 'var(--civic-navy-deep)' }}
                                      >
                                        <ChevronLeft className="w-3 h-3 inline" /> {t('scenarios:browse.prev')}
                                      </button>
                                      <span className="text-[9px] text-gray-400">
                                        {t('scenarios:browse.pageOfVariants', { page: page + 1, totalPages, count: filtered.length })}
                                      </span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setVariantPage(p => ({ ...p, [scenario.uuid]: Math.min(totalPages - 1, page + 1) })); }}
                                        disabled={page >= totalPages - 1}
                                        className="text-[10px] font-medium px-2 py-0.5 rounded transition-colors disabled:opacity-30"
                                        style={{ color: 'var(--civic-navy-deep)' }}
                                      >
                                        {t('common:pagination.next')} <ChevronRight className="w-3 h-3 inline" />
                                      </button>
                                    </div>
                                  )}

                                  {/* View all link */}
                                  {allMembers.length > PAGE_SIZE && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleScenarioSelect(scenario);
                                        setTimeout(() => setShowFamilyModal(true), 300);
                                      }}
                                      className={`w-full pl-10 pr-4 py-2 text-left text-[11px] font-semibold transition-colors border-t ${
                                        isAhp ? 'text-civic-red hover:bg-white border-gray-200' : (ahpDark ? 'text-civic-gold hover:bg-white/5 border-white/5' : 'text-matrix-green hover:bg-matrix-green/10 border-matrix-green/10')
                                      }`}
                                    >
                                      {t('scenarios:browse.viewAllVariants', { count: allMembers.length + 1 })}
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}

                      {/* Popular scenarios (no search query) */}
                      {scenarioSearchQuery === '' && popularScenarios.map((scenario) => {
                        const Icon = scenario.icon || Activity;

                        // Use real cost from scenario_costs_cache (loaded into estimatedCost)
                        const displayCost = scenario.estimatedCost;
                        const hasRealData = displayCost > 0;

                        // AHP dark mode accent color
                        const accentColor = ahpDark ? 'var(--civic-gold)' : isAhp ? 'var(--civic-red)' : undefined;

                        return (
                          <div
                            role="button"
                            tabIndex={0}
                            key={scenario.uuid}
                            onClick={() => handleScenarioSelect(scenario)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleScenarioSelect(scenario); } }}
                            className={`w-full px-4 py-3 text-left transition-colors last:border-b-0 cursor-pointer ${
                              ahpDark
                                ? `hover:bg-white/5 border-b border-white/5 ${selectedScenario?.uuid === scenario.uuid ? 'bg-white/10' : ''}`
                                : isAhp
                                  ? `hover:bg-gray-100 border-b border-gray-100 ${selectedScenario?.uuid === scenario.uuid ? 'bg-gray-100' : ''}`
                                  : `hover:bg-matrix-green/10 border-b border-matrix-green/10 ${selectedScenario?.uuid === scenario.uuid ? 'bg-matrix-green/20' : ''}`
                            }`}
                          >
                            <div className="flex items-start gap-3 w-full">
                              <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy-deep)') : scenario.color }} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <div className={`font-bold text-sm break-words max-w-full ${!isAhp ? 'font-mono' : ''}`} style={{ color: isAhp ? (ahpDark ? 'var(--civic-warm)' : 'var(--civic-navy-deep)') : scenario.color }}>
                                    {scenario.name}
                                  </div>
                                  {scenario.isVerified && (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-400" title={t('scenarios:card.verified')} />
                                  )}
                                  {scenario.severityLevel != null && (() => {
                                    const SV_COLORS = ['var(--civic-money)', '#3b7a57', 'var(--civic-gold)', '#e67e22', 'var(--civic-red)'];
                                    const SV_LABELS = [
                                      t('common:costQuartile.base'),
                                      t('common:costQuartile.lowCost'),
                                      t('common:costQuartile.midCost'),
                                      t('common:costQuartile.highCost'),
                                      t('common:costQuartile.highest'),
                                    ];
                                    const si = Math.min(Math.max(Math.round(scenario.severityLevel), 0), 4);
                                    if (si === 0) {
                                      return (
                                        <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded" style={{ color: 'var(--civic-money)', backgroundColor: '#2b5e4910' }}>
                                          {t('common:costQuartile.noComplications')}
                                        </span>
                                      );
                                    }
                                    return (
                                      <div className="flex items-center gap-1" title={t('scenarios:browse.severityTitle', { label: SV_LABELS[si], level: si })}>
                                        <div className="flex gap-[2px]">
                                          {[1,2,3,4].map(lvl => (
                                            <div key={lvl} style={{
                                              width: 6, height: 10, borderRadius: 2,
                                              backgroundColor: lvl <= si ? SV_COLORS[si] : 'rgba(var(--civic-ink-rgb),0.08)',
                                              transition: 'background-color 0.2s',
                                            }} />
                                          ))}
                                        </div>
                                        <span className="text-[8px] font-semibold" style={{ color: SV_COLORS[si] }}>
                                          {SV_LABELS[si]}
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </div>
                                <div className={`text-[10px] line-clamp-1 mb-2 ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
                                  {typeof scenario.description === 'string' ? scenario.description : JSON.stringify(scenario.description)}
                                </div>

                                {/* Tags */}
                                {scenario.tags && scenario.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {scenario.tags.slice(0, 3).map((tag, idx) => (
                                      <span key={idx} className="px-1.5 py-0.5 text-[9px] rounded border" style={{
                                        backgroundColor: isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.1)' : '#00214706') : undefined,
                                        color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy-deep)') : undefined,
                                        borderColor: isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.2)' : '#00214715') : undefined,
                                      }}>
                                        {tag}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Creator & Engagement Metrics */}
                                <div className={`flex items-center gap-3 text-[9px] ${ahpDark ? 'text-gray-500' : 'text-muted-foreground'}`}>
                                  {scenario.createdBy && (
                                    <div className="flex items-center gap-1">
                                      <User className="w-2.5 h-2.5" />
                                      <span>{scenario.createdBy}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* Compact cost + action buttons — mobile-friendly,
                                  matches the search-results row. Duration/procedures
                                  live on the card + detail page, not this typeahead
                                  row (their fixed-width columns used to crush the
                                  scenario name to one letter per line on phones). */}
                              <div className="flex flex-col items-end flex-shrink-0 gap-0.5 text-[10px]">
                                <div className="font-mono font-bold text-[15px]" style={{ color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy-deep)') : 'var(--civic-red)' }}>
                                  ${displayCost >= 1000 ? `${(displayCost / 1000).toFixed(0)}k` : Math.round(displayCost).toLocaleString()}
                                </div>
                                <div className={`text-[9px] whitespace-nowrap ${hasRealData ? 'text-blue-500' : 'text-gray-400'}`}>
                                  {hasRealData ? 'national avg' : 'est.'}
                                </div>
                              </div>
                              <div className="flex flex-col gap-1 flex-shrink-0 ml-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const members = familyStorage.getMembers();
                                    setAddToPlanMember(members[0]?.id || '');
                                    setShowAddToPlan(showAddToPlan === scenario.id ? null : scenario.id);
                                  }}
                                  className={`p-1.5 rounded transition-colors ${ahpDark ? 'hover:bg-white/10' : isAhp ? 'hover:bg-gray-100' : 'hover:bg-matrix-green/20'}`}
                                  title={t('scenarios:detail.addToFamilyPlan')}
                                >
                                  <Calendar className={`w-3.5 h-3.5 ${ahpDark ? 'text-gray-500 hover:text-civic-gold' : isAhp ? 'text-gray-400 hover:text-civic-navy-deep' : 'text-muted-foreground hover:text-matrix-green'}`} />
                                </button>
                                <button
                                  onClick={(e) => toggleBookmark(scenario.id, e)}
                                  className={`p-1.5 rounded transition-colors ${ahpDark ? 'hover:bg-white/10' : isAhp ? 'hover:bg-gray-100' : 'hover:bg-matrix-green/20'}`}
                                  title={bookmarkedScenarioIds.includes(scenario.id) ? 'Remove bookmark' : 'Bookmark this scenario'}
                                >
                                  <Bookmark
                                    className={`w-3.5 h-3.5 ${
                                      bookmarkedScenarioIds.includes(scenario.id)
                                        ? (ahpDark ? 'text-civic-gold fill-civic-gold' : isAhp ? 'text-civic-red fill-civic-red' : 'text-matrix-green fill-matrix-green')
                                        : (ahpDark ? 'text-gray-500' : 'text-gray-400')
                                    }`}
                                  />
                                </button>
                              </div>
                              {/* Add to Plan popover */}
                              {showAddToPlan === scenario.id && (
                                <div
                                  className={`mt-2 p-3 rounded-lg border space-y-2 ${
                                    ahpDark ? 'border-civic-gold/20 bg-civic-navy-ink' : 'border-matrix-green/20 bg-black/90'
                                  }`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <p className={`text-xs font-medium ${ahpDark ? 'text-civic-gold' : 'text-matrix-green'}`}>{t('scenarios:detail.addToFamilyPlan')}</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {familyStorage.getMembers().map(m => (
                                      <button
                                        key={m.id}
                                        onClick={() => setAddToPlanMember(m.id)}
                                        className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${
                                          addToPlanMember === m.id
                                            ? (ahpDark ? 'bg-civic-gold text-civic-card-dark' : 'bg-matrix-green text-black')
                                            : (ahpDark ? 'bg-civic-gold/10 text-civic-gold hover:bg-civic-gold/20' : 'bg-matrix-green/10 text-matrix-green hover:bg-matrix-green/20')
                                        }`}
                                      >
                                        {familyStorage.getDisplayName(m)}
                                      </button>
                                    ))}
                                  </div>
                                  <input
                                    type="date"
                                    value={addToPlanDate}
                                    onChange={(e) => setAddToPlanDate(e.target.value)}
                                    className={`w-full h-8 px-2 text-xs rounded border text-foreground ${
                                      ahpDark ? 'border-civic-gold/20 bg-civic-card-dark' : 'border-matrix-green/20 bg-black/50'
                                    }`}
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => handleAddToPlan(scenario)}
                                      disabled={!addToPlanMember}
                                      className={`flex-1 h-7 text-[11px] font-medium rounded disabled:opacity-50 ${
                                        ahpDark ? 'bg-civic-gold text-civic-card-dark hover:bg-civic-gold/90' : 'bg-matrix-green text-black hover:bg-matrix-green/90'
                                      }`}
                                    >
                                      {t('scenarios:detail.saveToPlan')}
                                    </button>
                                    <button
                                      onClick={() => setShowAddToPlan(null)}
                                      className={`px-3 h-7 text-[11px] rounded border hover:text-foreground ${
                                        ahpDark ? 'border-civic-gold/20 text-gray-400' : 'border-matrix-green/20 text-muted-foreground'
                                      }`}
                                    >
                                      {t('common:actions.close')}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* No Results */}
                  {showScenarioDropdown && scenarioSearchQuery && groupedSearchResults.length === 0 && (
                    <div
                      ref={scenarioDropdownRef}
                      className={`absolute z-[9999] w-full mt-1 rounded-lg shadow-lg p-4 ${
                        ahpDark
                          ? 'bg-civic-card-dark border border-civic-gold/30'
                          : isAhp
                            ? 'bg-white border border-gray-200'
                            : 'bg-black/95 border border-matrix-green/20'
                      }`}
                    >
                      <div className={`text-center text-sm mb-3 ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
                        No scenarios found for &ldquo;{scenarioSearchQuery}&rdquo;
                        {searchCategory !== 'all' && ` in ${SCENARIO_CATEGORIES.find(c => c.key === searchCategory)?.label}`}
                      </div>
                      <div className={`text-center text-[11px] mb-4 ${ahpDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Try searching by condition, procedure, or specialty
                      </div>
                      {/* Category pills for browsing */}
                      <div className="flex flex-wrap gap-1.5 justify-center mb-4">
                        {SCENARIO_CATEGORIES.filter(c => c.key !== 'all').slice(0, 6).map(cat => (
                          <button
                            key={cat.key}
                            onClick={() => {
                              setSearchCategory(cat.key);
                              setScenarioSearchQuery('');
                              // Show dropdown with popular scenarios filtered by this category
                            }}
                            className="px-2.5 py-1 rounded-full text-[10px] font-medium transition-all"
                            style={{
                              backgroundColor: 'transparent',
                              color: cat.color,
                              border: `1.5px solid ${cat.color}40`,
                            }}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            setShowScenarioDropdown(false);
                            setShowCreateDialog(true);
                          }}
                          className={`w-full px-4 py-3 text-left rounded-lg border transition-colors ${
                            ahpDark
                              ? 'bg-civic-gold/8 hover:bg-civic-gold/15 border-civic-gold/20'
                              : isAhp
                                ? 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                                : 'bg-matrix-green/5 hover:bg-matrix-green/10 border-matrix-green/20'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base ${
                              ahpDark ? 'bg-civic-gold/15' : isAhp ? 'bg-civic-red/10' : 'bg-matrix-green/10'
                            }`}>🔧</div>
                            <div>
                              <div className={`text-sm font-semibold ${ahpDark ? 'text-civic-warm' : isAhp ? 'text-civic-ink' : 'text-matrix-green'}`}>
                                Build Your Own — Free
                              </div>
                              <div className={`text-[11px] ${ahpDark ? 'text-gray-500' : 'text-muted-foreground'}`}>
                                Combine templates or build from scratch with real billing codes
                              </div>
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={() => {
                            setShowScenarioDropdown(false);
                            setShowCreateDialog(true);
                            // We'll auto-select AI path after dialog opens
                            setTimeout(() => {
                              const aiBtn = document.querySelector('[data-path="ai"]') as HTMLButtonElement;
                              if (aiBtn) aiBtn.click();
                            }, 300);
                          }}
                          className={`w-full px-4 py-3 text-left rounded-lg border transition-colors ${
                            ahpDark
                              ? 'bg-civic-red-soft/8 hover:bg-civic-red-soft/15 border-civic-red-soft/20'
                              : isAhp
                                ? 'bg-civic-red/5 hover:bg-civic-red/10 border-civic-red/20'
                                : 'bg-amber-500/5 hover:bg-amber-500/10 border-amber-500/20'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-base ${
                              ahpDark ? 'bg-civic-red-soft/15' : isAhp ? 'bg-civic-red/10' : 'bg-amber-500/10'
                            }`}>✨</div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-semibold ${ahpDark ? 'text-civic-red-soft' : isAhp ? 'text-civic-red' : 'text-amber-400'}`}>
                                  AI-Powered — $1.99
                                </span>
                              </div>
                              <div className={`text-[11px] ${ahpDark ? 'text-gray-500' : 'text-muted-foreground'}`}>
                                Describe your situation and AI builds a complete scenario with real pricing
                              </div>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Selected Scenario Details */}
                {selectedScenario && (
                  <div className={`flex items-center gap-4 flex-wrap sm:flex-nowrap sm:flex-shrink-0 sm:pl-4 sm:border-l ${
                    ahpDark ? 'border-civic-gold/30' : isAhp ? 'border-civic-red/20' : 'border-matrix-green/20'
                  }`}>
                    <div className="text-center">
                      <div className={`text-[10px] mb-1 ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>{t('scenarios:card.duration', { duration: '' }).replace(': ', '')}</div>
                      <div className={`font-mono text-xs ${ahpDark ? 'text-civic-warm' : isAhp ? 'text-civic-ink' : 'text-matrix-green'}`}>{selectedScenario.duration}</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-[10px] mb-1 ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>{t('scenarios:card.procedures', { count: '' }).trim()}</div>
                      <div className={`font-mono text-xs font-bold ${ahpDark ? 'text-civic-warm' : isAhp ? 'text-civic-ink' : 'text-matrix-green'}`}>{selectedScenario.totalProcedures}</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-[10px] mb-1 ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>{t('scenarios:detail.estimatedCost')}</div>
                      <div className={`font-mono text-sm font-bold ${ahpDark ? 'text-civic-gold' : isAhp ? 'text-civic-red' : 'text-matrix-green'}`}>
                        ${nationalTotal > 0 ? `${Math.round(nationalTotal / 1000)}k` : `${(selectedScenario.estimatedCost / 1000).toFixed(0)}k`}
                      </div>
                      <div className={`text-[10px] ${ahpDark ? 'text-gray-400' : isAhp ? 'text-civic-ink/60' : 'text-blue-400'}`}>{t('scenarios:card.nationalAverage')}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Description sentence — collapses (wrapper and divider too)
                  while the header is pinned, to give the page back a row. */}
              {selectedScenario && !headStuck && (
                <div className={`mt-3 pt-3 border-t ${ahpDark ? 'border-civic-gold/20' : isAhp ? 'border-civic-red/10' : 'border-matrix-green/20'}`}>
                  <p className={`text-xs ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>{selectedScenario.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Featured article — lived inside the sticky header until 2026-09-05.
            The header stays with the scenario itself; the article card sits in
            normal flow directly under it and scrolls away like everything else. */}
        {selectedScenario?.uuid && (
          <div className="mb-6">
            <BacklinkArticleCard scenarioId={selectedScenario.uuid} />
          </div>
        )}

        {/* Overview summary — the DEFAULT landing block for the scenario: a
            quick-nav across sections (scrolls on desktop, opens the modal on
            mobile/native) + the Dignity CTA. Sits right under the summary so it's
            the first thing shown before diving into any section. */}
        {selectedScenario && isAhp && renderOverviewSummary(selectedScenario)}

        {/* (The mobile phase-card embed that lived here was a duplicate of the
            severity section's current-scenario banner card — the TOC chips it
            carried now render under that banner card instead.) */}

        {/* Loading state when navigating directly to a scenario URL */}
        {!selectedScenario && uuid && scenariosLoading && (
          <div className="w-full flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className={`w-8 h-8 animate-spin ${isAhp ? 'text-civic-red' : 'text-matrix-green'}`} />
            <p className={`text-sm ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
              Loading scenario data...
            </p>
          </div>
        )}

        {/* ── Related Scenarios Row with Per-Card Severity Sliders ── */}
        {selectedScenario && (familyScenarios.length > 0 || relatedScenarios.length > 0) && (() => {
          const COMP_COLORS = ['var(--civic-money)', '#5a9a3c', 'var(--civic-gold)', '#e67e22', 'var(--civic-red)'];
          const COMP_LABELS = [
            t('common:costQuartile.base'),
            t('common:costQuartile.lowCost'),
            t('common:costQuartile.midCost'),
            t('common:costQuartile.highCost'),
            t('common:costQuartile.highest'),
          ];
          const serif = "'Instrument Serif', Georgia, serif";
          const PHASE_COLORS = ['var(--civic-red)', 'var(--civic-navy)', 'var(--civic-gold)', 'var(--civic-money)', '#6B4C8A', '#D4763B'];

          // Build the current scenario's family (sorted by computed severity)
          const selectedWithSev = { ...selectedScenario, severityLevel: familySeverityMap.get(selectedScenario.uuid) ?? selectedScenario.severityLevel ?? 0 };
          const currentFamily = selectedScenario.scenarioFamilyId
            ? [selectedWithSev, ...familyScenarios].sort((a, b) => (a.severityLevel ?? 0) - (b.severityLevel ?? 0))
            : [selectedWithSev];

          // Find root scenario name (severity 0) for family title
          const familyRootName = selectedScenario.scenarioFamilyId
            ? (currentFamily.find(s => (s.severityLevel ?? 0) === 0)?.name || selectedScenario.name)
            : selectedScenario.name;

          // Helper: render a scenario card with optional severity slider
          const renderCard = (scenario: Scenario, cardSlotId: string, familyMembers: Scenario[], isCurrent: boolean) => {
            // If this card has a family, check if user has slid to a different severity
            const overrideUuid = cardSeverityOverrides[cardSlotId];
            const displayScenario = overrideUuid
              ? (familyMembers.find(f => f.uuid === overrideUuid) || scenario)
              : scenario;
            const Icon = displayScenario.icon;
            // Honor the avg/median + capped/raw toggles. median → medianCost;
            // avg+raw → estimatedCostRaw (uncapped); avg+capped → estimatedCost
            // (winsorized default). All from real chargemaster data
            // (scenario_costs_cache) — never seed estimates.
            const useMedian = costStat === 'median' && (displayScenario.medianCost ?? 0) > 0;
            const useRaw = !useMedian && costScope === 'raw' && (displayScenario.estimatedCostRaw ?? 0) > 0;
            const cost = useMedian
              ? (displayScenario.medianCost as number)
              : (useRaw ? (displayScenario.estimatedCostRaw as number) : displayScenario.estimatedCost);
            const sev = Math.min(Math.max(displayScenario.severityLevel ?? 0, 0), 4);
            const hasFamily = familyMembers.length > 1;

            // Phase breakdown from the real-data cache (median / raw / capped per
            // toggle). No procedures-based fallback — that would re-introduce seed
            // estimatedCost figures, which we never display.
            const phaseSource = (useMedian && displayScenario.phaseBreakdownMedian && Object.keys(displayScenario.phaseBreakdownMedian).length > 0)
              ? displayScenario.phaseBreakdownMedian
              : (useRaw && displayScenario.phaseBreakdownRaw && Object.keys(displayScenario.phaseBreakdownRaw).length > 0)
                ? displayScenario.phaseBreakdownRaw
                : displayScenario.phaseBreakdown;
            const phaseBreakdown: Record<string, number> = phaseSource && Object.keys(phaseSource).length > 0
              ? { ...phaseSource }
              : {};
            // Resolve which phase names to display — prefer scenario.phases if they have cost data,
            // otherwise fall back to the keys from phaseBreakdown (which are the actual procedure phases)
            const declaredPhases = (displayScenario.phases || []).map((p: any) => typeof p === 'string' ? p : p?.name || String(p));
            const declaredHaveCosts = declaredPhases.some((p: string) => (phaseBreakdown[p] || 0) > 0);
            const phases = (declaredHaveCosts ? declaredPhases : Object.keys(phaseBreakdown))
              .sort((a: string, b: string) => (phaseBreakdown[b] || 0) - (phaseBreakdown[a] || 0));
            const maxPhaseCost = Math.max(...Object.values(phaseBreakdown), 1);

            return (
              <div
                key={cardSlotId}
                className={`rounded-xl relative ${isCurrent ? 'w-full' : 'flex-shrink-0 w-full lg:w-[320px] cursor-pointer hover:shadow-lg'}`}
                style={{
                  backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff',
                  border: isCurrent ? `2px solid ${ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)'}` : `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(var(--civic-ink-rgb),0.08)'}`,
                  boxShadow: isCurrent ? `0 4px 20px ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(var(--civic-red-rgb),0.12)'}` : `0 2px 16px ${ahpDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)'}`,
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                }}
                onClick={() => { if (!isCurrent) handleScenarioSelect(displayScenario); }}
                onMouseEnter={(e) => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.borderColor = ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)'; }}
                onMouseLeave={(e) => { if (!isCurrent) (e.currentTarget as HTMLDivElement).style.borderColor = ahpDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(var(--civic-ink-rgb),0.08)'; }}
              >
                {/* Severity gauge — top right */}
                <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1">
                  {isCurrent && (
                    <div className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider" style={{ backgroundColor: 'var(--civic-red)', color: '#fff' }}>
                      Current
                    </div>
                  )}
                  <div className="flex flex-col items-end gap-0.5" title={`Severity: ${sev === 0 ? 'No Complications' : `${COMP_LABELS[sev]} ${sev}/4`}`}>
                    {sev === 0 ? (
                      <span style={{ fontSize: 9, fontWeight: 600, color: COMP_COLORS[0], backgroundColor: '#2b5e4910', padding: '2px 6px', borderRadius: 4 }}>
                        No Complications
                      </span>
                    ) : (
                      <>
                        <div className="flex gap-[2px]">
                          {[1,2,3,4].map(lvl => (
                            <div key={lvl} style={{
                              width: 8, height: 14, borderRadius: 2,
                              backgroundColor: lvl <= sev ? COMP_COLORS[sev] : 'rgba(var(--civic-ink-rgb),0.08)',
                            }} />
                          ))}
                        </div>
                        <span style={{ fontSize: 9, fontWeight: 600, color: COMP_COLORS[sev] }}>
                          {`${COMP_LABELS[sev]} ${sev}/4`}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="px-5 pt-5 pb-2 flex flex-col">
                  {/* Title + icon */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: `${displayScenario.color}15` }}>
                      <Icon className={isCurrent ? 'w-6 h-6' : 'w-5 h-5'} style={{ color: displayScenario.color }} />
                    </div>
                    <div className="flex-1 min-w-0 pr-14">
                      {/* Title is the ACTUAL selected scenario (the variant), so picking
                          a variant visibly changes the card. The family root is shown as
                          a small eyebrow above for context. */}
                      {isCurrent && hasFamily && (displayScenario.severityLevel ?? 0) > 0 && familyRootName !== displayScenario.name && (
                        <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide truncate" style={{ color: ahpDark ? 'var(--civic-muted)' : '#9ca3af' }}>
                          {familyRootName} family
                        </div>
                      )}
                      <h3 className="leading-snug line-clamp-2" style={{ fontFamily: serif, fontSize: isCurrent ? 'clamp(17px, 2.5vw, 20px)' : 16, fontWeight: 400, color: ahpDark ? '#e5e7eb' : 'var(--civic-ink)' }}>
                        {displayScenario.name}
                      </h3>
                      <p className="mt-1 line-clamp-2" style={{ fontSize: 12, color: ahpDark ? 'var(--civic-muted)' : '#6b7280', lineHeight: 1.4 }}>
                        {displayScenario.familyVariantLabel && displayScenario.familyVariantLabel !== 'uncomplicated'
                          ? displayScenario.familyVariantLabel
                          : displayScenario.description}
                      </p>
                    </div>
                  </div>

                  {/* Cost + meta. On the active card we surface an AVG \u21c4 MEDIAN
                   *  toggle (when medianCost is present) so users can flip the
                   *  headline without scrolling to the cost summary block. The
                   *  toggle writes through `costStat` so the breakdown lower on
                   *  the page stays in sync. */}
                  {(() => {
                    const showToggle = isCurrent && (displayScenario.medianCost ?? 0) > 0;
                    // `cost` already reflects median / raw / capped per the toggles.
                    const displayedCost = cost;
                    // Capped⇄raw toggle shown only in avg mode when a raw figure exists.
                    const showScopeToggle = isCurrent && costStat === 'avg' && (displayScenario.estimatedCostRaw ?? 0) > 0;
                    return (
                      <div className="flex items-end justify-between mb-3 gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p style={{ fontSize: 10, color: ahpDark ? 'var(--civic-muted)' : '#6b7280', letterSpacing: '0.5px', textTransform: 'uppercase' as const, margin: 0 }}>
                              {isCurrent && costStat === 'median'
                                ? t('scenarios:costSummary.medianHospitalPrice', { defaultValue: 'Median Hospital Price' })
                                : isCurrent && costStat === 'avg' && costScope === 'raw'
                                  ? t('scenarios:costSummary.avgHospitalPriceRaw', { defaultValue: 'Avg Hospital Price (Raw)' })
                                  : t('scenarios:card.avgHospitalPrice', { defaultValue: 'Avg Hospital Price' })}
                            </p>
                            {showToggle && (
                              <div
                                role="tablist"
                                aria-label={t('scenarios:costSummary.ariaCostStat', { defaultValue: 'Cost statistic' })}
                                className="inline-flex items-center rounded-md p-0.5"
                                style={{
                                  background: ahpDark ? 'rgba(255,255,255,0.06)' : isAhp ? 'rgba(var(--civic-red-rgb),0.06)' : 'rgba(34,197,94,0.08)',
                                  border: `1px solid ${ahpDark ? 'rgba(255,255,255,0.10)' : isAhp ? 'rgba(var(--civic-red-rgb),0.18)' : 'rgba(34,197,94,0.30)'}`,
                                }}
                              >
                                {(['avg', 'median'] as const).map((stat) => {
                                  const active = costStat === stat;
                                  return (
                                    <button
                                      key={stat}
                                      type="button"
                                      role="tab"
                                      aria-selected={active}
                                      onClick={(e) => { e.stopPropagation(); handleCostStatChange(stat); }}
                                      className="text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded transition-colors"
                                      style={{
                                        background: active
                                          ? (ahpDark ? 'var(--civic-gold)' : isAhp ? 'var(--civic-red)' : '#22c55e')
                                          : 'transparent',
                                        color: active
                                          ? (ahpDark ? '#1a1a1a' : '#fff')
                                          : (ahpDark ? '#aaa' : isAhp ? '#1B1B1B99' : '#22c55e'),
                                        cursor: 'pointer',
                                      }}
                                      title={stat === 'avg'
                                        ? t('scenarios:costSummary.tooltipAvg', { defaultValue: 'Mean of per-hospital prices' })
                                        : t('scenarios:costSummary.tooltipMedian', { defaultValue: 'Median per-hospital price' })}
                                    >
                                      {stat === 'avg'
                                        ? t('scenarios:costSummary.avg', { defaultValue: 'Avg' })
                                        : t('scenarios:costSummary.median', { defaultValue: 'Median' })}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            {showScopeToggle && (
                              <div
                                role="tablist"
                                aria-label={t('scenarios:costSummary.ariaCostScope', { defaultValue: 'Outlier capping' })}
                                className="inline-flex items-center rounded-md p-0.5"
                                style={{
                                  background: ahpDark ? 'rgba(255,255,255,0.06)' : isAhp ? 'rgba(var(--civic-navy-rgb),0.06)' : 'rgba(34,197,94,0.08)',
                                  border: `1px solid ${ahpDark ? 'rgba(255,255,255,0.10)' : isAhp ? 'rgba(var(--civic-navy-rgb),0.18)' : 'rgba(34,197,94,0.30)'}`,
                                }}
                              >
                                {(['capped', 'raw'] as const).map((scope) => {
                                  const active = costScope === scope;
                                  return (
                                    <button
                                      key={scope}
                                      type="button"
                                      role="tab"
                                      aria-selected={active}
                                      onClick={(e) => { e.stopPropagation(); handleCostScopeChange(scope); }}
                                      className="text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded transition-colors"
                                      style={{
                                        background: active
                                          ? (ahpDark ? '#5a7da8' : isAhp ? 'var(--civic-navy)' : '#22c55e')
                                          : 'transparent',
                                        color: active
                                          ? '#fff'
                                          : (ahpDark ? '#aaa' : isAhp ? '#1B1B1B99' : '#22c55e'),
                                        cursor: 'pointer',
                                      }}
                                      title={scope === 'capped'
                                        ? t('scenarios:costSummary.tooltipCapped', { defaultValue: 'Outlier-capped (winsorized at 25× the median)' })
                                        : t('scenarios:costSummary.tooltipRaw', { defaultValue: 'Raw uncapped average — includes extreme charges' })}
                                    >
                                      {scope === 'capped'
                                        ? t('scenarios:costSummary.capped', { defaultValue: 'Capped' })
                                        : t('scenarios:costSummary.raw', { defaultValue: 'Raw' })}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <p className="flex items-baseline gap-0.5" style={{ fontFamily: serif, margin: 0 }}>
                            <span style={{ fontSize: 12, color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)' }}>$</span>
                            <span style={{ fontSize: isCurrent ? 'clamp(22px, 2.8vw, 28px)' : 22, color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)', fontWeight: 400, transition: 'all 0.3s' }}>
                              {displayedCost > 0 ? Math.round(displayedCost).toLocaleString() : '\u2014'}
                            </span>
                          </p>
                        </div>
                        <div className="flex gap-4 text-right">
                          <div>
                            <p style={{ fontSize: 10, color: ahpDark ? 'var(--civic-muted)' : '#6b7280' }}>Procedures</p>
                            <p style={{ fontSize: 13, color: ahpDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)', marginTop: 2 }}>{displayScenario.totalProcedures}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* p10/median/avg/p90 band — real per-hospital complete bills,
                   *  same basis as the headline cost above. Main card only (not
                   *  the smaller family-variant cards); null-safe inside
                   *  PriceRangeBand itself, which renders nothing without all
                   *  four figures (thin-coverage scenarios have no p10/p90 yet). */}
                  {/* ...and, on the current card, what it costs near the reader:
                   *  the nearest / cheapest / priciest within the radius on the
                   *  same band, the nearest five, and one location + radius
                   *  control (ScenarioLocalPrices; Trent, 2026-09-29). */}
                  {isCurrent && (
                    <ScenarioLocalPrices
                      scenarioId={(displayScenario as any).uuid || (displayScenario as any).id}
                      nationalTotal={displayScenario.estimatedCost ?? cacheAvgTotal}
                      band={{ p10: displayScenario.p10Cost ?? null, median: displayScenario.medianCost ?? null, avg: displayScenario.estimatedCost ?? null, p90: displayScenario.p90Cost ?? null }}
                      dark={ahpDark}
                      location={userLocation}
                      isLocating={isLocating}
                      error={locationError}
                      onDetect={detectLocation}
                      onZip={lookupZip}
                      radius={radiusMiles}
                      onRadius={setRadiusMiles}
                    />
                  )}

                  {/* Phase breakdown bars */}
                  {phases.length > 0 && (
                    <div className="space-y-1 mb-3">
                      {phases.slice(0, 4).map((phase, idx) => {
                        const phaseName = typeof phase === 'string' ? phase : (phase as any)?.name || String(phase);
                        const phaseCost = phaseBreakdown[phaseName] || phaseBreakdown[phase as any] || 0;
                        const pct = Math.max((phaseCost / maxPhaseCost) * 100, 3);
                        return (
                          <div key={phaseName} className="flex items-center gap-2">
                            <span className="text-right flex-shrink-0 truncate" style={{ width: isCurrent ? 90 : 80, color: ahpDark ? 'var(--civic-muted)' : '#6b7280', fontSize: isCurrent ? 11 : 10 }}>{phaseName}</span>
                            <div className="flex-1 rounded-sm" style={{ height: isCurrent ? 16 : 12, backgroundColor: ahpDark ? 'rgba(255,255,255,0.06)' : 'rgba(var(--civic-ink-rgb),0.04)' }}>
                              <div className="h-full rounded-sm transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: PHASE_COLORS[idx % PHASE_COLORS.length], minWidth: 3 }} />
                            </div>
                            <span style={{ fontSize: isCurrent ? 11 : 10, fontFamily: "'JetBrains Mono', monospace", color: ahpDark ? 'var(--civic-muted)' : '#6b7280', width: isCurrent ? 52 : 45, textAlign: 'right' as const, flexShrink: 0 }}>
                              ${phaseCost >= 1000 ? `${(phaseCost / 1000).toFixed(0)}k` : Math.round(phaseCost).toLocaleString()}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* ── Severity Slider (on every card that has a family) ── */}
                  {hasFamily && (() => {
                    // Group family members by severity level
                    const byLevel: Record<number, Scenario[]> = {};
                    familyMembers.forEach(fm => {
                      const lvl = Math.min(Math.max(fm.severityLevel ?? 0, 0), 4);
                      if (!byLevel[lvl]) byLevel[lvl] = [];
                      byLevel[lvl].push(fm);
                    });
                    const totalInFamily = familyMembers.length;
                    // Find which level the current display scenario is in
                    const currentLevel = Math.min(Math.max(displayScenario.severityLevel ?? 0, 0), 4);
                    const scenariosAtCurrentLevel = byLevel[currentLevel] || [];
                    const currentIndexInLevel = scenariosAtCurrentLevel.findIndex(s => s.uuid === displayScenario.uuid);

                    return (
                      <div className="pt-2 mt-auto" style={{ borderTop: '1px solid rgba(var(--civic-ink-rgb),0.06)' }}>
                        <div className="flex items-center justify-between mb-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowFamilyModal(true); }}
                            className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider hover:opacity-70 transition-opacity"
                            style={{ color: COMP_COLORS[sev], letterSpacing: '0.5px' }}
                          >
                            {COMP_LABELS[sev]}
                            {scenariosAtCurrentLevel.length > 1 && (
                              <span style={{ fontSize: 9, opacity: 0.7 }}>
                                ({currentIndexInLevel + 1}/{scenariosAtCurrentLevel.length})
                              </span>
                            )}
                            <ChevronRight className="w-3 h-3" />
                          </button>
                          <div className="flex items-center gap-2">
                            <span style={{ fontSize: 9, color: '#9ca3af' }}>{totalInFamily} scenarios</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); setShowFamilyModal(true); }}
                              className="hover:opacity-70 transition-opacity"
                              title="Explore all scenarios in this family"
                            >
                              <Maximize2 className="w-3.5 h-3.5" style={{ color: '#9ca3af' }} />
                            </button>
                          </div>
                        </div>
                        {/* Dot slider — fixed 5 positions (0-4) */}
                        <div className="flex items-center gap-0 relative" style={{ height: 36 }}>
                          {/* Track line */}
                          <div className="absolute left-2 right-2 top-[16px] h-[3px] rounded-full" style={{ background: 'linear-gradient(to right, var(--civic-money), #5a9a3c, var(--civic-gold), #e67e22, var(--civic-red))' }} />
                          {/* 5 fixed dots: 0, 1, 2, 3, 4 */}
                          <div className="flex justify-between w-full relative z-10 px-1">
                            {[0, 1, 2, 3, 4].map((level) => {
                              const scenariosAtLevel = byLevel[level] || [];
                              const count = scenariosAtLevel.length;
                              const hasScenario = count > 0;
                              // Currently displayed scenario's index at this level
                              const activeIdx = scenariosAtLevel.findIndex(s => s.uuid === displayScenario.uuid);
                              const isActive = activeIdx >= 0;

                              return (
                                <button
                                  key={level}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!hasScenario) return;
                                    // Open the family/variant explorer scrolled to this
                                    // severity level (instead of navigating, which just
                                    // reloaded the scenario overview).
                                    setFamilyModalLevel(level);
                                    setShowFamilyModal(true);
                                  }}
                                  className="group relative flex flex-col items-center"
                                  title={hasScenario
                                    ? `${COMP_LABELS[level]} — ${count} scenario${count > 1 ? 's' : ''}`
                                    : `${COMP_LABELS[level]} — no scenario`}
                                  disabled={!hasScenario}
                                >
                                  <div className="relative" style={{
                                    width: isActive ? 26 : 22,
                                    height: isActive ? 26 : 22,
                                    borderRadius: '50%',
                                    backgroundColor: isActive ? COMP_COLORS[level] : hasScenario ? (ahpDark ? 'rgba(255,255,255,0.08)' : '#fff') : (ahpDark ? 'rgba(255,255,255,0.03)' : 'rgba(var(--civic-ink-rgb),0.06)'),
                                    border: `2px solid ${hasScenario ? COMP_COLORS[level] : (ahpDark ? 'rgba(255,255,255,0.15)' : 'rgba(var(--civic-ink-rgb),0.15)')}`,
                                    boxShadow: isActive ? `0 0 0 3px ${COMP_COLORS[level]}25` : '0 1px 2px rgba(0,0,0,0.1)',
                                    transition: 'all 0.2s',
                                    cursor: hasScenario ? 'pointer' : 'default',
                                    opacity: hasScenario ? 1 : 0.4,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 10, fontWeight: 700,
                                    color: isActive ? '#fff' : COMP_COLORS[level],
                                  }}>
                                    {level}
                                    {/* Count badge for multi-scenario levels */}
                                    {count > 1 && (
                                      <span style={{
                                        position: 'absolute', top: -6, right: -8,
                                        width: 14, height: 14, borderRadius: '50%',
                                        backgroundColor: COMP_COLORS[level], color: '#fff',
                                        fontSize: 8, fontWeight: 800,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        border: `2px solid ${ahpDark ? 'var(--civic-bg-dark)' : '#fff'}`,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                      }}>
                                        {count}
                                      </span>
                                    )}
                                  </div>
                                  {/* Hover tooltip */}
                                  {hasScenario && (
                                    <div className="hidden group-hover:block absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap px-2 py-0.5 rounded text-[9px] font-medium z-20" style={{
                                      backgroundColor: 'var(--civic-ink)', color: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                    }}>
                                      {count > 1 ? `${count} scenarios` : `$${Math.round(scenariosAtLevel[0].estimatedCost).toLocaleString()}`}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Interaction buttons (current card only) — segmented row.
                   *  Redesigned 2026-05-26: switched from 3 stand-alone pills to
                   *  an evenly-spaced row with consistent borders, icon-first
                   *  layout, and brand-aware accents (green for Helpful, navy
                   *  for Follow, gold for Assign). Filled accents only when
                   *  active so the inactive state stays quiet. */}
                  {isCurrent && (() => {
                    const helpfulActive = helpfulVotes.has(displayScenario.id || displayScenario.uuid);
                    const followActive = followedScenarios.has(displayScenario.id || displayScenario.uuid);
                    const baseBtn = "flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold uppercase tracking-wider transition-all";
                    const colors = {
                      helpful: { active: 'var(--civic-money)', tint: 'rgba(var(--civic-money-rgb),0.10)', tintActive: 'rgba(var(--civic-money-rgb),0.16)' },
                      follow:  { active: 'var(--civic-navy)', tint: 'rgba(var(--civic-navy-rgb),0.10)', tintActive: 'rgba(var(--civic-navy-rgb),0.18)' },
                      assign:  { active: '#B8860B', tint: 'rgba(184,134,11,0.10)', tintActive: 'rgba(184,134,11,0.18)' },
                    };
                    const idleBg = ahpDark ? 'rgba(255,255,255,0.04)' : 'rgba(var(--civic-ink-rgb),0.03)';
                    const idleColor = ahpDark ? 'var(--civic-muted)' : '#6b7280';
                    const idleBorder = ahpDark ? 'rgba(255,255,255,0.08)' : 'rgba(var(--civic-ink-rgb),0.08)';
                    return (
                      <div className="flex gap-1.5 pt-2 mt-1" style={{ borderTop: hasFamily ? 'none' : `1px solid ${idleBorder}` }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const now = scenarioStorage.toggleHelpful(displayScenario.id || displayScenario.uuid);
                            setHelpfulVotes(new Set(scenarioStorage.getHelpfulVotes()));
                            toast.success(now ? 'Marked as helpful' : 'Removed helpful vote');
                          }}
                          className={baseBtn}
                          style={{
                            backgroundColor: helpfulActive ? colors.helpful.tintActive : idleBg,
                            color: helpfulActive ? colors.helpful.active : idleColor,
                            border: `1px solid ${helpfulActive ? colors.helpful.active + '55' : idleBorder}`,
                          }}
                        >
                          <ThumbsUp className="w-3.5 h-3.5" fill={helpfulActive ? colors.helpful.active : 'none'} />
                          {t('scenarios:actions.helpful', { defaultValue: 'Helpful' })}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const now = scenarioStorage.toggleFollow(displayScenario.id || displayScenario.uuid);
                            setFollowedScenarios(new Set(scenarioStorage.getFollowedScenarios()));
                            toast.success(now ? 'Following' : 'Unfollowed');
                          }}
                          className={baseBtn}
                          style={{
                            backgroundColor: followActive ? colors.follow.tintActive : idleBg,
                            color: followActive ? colors.follow.active : idleColor,
                            border: `1px solid ${followActive ? colors.follow.active + '55' : idleBorder}`,
                          }}
                        >
                          <Bookmark className="w-3.5 h-3.5" fill={followActive ? colors.follow.active : 'none'} />
                          {t('scenarios:actions.follow', { defaultValue: 'Follow' })}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setAssignTarget(displayScenario); }}
                          className={baseBtn}
                          style={{
                            backgroundColor: assignedNames.has(displayScenario.name) ? colors.assign.tintActive : idleBg,
                            color: assignedNames.has(displayScenario.name) ? colors.assign.active : idleColor,
                            border: `1px solid ${assignedNames.has(displayScenario.name) ? colors.assign.active + '55' : idleBorder}`,
                          }}
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          {assignedNames.has(displayScenario.name)
                            ? t('scenarios:actions.assigned', { defaultValue: 'Assigned' })
                            : t('scenarios:actions.assign', { defaultValue: 'Assign' })}
                        </button>
                      </div>
                    );
                  })()}

                  {/* The narration / care-resources VN block used to live
                   *  here inside the card. Master 2026-05-26 moved it to its
                   *  own block below the card so it can breathe and read as
                   *  a visual-novel dialog rather than another card row.
                   *  See the <VisualNovelStrip> render lower in this IIFE. */}
                </div>
              </div>
            );
          };

          const severityEl = (
            <div className="w-full mb-6 scroll-mt-24" id="severity-variants">
              {/* Section header */}
              <div className="flex items-center gap-2 mb-3 px-1">
                <div className="h-px flex-1" style={{ backgroundColor: ahpDark ? 'rgba(var(--civic-gold-rgb),0.22)' : 'rgba(var(--civic-red-rgb),0.15)' }} />
                <span style={{ fontSize: 11, fontWeight: 700, color: ahpDark ? 'rgba(var(--civic-gold-rgb),0.85)' : '#6b7280', letterSpacing: '0.8px', textTransform: 'uppercase' as const }}>
                  {familyScenarios.length > 0 ? 'Severity & Related Scenarios' : 'Related Scenarios'}
                </span>
                <div className="h-px flex-1" style={{ backgroundColor: ahpDark ? 'rgba(var(--civic-gold-rgb),0.22)' : 'rgba(var(--civic-red-rgb),0.15)' }} />
              </div>

              {/* Full-width stacked layout: the current scenario reads as a
                  banner on top, then the variant + related cards flow across the
                  entire width below. The old two-column split (current card left,
                  variants squeezed right) and its dead duplicate Dignity card were
                  removed once Dignity moved up to the Overview hub. */}
              <div className="flex flex-col gap-3">
                {/* Banner: current scenario card, full width */}
                <div className="w-full">
                  {renderCard(selectedWithSev, 'current', currentFamily, true)}
                </div>

                {/* Section table of contents — moved here from the (removed)
                    duplicate top phase-card so the banner card above is the
                    single scenario card. Press → modal on mobile, scroll on
                    desktop; the rail scroll-spies the same sections. */}
                {isMobileViewport && (
                  <div className="grid grid-cols-3 gap-1.5">
                    {[
                      { key: 'cost-timeline', label: 'Timeline', icon: Calendar },
                      { key: 'code-breakdown', label: 'Code Breakdown', icon: Receipt },
                      { key: 'price-comparison', label: 'Price Comparison', icon: Scale },
                      { key: 'nearby-hospitals', label: 'Hospitals', icon: Building2 },
                      ...(brand.key === 'ahp' ? [{ key: 'dignity-guide', label: "Dignity's Guide", icon: Sparkles }] : []),
                      { key: 'international', label: 'Global Prices', icon: Globe },
                      { key: 'care-resources', label: 'Connect to Care', icon: HandHeart },
                    ].filter((s) => s.key !== 'international' || hasIntlData(selectedScenario?.id, selectedScenario?.uuid)).map((s) => {
                      const ChipIcon = s.icon;
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => goToSection(s.key, s.key)}
                          className="flex items-center justify-center gap-1 py-2 px-1 rounded-lg text-[11px] font-semibold transition-colors"
                          style={{
                            border: `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.25)' : 'rgba(var(--civic-ink-rgb),0.12)'}`,
                            color: ahpDark ? 'rgba(var(--civic-warm-rgb),0.75)' : 'rgba(var(--civic-ink-rgb),0.65)',
                            backgroundColor: ahpDark ? 'rgba(255,255,255,0.03)' : '#fff',
                            minWidth: 0,
                          }}
                        >
                          <ChipIcon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate">{s.label}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Variants + related, full width */}
                <div className="w-full min-w-0 flex flex-col gap-2">
                  {/* Helper: render a compact card for the right-side rows */}
                  {(() => {
                    const MINI_PHASE_COLORS = ['var(--civic-red)', 'var(--civic-navy)', 'var(--civic-gold)', 'var(--civic-money)', '#6B4C8A'];
                    // Base scenario name for context labels on variant cards
                    const baseFamilyName = (selectedScenario.severityLevel ?? 0) === 0
                      ? selectedScenario.name
                      : (familyScenarios.find(s => (s.severityLevel ?? 0) === 0)?.name || selectedScenario.name);

                    const renderMiniCard = (s: Scenario, idx: number, isSeverityVariant: boolean, isGrid: boolean = false) => {
                      try {
                      const Icon = s.icon || Activity;
                      const sevLvl = Math.min(Math.max(s.severityLevel ?? 0, 0), 4);
                      const cardColor = s.color || '#60a5fa';
                      // Phase breakdown — prefer pre-computed from index, fall back to procedures
                      const pb: Record<string, number> = s.phaseBreakdown && Object.keys(s.phaseBreakdown).length > 0
                        ? { ...s.phaseBreakdown }
                        : {};
                      if (Object.keys(pb).length === 0) {
                        (s.procedures || []).forEach(p => {
                          const ph = p.phase || 'Other';
                          pb[ph] = (pb[ph] || 0) + (p.estimatedCost || 0) * (p.quantity || 1);
                        });
                      }
                      const ph = (s.phases?.length ? s.phases : Object.keys(pb)).map((p: any) => typeof p === 'string' ? p : (p?.name || String(p)))
                        .sort((a, b) => (pb[b] || 0) - (pb[a] || 0));
                      const maxPh = Math.max(...Object.values(pb), 1);
                      const contributor = s.createdBy || 'Community';
                      const addedDate = s.createdAt ? new Date(s.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';

                      return (
                        <div
                          key={s.uuid}
                          onClick={() => handleScenarioSelect(s)}
                          className={`rounded-lg p-2.5 cursor-pointer hover:shadow-md transition-all ${isGrid ? 'w-full' : 'flex-shrink-0 w-[260px]'}`}
                          style={{
                            backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff',
                            border: isSeverityVariant ? `1px solid ${COMP_COLORS[sevLvl]}20` : `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.1)' : 'rgba(var(--civic-ink-rgb),0.06)'}`,
                            scrollSnapAlign: 'start',
                          }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = isSeverityVariant ? COMP_COLORS[sevLvl] : ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = isSeverityVariant ? `${COMP_COLORS[sevLvl]}20` : ahpDark ? 'rgba(var(--civic-gold-rgb),0.1)' : 'rgba(var(--civic-ink-rgb),0.06)'; }}
                        >
                          {/* Title row */}
                          <div className="mb-1.5">
                            {isSeverityVariant && (
                              <div className="text-[8px] truncate mb-0.5 pl-7" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>
                                {baseFamilyName}
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                            {isSeverityVariant ? (
                              <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0" style={{ backgroundColor: COMP_COLORS[sevLvl] }}>
                                {sevLvl}
                              </div>
                            ) : (
                              <div className="p-1 rounded flex-shrink-0" style={{ backgroundColor: `${cardColor}15` }}>
                                <Icon className="w-3.5 h-3.5" style={{ color: cardColor }} />
                              </div>
                            )}
                            <span className="text-[11px] font-medium truncate" style={{ color: ahpDark ? '#e5e7eb' : 'var(--civic-ink)' }}>
                              {isSeverityVariant ? s.name.replace(/^Vaginal Delivery (with |— )/, '') : s.name}
                            </span>
                            {!isSeverityVariant && (
                              <div className="flex-shrink-0 flex items-center gap-[2px] ml-auto" title={`${sevLvl === 0 ? 'No Complications' : `Severity ${sevLvl}/4`}`}>
                                {sevLvl === 0 ? (
                                  <span style={{ fontSize: 7, fontWeight: 600, color: COMP_COLORS[0] }}>Base</span>
                                ) : [1,2,3,4].map(lvl => (
                                  <div key={lvl} style={{
                                    width: 4, height: 8, borderRadius: 1,
                                    backgroundColor: lvl <= sevLvl ? COMP_COLORS[sevLvl] : ahpDark ? 'rgba(255,255,255,0.08)' : 'rgba(var(--civic-ink-rgb),0.08)',
                                  }} />
                                ))}
                              </div>
                            )}
                            </div>
                          </div>

                          {/* Cost + procs */}
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-mono text-[13px] font-semibold" style={{ color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)', fontFamily: serif }}>
                              ${Math.round(s.estimatedCost).toLocaleString()}
                            </span>
                            <span style={{ fontSize: 9, color: ahpDark ? 'var(--civic-muted)' : '#9ca3af' }}>{s.totalProcedures} procedures</span>
                          </div>

                          {/* Mini phase bars */}
                          {ph.length > 0 && (
                            <div className="space-y-0.5 mb-1.5">
                              {ph.slice(0, 4).map((phase, pIdx) => {
                                const pName = typeof phase === 'string' ? phase : (phase as any)?.name || String(phase);
                                const pCost = pb[pName] || pb[phase as any] || 0;
                                const pPct = Math.max((pCost / maxPh) * 100, 2);
                                return (
                                  <div key={pName} className="flex items-center gap-1.5">
                                    <span className="text-right flex-shrink-0 truncate" style={{ width: 65, fontSize: 8, color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>{pName}</span>
                                    <div className="flex-1 h-2 rounded-sm" style={{ backgroundColor: ahpDark ? 'rgba(255,255,255,0.06)' : 'rgba(var(--civic-ink-rgb),0.04)' }}>
                                      <div className="h-full rounded-sm" style={{ width: `${pPct}%`, backgroundColor: MINI_PHASE_COLORS[pIdx % MINI_PHASE_COLORS.length], minWidth: 2 }} />
                                    </div>
                                    <span className="text-[8px] font-mono flex-shrink-0" style={{ width: 30, textAlign: 'right' as const, color: ahpDark ? 'var(--civic-muted)' : '#9ca3af' }}>
                                      ${pCost >= 1000 ? `${(pCost / 1000).toFixed(0)}k` : Math.round(pCost)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Contributor + date */}
                          <div className="flex items-center justify-between pt-1" style={{ borderTop: `1px solid ${ahpDark ? 'rgba(255,255,255,0.04)' : 'rgba(var(--civic-ink-rgb),0.04)'}` }}>
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" style={{ color: ahpDark ? '#445566' : '#d1d5db' }} />
                              <span className="text-[9px]" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>
                                {contributor === 'Anonymous' ? 'Contributed anonymously' : contributor}
                              </span>
                            </div>
                            <span className="text-[9px]" style={{ color: ahpDark ? '#445566' : '#d1d5db' }}>{addedDate}</span>
                          </div>
                        </div>
                      );
                      } catch (err) { return <div key={s.uuid || idx} className="text-[9px] text-red-400 p-2">Error rendering card</div>; }
                    };

                    const scrollRow = (e: React.MouseEvent, dir: number) => {
                      const row = e.currentTarget.parentElement?.querySelector('.carousel-scroll') as HTMLDivElement;
                      if (row) row.scrollBy({ left: dir * 280, behavior: 'smooth' });
                    };

                    // Group family by severity level
                    const byLevel: Record<number, Scenario[]> = {};
                    familyScenarios.forEach(s => {
                      const lvl = Math.min(Math.max(s.severityLevel ?? 0, 0), 4);
                      if (!byLevel[lvl]) byLevel[lvl] = [];
                      byLevel[lvl].push(s);
                    });
                    // Which severity levels have scenarios? Include 0 so base is always visible
                    const activeLevels = [0, 1, 2, 3, 4].filter(lvl => byLevel[lvl]?.length);
                    // Track which scenario is shown per level (for stacked cycling)
                    const getLevelDisplay = (lvl: number) => {
                      const scenarios = byLevel[lvl] || [];
                      if (scenarios.length === 0) return null;
                      // Check for user override
                      const overrideUuid = cardSeverityOverrides[`sev_${lvl}`];
                      if (overrideUuid) {
                        const found = scenarios.find(s => s.uuid === overrideUuid);
                        if (found) return found;
                      }
                      return scenarios[0];
                    };

                    return (
                      <>
                        {/* ── Severity Variants — stacked level columns ── */}
                        {/* ── VARIANTS IN THIS FAMILY ── adopted from /home-beta
                         *   (InlineFamilyVariantsList). Replaces the previous
                         *   stacked-cards horizontal "Severity Variants" view,
                         *   which competed for screen space with the detail card
                         *   and showed at most one variant per bucket. The
                         *   vertical bucketed list shows ALL variants grouped by
                         *   BASE / LOW-COST / MID-COST / HIGH-COST / HIGHEST with
                         *   per-row delta-from-base and cost-range chips up top
                         *   — 3× density on the same data and matches the
                         *   canonical home-beta surface. Cost-quartile language
                         *   is the unified vocabulary post Ciel's label pass. */}
                        {familyScenarios.length > 0 && (() => {
                          const baseCost = (byLevel[0]?.[0]?.estimatedCost) || selectedScenario.estimatedCost || 0;
                          const levelRanges = [0, 1, 2, 3, 4].map((lv) => {
                            const costs = (byLevel[lv] || []).map((s) => s.estimatedCost || 0).filter((c) => c > 0);
                            return { min: costs.length ? Math.min(...costs) : 0, max: costs.length ? Math.max(...costs) : 0 };
                          });
                          const fmt = (n: number) =>
                            n >= 1000 ? `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `$${Math.round(n).toLocaleString()}`;
                          const totalVariants = familyScenarios.length;
                          return (
                            <div
                              className="rounded-lg overflow-hidden"
                              style={{
                                background: ahpDark ? 'var(--civic-card-dark)' : '#fff',
                                border: `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.18)' : 'rgba(var(--civic-ink-rgb),0.08)'}`,
                              }}
                            >
                              {/* Header — root family name + variant count + open-explorer */}
                              <div className="px-4 py-3" style={{ borderBottom: `1px solid ${ahpDark ? 'rgba(255,255,255,0.06)' : 'rgba(var(--civic-ink-rgb),0.06)'}` }}>
                                <div className="flex items-start justify-between gap-3">
                                  <div className="min-w-0">
                                    <div style={{ fontSize: 10, fontWeight: 700, color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                                      {t('homepage:homeBeta.familyModal.variantsInFamily', { defaultValue: 'Variants in this family' })}
                                    </div>
                                    <div className="truncate" style={{ fontFamily: serif, fontSize: '1.05rem', color: ahpDark ? '#e5e7eb' : 'var(--civic-ink)', marginTop: 2, lineHeight: 1.2 }}>
                                      {familyRootName}
                                    </div>
                                    <div style={{ fontSize: 11, color: ahpDark ? 'var(--civic-muted)' : '#6b7280', marginTop: 4 }}>
                                      <span style={{ color: COMP_COLORS[0], fontWeight: 700 }}>{fmt(baseCost)}</span>
                                      {' '}base · {totalVariants} {totalVariants === 1 ? 'variant' : 'variants'}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setShowFamilyModal(true)}
                                    className="hover:opacity-80 transition-opacity flex-shrink-0"
                                    style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: ahpDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)',
                                      background: 'transparent',
                                      border: `1px solid ${ahpDark ? 'rgba(var(--civic-navy-soft-rgb),0.4)' : 'rgba(var(--civic-navy-rgb),0.4)'}`,
                                      padding: '6px 12px',
                                      borderRadius: 4,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {t('homepage:homeBeta.familyModal.openExplorer', { defaultValue: 'Open full explorer' })}
                                  </button>
                                </div>
                              </div>
                              {/* Cost-range chip strip across the 5 quartile levels */}
                              <div className="flex gap-1 px-4 py-3" style={{ borderBottom: `1px solid ${ahpDark ? 'rgba(255,255,255,0.06)' : 'rgba(var(--civic-ink-rgb),0.06)'}` }}>
                                {[0, 1, 2, 3, 4].map((lv) => {
                                  const count = (byLevel[lv] || []).length;
                                  const color = COMP_COLORS[lv];
                                  const range = levelRanges[lv];
                                  const disabled = count === 0;
                                  return (
                                    <button
                                      key={lv}
                                      type="button"
                                      onClick={() => {
                                        if (disabled) return;
                                        // Expand the collapsed variant lists first, then scroll
                                        // to the tapped level's group.
                                        const scrollToBucket = () => {
                                          const el = document.getElementById(`variants-bucket-${lv}`);
                                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                        };
                                        if (!variantsExpanded) {
                                          setVariantsExpanded(true);
                                          setTimeout(scrollToBucket, 300);
                                        } else {
                                          scrollToBucket();
                                        }
                                      }}
                                      disabled={disabled}
                                      className={!disabled ? 'transition-transform hover:-translate-y-0.5' : ''}
                                      style={{
                                        flex: 1,
                                        background: !disabled ? `${color}12` : 'rgba(128,128,128,0.04)',
                                        border: `1px solid ${!disabled ? `${color}33` : 'rgba(128,128,128,0.12)'}`,
                                        borderRadius: 4,
                                        padding: '6px 6px',
                                        textAlign: 'left',
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                        opacity: disabled ? 0.5 : 1,
                                      }}
                                    >
                                      <div className="flex items-center gap-1">
                                        <span
                                          className="inline-flex items-center justify-center text-[9px] font-bold flex-shrink-0"
                                          style={{ width: 13, height: 13, borderRadius: '50%', backgroundColor: !disabled ? color : '#cbd5e1', color: '#fff' }}
                                        >
                                          {lv}
                                        </span>
                                        <span style={{ fontSize: 9, fontWeight: 600, color: !disabled ? color : (ahpDark ? 'var(--civic-muted-2)' : '#9ca3af'), letterSpacing: 0.3 }}>
                                          {!disabled ? count : '—'}
                                        </span>
                                      </div>
                                      <div style={{ fontSize: 9, color: !disabled ? color : (ahpDark ? 'var(--civic-muted-2)' : '#9ca3af'), marginTop: 2, fontWeight: 600, fontFamily: 'ui-monospace, monospace' }}>
                                        {!disabled ? (lv === 0 ? fmt(baseCost) : (range.min === range.max ? fmt(range.min) : `${fmt(range.min)}–${fmt(range.max)}`)) : '—'}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                              {/* Disambiguation microcopy — these labels are cost-quartile,
                               *  NOT clinical urgency. Same line as home-beta uses for the
                               *  same panel. */}
                              <div className="px-4 pt-2 pb-1" style={{ fontSize: 10, color: ahpDark ? 'var(--civic-muted)' : '#6b7280', lineHeight: 1.4, fontStyle: 'italic' }}>
                                <Trans
                                  i18nKey="homepage:homeBeta.severityLadder.microcopyRich"
                                  components={{ b: <span style={{ fontWeight: 600, color: ahpDark ? '#e5e7eb' : 'var(--civic-ink)', fontStyle: 'normal' }} /> }}
                                />
                              </div>
                              {/* Expand/collapse — the full lists are reference
                                  material; the chip strip above is the summary. */}
                              <button
                                type="button"
                                onClick={() => setVariantsExpanded(v => !v)}
                                className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-semibold transition-colors"
                                style={{ color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)', borderTop: `1px solid ${ahpDark ? 'rgba(255,255,255,0.06)' : 'rgba(var(--civic-ink-rgb),0.06)'}` }}
                              >
                                {variantsExpanded
                                  ? t('scenarios:related.hideVariants', { defaultValue: 'Hide variant list' })
                                  : t('scenarios:related.showVariants', { defaultValue: 'Show all {{count}} variants', count: familyScenarios.length + 1 })}
                                <ChevronDown className="w-3.5 h-3.5 transition-transform" style={{ transform: variantsExpanded ? 'rotate(180deg)' : 'none' }} />
                              </button>
                              {/* Per-level bucketed variant lists */}
                              {variantsExpanded && (
                              <div className="px-4 py-2 max-h-[440px] overflow-y-auto">
                                {[0, 1, 2, 3, 4].map((lv) => {
                                  const variants = byLevel[lv] || [];
                                  if (variants.length === 0) return null;
                                  const color = COMP_COLORS[lv];
                                  const labelKey = ['base', 'lowCost', 'midCost', 'highCost', 'highest'][lv];
                                  const upperLabel = t(`common:costQuartile.${labelKey}`).toUpperCase();
                                  return (
                                    <div key={lv} id={`variants-bucket-${lv}`} className="mb-3 last:mb-0">
                                      <div className="flex items-center gap-2 mb-1.5">
                                        <span
                                          className="inline-flex items-center justify-center text-[9px] font-bold"
                                          style={{ width: 16, height: 16, borderRadius: '50%', background: color, color: '#fff' }}
                                        >
                                          {lv}
                                        </span>
                                        <span style={{ fontSize: 10, fontWeight: 700, color, letterSpacing: 0.4 }}>
                                          {upperLabel}
                                        </span>
                                        {lv === 0 && (
                                          <span style={{ fontSize: 10, fontWeight: 500, color: ahpDark ? 'var(--civic-muted)' : '#6b7280', fontStyle: 'italic' }}>
                                            {t('homepage:homeBeta.familyModal.withNoComplications', { defaultValue: '(with no complications)' })}
                                          </span>
                                        )}
                                        <span style={{ fontSize: 10, color: ahpDark ? 'var(--civic-muted)' : '#6b7280' }}>
                                          {variants.length} {variants.length === 1 ? 'variant' : 'variants'}
                                        </span>
                                        <div className="h-px flex-1" style={{ background: `${color}22` }} />
                                      </div>
                                      <ul className="space-y-1">
                                        {variants.map((v) => {
                                          const variantName = v.familyVariantLabel && v.familyVariantLabel !== 'uncomplicated'
                                            ? v.familyVariantLabel
                                            : (familyRootName
                                                ? v.name.replace(`${familyRootName} — `, '').replace(`${familyRootName}: `, '').replace(`${familyRootName} `, '')
                                                : v.name);
                                          const cost = v.estimatedCost || 0;
                                          const delta = cost - baseCost;
                                          const isCurrent = v.uuid === selectedScenario.uuid;
                                          return (
                                            <li key={v.uuid}>
                                              <button
                                                type="button"
                                                onClick={() => handleScenarioSelect(v)}
                                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded transition-colors text-left"
                                                style={{
                                                  background: isCurrent ? `${color}14` : 'transparent',
                                                  border: isCurrent ? `1px solid ${color}55` : '1px solid transparent',
                                                  color: ahpDark ? '#e5e7eb' : 'var(--civic-ink)',
                                                  cursor: 'pointer',
                                                }}
                                                onMouseEnter={(e) => { if (!isCurrent) (e.currentTarget as HTMLButtonElement).style.background = ahpDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'; }}
                                                onMouseLeave={(e) => { if (!isCurrent) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                                              >
                                                <span style={{ color, fontSize: 9, opacity: 0.85 }}>●</span>
                                                <span className="flex-1 min-w-0" style={{ fontSize: 12, lineHeight: 1.3 }}>
                                                  <span className="truncate block">
                                                    {variantName || v.name}
                                                    {lv === 0 && (
                                                      <span className="ml-2 text-[9px] font-semibold uppercase" style={{ color: ahpDark ? 'var(--civic-muted)' : '#6b7280' }}>
                                                        {t('common:costQuartile.base').toLowerCase()}
                                                      </span>
                                                    )}
                                                    {isCurrent && (
                                                      <span className="ml-2 text-[9px] font-semibold uppercase" style={{ color }}>
                                                        current
                                                      </span>
                                                    )}
                                                  </span>
                                                </span>
                                                <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, fontWeight: 600, color: lv === 0 ? color : (ahpDark ? '#e5e7eb' : 'var(--civic-ink)'), flexShrink: 0 }}>
                                                  {fmt(cost)}
                                                </span>
                                                {lv !== 0 && delta > 0 && (
                                                  <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 10, color: 'var(--civic-red)', flexShrink: 0, width: 60, textAlign: 'right' }}>
                                                    +{fmt(delta)}
                                                  </span>
                                                )}
                                              </button>
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  );
                                })}
                              </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* ── Severity Variants horizontal card row ──
                         *   Restored 2026-05-26 per Master's request. Sits
                         *   BETWEEN the vertical variants panel above and the
                         *   Related Scenarios row below. Renders one
                         *   stacked-card column per severity level with the
                         *   first matched variant on top (cyclable when count
                         *   > 1) — same renderer the family ladder uses on
                         *   /home-beta. The vertical panel above is the
                         *   dense reference; this row is the at-a-glance
                         *   "what does each severity look like" preview.
                         *   Collapsed with the variant lists (2026-07-11) —
                         *   expands via the panel's Show-all toggle. */}
                        {variantsExpanded && familyScenarios.length > 0 && (
                          <div className="mt-4">
                            <div className="flex items-center gap-1.5 mb-2 px-1">
                              <span style={{ fontSize: 10, fontWeight: 600, color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)', letterSpacing: '0.5px', textTransform: 'uppercase' as const }}>
                                {t('scenarios:related.severityVariants', { defaultValue: 'Severity Variants' })}
                              </span>
                              <span className="text-[10px]" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>
                                {familyScenarios.length} {familyScenarios.length === 1 ? 'variation' : 'variations'}
                              </span>
                              <div className="h-px flex-1" style={{ backgroundColor: ahpDark ? 'rgba(var(--civic-gold-rgb),0.18)' : 'rgba(var(--civic-red-rgb),0.12)' }} />
                              <button
                                type="button"
                                onClick={() => setShowFamilyModal(true)}
                                className="text-[10px] font-medium hover:opacity-70 transition-opacity"
                                style={{ color: ahpDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)' }}
                              >
                                {t('scenarios:related.viewAll', { defaultValue: 'View All' })}
                              </button>
                            </div>
                            {/* One column per severity level. Horizontal scroll
                             *  on narrow viewports so the columns don't squeeze. */}
                            <div className="flex gap-0 items-stretch overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
                              {activeLevels.map((lvl, colIdx) => {
                                const scenarios = byLevel[lvl] || [];
                                const displayScenario = getLevelDisplay(lvl)!;
                                const Icon = displayScenario.icon || Activity;
                                const color = COMP_COLORS[lvl];
                                const count = scenarios.length;
                                const currentIdx = scenarios.findIndex(s => s.uuid === displayScenario.uuid);

                                // Phase breakdown for the mini phase bars
                                const pb: Record<string, number> = displayScenario.phaseBreakdown && Object.keys(displayScenario.phaseBreakdown).length > 0
                                  ? { ...displayScenario.phaseBreakdown } : {};
                                if (Object.keys(pb).length === 0) {
                                  (displayScenario.procedures || []).forEach(p => {
                                    const ph = p.phase || 'Other';
                                    pb[ph] = (pb[ph] || 0) + (p.estimatedCost || 0) * (p.quantity || 1);
                                  });
                                }
                                const phs = (displayScenario.phases?.length ? displayScenario.phases : Object.keys(pb)).map((p: any) => typeof p === 'string' ? p : (p?.name || String(p)))
                                  .sort((a, b) => (pb[b] || 0) - (pb[a] || 0));
                                const maxPh = Math.max(...Object.values(pb), 1);
                                const MINI_PHASE_COLORS = ['var(--civic-red)', 'var(--civic-navy)', 'var(--civic-gold)', 'var(--civic-money)', '#6B4C8A'];

                                return (
                                  <div key={lvl} className="flex items-stretch flex-shrink-0" style={{ flex: 1, minWidth: 180 }}>
                                    {colIdx > 0 && (
                                      <div className="flex flex-col items-center justify-center px-1 flex-shrink-0" style={{ width: 20 }}>
                                        <ChevronRight className="w-4 h-4" style={{ color: COMP_COLORS[activeLevels[colIdx - 1]], opacity: 0.4 }} />
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0 flex flex-col">
                                      <div className="flex items-center gap-1.5 mb-1.5 px-1">
                                        {lvl === 0 ? (
                                          <span style={{ fontSize: 9, fontWeight: 600, color: COMP_COLORS[0], backgroundColor: 'rgba(var(--civic-money-rgb),0.10)', padding: '2px 6px', borderRadius: 4 }}>
                                            {COMP_LABELS[0]}
                                          </span>
                                        ) : (
                                          <>
                                            <div className="flex gap-[2px]">
                                              {[1, 2, 3, 4].map(g => (
                                                <div key={g} style={{
                                                  width: 5, height: 10, borderRadius: 1.5,
                                                  backgroundColor: g <= lvl ? color : (ahpDark ? 'rgba(255,255,255,0.08)' : 'rgba(var(--civic-ink-rgb),0.08)'),
                                                }} />
                                              ))}
                                            </div>
                                            <span style={{ fontSize: 10, fontWeight: 700, color }}>{COMP_LABELS[lvl]}</span>
                                          </>
                                        )}
                                        {count > 1 && (
                                          <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full" style={{
                                            backgroundColor: `${color}12`, color, border: `1px solid ${color}25`,
                                          }}>
                                            {count}
                                          </span>
                                        )}
                                      </div>
                                      <div className="relative cursor-pointer" onClick={() => handleScenarioSelect(displayScenario)}>
                                        {/* Stack shadow when there are siblings at this level */}
                                        {count > 2 && (
                                          <div className="absolute -bottom-2 left-2 right-2 rounded-lg" style={{
                                            height: 8, backgroundColor: `${color}08`, border: `1px solid ${color}15`,
                                          }} />
                                        )}
                                        {count > 1 && (
                                          <div className="absolute -bottom-1 left-1 right-1 rounded-lg" style={{
                                            height: 8, backgroundColor: `${color}12`, border: `1px solid ${color}20`,
                                          }} />
                                        )}
                                        <div
                                          className="relative rounded-lg p-3 hover:shadow-md transition-all"
                                          style={{
                                            backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff',
                                            border: `1px solid ${color}25`,
                                          }}
                                          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = color; }}
                                          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.borderColor = `${color}25`; }}
                                        >
                                          <div className="mb-2">
                                            <div className="text-[9px] truncate mb-0.5 pl-8" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>
                                              {familyRootName}
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: color }}>
                                                {lvl}
                                              </div>
                                              <span className="text-[12px] font-medium truncate" style={{ color: ahpDark ? '#e5e7eb' : 'var(--civic-ink)' }}>
                                                {(displayScenario.familyVariantLabel && displayScenario.familyVariantLabel !== 'uncomplicated')
                                                  ? displayScenario.familyVariantLabel
                                                  : displayScenario.name.replace(/^.*?(with |— )/, '')}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="flex items-center justify-between mb-2">
                                            <span className="font-mono text-[15px] font-semibold" style={{ color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)', fontFamily: "'Instrument Serif', Georgia, serif" }}>
                                              ${Math.round(displayScenario.estimatedCost || 0).toLocaleString()}
                                            </span>
                                            <span style={{ fontSize: 9, color: ahpDark ? 'var(--civic-muted)' : '#9ca3af' }}>{displayScenario.totalProcedures} procs</span>
                                          </div>
                                          {phs.length > 0 && (
                                            <div className="space-y-0.5 mb-2">
                                              {phs.slice(0, 4).map((phase, pIdx) => {
                                                const pName = typeof phase === 'string' ? phase : (phase as any)?.name || String(phase);
                                                const pCost = pb[pName] || pb[phase as any] || 0;
                                                const pPct = Math.max((pCost / maxPh) * 100, 2);
                                                return (
                                                  <div key={pName} className="flex items-center gap-1.5">
                                                    <span className="text-right flex-shrink-0 truncate text-[8px]" style={{ width: 55, color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>{pName}</span>
                                                    <div className="flex-1 h-2.5 rounded-sm" style={{ backgroundColor: ahpDark ? 'rgba(255,255,255,0.06)' : 'rgba(var(--civic-ink-rgb),0.04)' }}>
                                                      <div className="h-full rounded-sm" style={{ width: `${pPct}%`, backgroundColor: MINI_PHASE_COLORS[pIdx % MINI_PHASE_COLORS.length], minWidth: 2 }} />
                                                    </div>
                                                    <span className="text-[8px] font-mono flex-shrink-0" style={{ width: 30, textAlign: 'right' as const, color: ahpDark ? 'var(--civic-muted)' : '#9ca3af' }}>
                                                      ${pCost >= 1000 ? `${(pCost / 1000).toFixed(0)}k` : Math.round(pCost)}
                                                    </span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          )}
                                          {count > 1 && (
                                            <div className="flex items-center justify-between pt-1.5" style={{ borderTop: `1px solid ${ahpDark ? 'rgba(255,255,255,0.06)' : 'rgba(var(--civic-ink-rgb),0.06)'}` }}>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const prevIdx = (currentIdx - 1 + count) % count;
                                                  setCardSeverityOverrides(prev => ({ ...prev, [`sev_${lvl}`]: scenarios[prevIdx].uuid }));
                                                }}
                                                className="p-0.5 rounded transition-colors"
                                                style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}
                                              >
                                                <ChevronLeft className="w-3.5 h-3.5" />
                                              </button>
                                              <span className="text-[9px] font-medium" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>
                                                {currentIdx + 1} of {count}
                                              </span>
                                              <button
                                                type="button"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const nextIdx = (currentIdx + 1) % count;
                                                  setCardSeverityOverrides(prev => ({ ...prev, [`sev_${lvl}`]: scenarios[nextIdx].uuid }));
                                                }}
                                                className="p-0.5 rounded transition-colors"
                                                style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}
                                              >
                                                <ChevronRight className="w-3.5 h-3.5" />
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {count > 1 && <div style={{ height: count > 2 ? 10 : 6 }} />}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Related / Severity-Filtered Scenarios */}
                        {activeSeverityFilter !== null ? (
                          /* ── Severity filter active: grid of all scenarios at this level ── */
                          <div className="mt-3">
                            <div className="flex items-center gap-2 mb-2 px-1">
                              <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold" style={{ backgroundColor: COMP_COLORS[activeSeverityFilter] }}>
                                {activeSeverityFilter}
                              </div>
                              <span style={{ fontSize: 10, fontWeight: 600, color: COMP_COLORS[activeSeverityFilter], letterSpacing: '0.5px', textTransform: 'uppercase' as const }}>
                                {COMP_LABELS[activeSeverityFilter]} Scenarios
                              </span>
                              <span className="text-[9px]" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>{severityFilteredScenarios.length}</span>
                              <div className="h-px flex-1" style={{ backgroundColor: ahpDark ? 'rgba(255,255,255,0.08)' : 'rgba(var(--civic-ink-rgb),0.08)' }} />
                              <button
                                onClick={() => {
                                  const basePath = `/scenarios/${selectedScenario.uuid}/${selectedScenario.slug}`;
                                  const statePart = stateParam ? `/${stateParam}` : '';
                                  navigate(`${basePath}${statePart}`, { replace: true });
                                }}
                                className="text-[9px] px-2 py-0.5 rounded-full transition-colors"
                                style={{ color: ahpDark ? 'var(--civic-muted)' : '#6b7280', border: `1px solid ${ahpDark ? 'rgba(255,255,255,0.12)' : 'rgba(var(--civic-ink-rgb),0.12)'}` }}
                              >
                                Clear
                              </button>
                            </div>
                            {severityFilteredScenarios.length > 0 ? (
                              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                {severityFilteredScenarios.map((s, i) => renderMiniCard(s, i, false, true))}
                              </div>
                            ) : (
                              <div className="text-center py-4 text-[11px]" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>
                                No other scenarios at this severity level
                              </div>
                            )}
                          </div>
                        ) : relatedScenarios.length > 0 ? (
                          /* ── Tag-based related scenarios — restored to the
                           *   full mini-card carousel per Master's 2026-05-26
                           *   request. The pill-strip demotion lost too much
                           *   information (phase bars, cost prominence, family
                           *   context). renderMiniCard is the same renderer
                           *   used by the activeSeverityFilter grid above, so
                           *   the two surfaces stay visually consistent. */
                          <div className="mt-4">
                            <div className="flex items-center gap-1.5 mb-1.5 px-1">
                              <span style={{ fontSize: 9, fontWeight: 600, color: ahpDark ? 'var(--civic-muted)' : '#6b7280', letterSpacing: '0.5px', textTransform: 'uppercase' as const }}>
                                {t('scenarios:related.title', { defaultValue: 'Related Scenarios' })}
                              </span>
                              <span className="text-[9px]" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>{relatedScenarios.length}</span>
                              <div className="h-px flex-1" style={{ backgroundColor: ahpDark ? 'rgba(255,255,255,0.08)' : 'rgba(var(--civic-ink-rgb),0.08)' }} />
                            </div>
                            <div className="relative group/rel">
                              <button
                                type="button"
                                onClick={(e) => scrollRow(e, -1)}
                                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover/rel:opacity-100 transition-opacity shadow-lg"
                                style={{
                                  backgroundColor: ahpDark ? 'var(--civic-navy-deep)' : '#fff',
                                  border: `1px solid ${ahpDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                }}
                                aria-label={t('scenarios:related.scrollPrev', { defaultValue: 'Scroll previous' }) as string}
                              >
                                <ChevronLeft className="w-4 h-4" style={{ color: ahpDark ? 'var(--civic-muted)' : '#6b7280' }} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => scrollRow(e, 1)}
                                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover/rel:opacity-100 transition-opacity shadow-lg"
                                style={{
                                  backgroundColor: ahpDark ? 'var(--civic-navy-deep)' : '#fff',
                                  border: `1px solid ${ahpDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                                }}
                                aria-label={t('scenarios:related.scrollNext', { defaultValue: 'Scroll next' }) as string}
                              >
                                <ChevronRight className="w-4 h-4" style={{ color: ahpDark ? 'var(--civic-muted)' : '#6b7280' }} />
                              </button>
                              <div
                                className="carousel-scroll flex gap-2 overflow-x-auto pb-1"
                                style={{ scrollbarWidth: 'none', scrollSnapType: 'x mandatory' }}
                              >
                                {relatedScenarios.map((s, i) => renderMiniCard(s, i + familyScenarios.length, false))}
                                <div
                                  className="flex-shrink-0 rounded-lg p-2.5 w-[180px] cursor-pointer hover:shadow-md transition-all flex items-center gap-2"
                                  style={{
                                    background: 'linear-gradient(135deg, var(--civic-navy) 0%, var(--civic-navy-deep) 100%)',
                                    border: '1px solid rgba(var(--civic-navy-rgb),0.3)',
                                    scrollSnapAlign: 'start',
                                  }}
                                  onClick={() => setShowCreateDialog(true)}
                                >
                                  <Plus className="w-4 h-4 flex-shrink-0 text-white/80" />
                                  <div>
                                    <div className="text-[11px] font-medium text-white">
                                      {t('scenarios:related.buildYourPlan', { defaultValue: 'Build Your Plan' })}
                                    </div>
                                    <div className="text-[9px] text-white/50 mt-0.5">
                                      {t('scenarios:related.customScenario', { defaultValue: 'Custom scenario' })}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          );
          // Hybrid (2026-07-11): the section renders INLINE on every viewport
          // (one continuous scroll; the rail scroll-spies it), and on mobile
          // the rail/button presses ALSO open it as a full-screen modal — the
          // fluid native feel. Modal mounts children only while open.
          return (
            <>
              {severityEl}
              {isMobileViewport && (
                <ScenarioSectionModal open={openSection === 'severity-variants'} onClose={closeSection} urlManaged dark={ahpDark} scenarioName={selectedScenario.name} sectionTitle={t('scenarios:familyExplorer.sectionSeverityVariants')}>
                  {severityEl}
                </ScenarioSectionModal>
              )}
            </>
          );
        })()}

        {/* Family Explorer Modal — fullscreen breakdown by severity */}
        {showFamilyModal && selectedScenario?.scenarioFamilyId && (() => {
          const COMP_COLORS_M = ['var(--civic-money)', '#5a9a3c', 'var(--civic-gold)', '#e67e22', 'var(--civic-red)'];
          const COMP_LABELS_M = [
            t('common:costQuartile.base'),
            `${t('common:costQuartile.lowCost')} — ${t('common:costQuartile.percentile.p25')}`,
            `${t('common:costQuartile.midCost')} — ${t('common:costQuartile.percentile.p50')}`,
            `${t('common:costQuartile.highCost')} — ${t('common:costQuartile.percentile.p75')}`,
            `${t('common:costQuartile.highest')} — ${t('common:costQuartile.percentile.p100')}`,
          ];
          const serifM = "'Instrument Serif', Georgia, serif";
          const allFamilyM = [{ ...selectedScenario, severityLevel: familySeverityMap.get(selectedScenario.uuid) ?? selectedScenario.severityLevel ?? 0 }, ...familyScenarios].sort((a, b) => (a.severityLevel ?? 0) - (b.severityLevel ?? 0));
          // Group by severity
          const byLevelM: Record<number, Scenario[]> = {};
          allFamilyM.forEach(s => {
            const lvl = Math.min(Math.max(s.severityLevel ?? 0, 0), 4);
            if (!byLevelM[lvl]) byLevelM[lvl] = [];
            byLevelM[lvl].push(s);
          });

          // Free-text filter across the family (name / description / variant label).
          const fq = familySearch.trim().toLowerCase();
          const matchFamily = (s: Scenario) =>
            !fq ||
            (s.name || "").toLowerCase().includes(fq) ||
            (s.description || "").toLowerCase().includes(fq) ||
            ((s as any).familyVariantLabel || "").toLowerCase().includes(fq);
          const matchCount = fq ? allFamilyM.filter(matchFamily).length : allFamilyM.length;

          const body = (
            <>
                <div className="p-4 sm:p-6 pb-2" style={{ backgroundColor: ahpDark ? 'var(--civic-navy-deeper)' : undefined }}>
                  {/* Header */}
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <h2 style={{ fontFamily: serifM, fontSize: 28, fontWeight: 400, color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)' }}>
                        {t('scenarios:familyExplorer.title')}
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        {fq
                          ? t('scenarios:familyExplorer.matchSummary', { count: matchCount, total: allFamilyM.length, query: familySearch.trim() })
                          : t('scenarios:familyExplorer.countSummary', { count: allFamilyM.length, levels: Object.keys(byLevelM).length })}
                      </p>
                    </div>
                  </div>
                  {/* Search — filter variants within this family (handy for 70+ member families) */}
                  <div className="relative mt-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }} />
                    <input
                      type="text"
                      value={familySearch}
                      onChange={(e) => setFamilySearch(e.target.value)}
                      placeholder={t('scenarios:familyExplorer.searchPlaceholder')}
                      className="w-full rounded-lg pl-9 pr-9 py-2.5 text-sm outline-none"
                      style={{
                        backgroundColor: ahpDark ? 'var(--civic-bg-dark)' : 'var(--civic-cream)',
                        border: `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.2)' : 'rgba(var(--civic-ink-rgb),0.12)'}`,
                        color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)',
                        fontFamily: "'Instrument Sans', system-ui, sans-serif",
                      }}
                    />
                    {familySearch && (
                      <button
                        onClick={() => setFamilySearch("")}
                        aria-label={t('scenarios:familyExplorer.clearSearch')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full"
                        style={{ color: ahpDark ? 'var(--civic-muted)' : '#9ca3af' }}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {/* Summary bar with cost ranges */}
                  {(() => {
                    const baseCost = (byLevelM[0] || [])[0]?.estimatedCost || 0;
                    // Get all complication costs (levels 1-4) sorted
                    const compCosts = allFamilyM
                      .filter(s => (s.severityLevel ?? 0) > 0)
                      .map(s => s.estimatedCost)
                      .sort((a, b) => a - b);
                    // Calculate quartile thresholds from actual data
                    const q = (arr: number[], p: number) => {
                      if (arr.length === 0) return 0;
                      const idx = Math.max(0, Math.ceil(arr.length * p) - 1);
                      return arr[idx];
                    };
                    const ranges = [
                      { label: t('common:costQuartile.noComplications'), pct: '', min: baseCost, max: baseCost },
                      { label: t('common:costQuartile.lowCost'),  pct: t('common:costQuartile.percentile.p25'),  min: compCosts[0] || 0,    max: q(compCosts, 0.25) },
                      { label: t('common:costQuartile.midCost'),  pct: t('common:costQuartile.percentile.p50'),  min: q(compCosts, 0.25),    max: q(compCosts, 0.50) },
                      { label: t('common:costQuartile.highCost'), pct: t('common:costQuartile.percentile.p75'),  min: q(compCosts, 0.50),    max: q(compCosts, 0.75) },
                      { label: t('common:costQuartile.highest'),  pct: t('common:costQuartile.percentile.p100'), min: q(compCosts, 0.75),    max: q(compCosts, 1.0) },
                    ];
                    const fmt = (n: number) => n >= 1000 ? `$${(n / 1000).toFixed(0)}k` : `$${Math.round(n).toLocaleString()}`;

                    return (
                      <div className="mt-3 mb-4">
                        <div className="flex gap-1.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                          {[0, 1, 2, 3, 4].map(level => {
                            const count = (byLevelM[level] || []).length;
                            const r = ranges[level];
                            return (
                              <div key={level} className="flex-1 min-w-[100px] rounded-lg p-2" style={{
                                backgroundColor: count > 0 ? `${COMP_COLORS_M[level]}06` : 'rgba(var(--civic-ink-rgb),0.02)',
                                border: `1px solid ${count > 0 ? `${COMP_COLORS_M[level]}20` : 'rgba(var(--civic-ink-rgb),0.06)'}`,
                              }}>
                                <div className="flex items-center gap-1.5 mb-1">
                                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0" style={{
                                    backgroundColor: count > 0 ? COMP_COLORS_M[level] : '#d1d5db',
                                  }}>
                                    {level}
                                  </div>
                                  <span className="text-[11px] font-semibold" style={{ color: count > 0 ? COMP_COLORS_M[level] : '#9ca3af' }}>
                                    {r.label}
                                  </span>
                                  <span className="text-[10px] text-gray-400 ml-auto">{count}</span>
                                </div>
                                {level === 0 ? (
                                  <div className="text-[10px] text-gray-500">
                                    <span className="font-mono font-semibold" style={{ color: COMP_COLORS_M[0] }}>{fmt(baseCost)}</span>
                                    <span className="ml-1">{t('scenarios:familyExplorer.basePrice')}</span>
                                  </div>
                                ) : (
                                  <div className="text-[10px] text-gray-500">
                                    <div className="font-mono font-semibold" style={{ color: COMP_COLORS_M[level] }}>
                                      {r.min === r.max ? fmt(r.min) : `${fmt(r.min)}–${fmt(r.max)}`}
                                    </div>
                                    <div className="text-[9px] text-gray-400 mt-0.5">{r.pct}</div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[10px] mt-2 px-1" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>
                          {t('scenarios:familyExplorer.quartileNote', { count: allFamilyM.length })}
                        </p>
                      </div>
                    );
                  })()}
                </div>

                {/* Severity sections */}
                <div className="px-4 sm:px-6 pb-6 space-y-5" style={{ backgroundColor: ahpDark ? 'var(--civic-navy-deeper)' : undefined }}>
                  {[0, 1, 2, 3, 4].map(level => {
                    const scenarios = (byLevelM[level] || []).filter(matchFamily);
                    // While searching, hide levels with no matches entirely (the
                    // "no scenarios yet / + Create" placeholder is only for the
                    // unfiltered view).
                    if (scenarios.length === 0 && fq) return null;
                    if (scenarios.length === 0) return (
                      <div key={level} id={`fam-level-${level}`} className="flex items-center gap-3 p-3 rounded-lg scroll-mt-4" style={{ backgroundColor: ahpDark ? 'rgba(255,255,255,0.02)' : 'rgba(var(--civic-ink-rgb),0.02)', border: `1px dashed ${ahpDark ? 'rgba(255,255,255,0.08)' : 'rgba(var(--civic-ink-rgb),0.1)'}` }}>
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: '#d1d5db' }}>{level}</div>
                        <div>
                          <div className="text-sm font-medium" style={{ color: ahpDark ? 'var(--civic-muted)' : '#9ca3af' }}>{COMP_LABELS_M[level]}</div>
                          <div className="text-xs" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#d1d5db' }}>{t('scenarios:familyExplorer.noScenariosYet')}</div>
                        </div>
                        <button
                          onClick={() => { setShowFamilyModal(false); setShowCreateDialog(true); }}
                          className="ml-auto text-xs font-medium px-3 py-1.5 rounded-lg transition-colors hover:opacity-80"
                          style={{ backgroundColor: `${COMP_COLORS_M[level]}12`, color: COMP_COLORS_M[level], border: `1px solid ${COMP_COLORS_M[level]}30` }}
                        >
                          {t('scenarios:familyExplorer.createShort')}
                        </button>
                      </div>
                    );
                    return (
                      <div key={level} id={`fam-level-${level}`} className="scroll-mt-4">
                        {/* Level header */}
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-xs" style={{ backgroundColor: COMP_COLORS_M[level] }}>
                            {level}
                          </div>
                          <div>
                            <span className="text-sm font-semibold" style={{ color: COMP_COLORS_M[level] }}>{COMP_LABELS_M[level]}</span>
                            <span className="text-xs ml-2" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>{t('scenarios:familyExplorer.scenarioCount', { count: scenarios.length })}</span>
                          </div>
                          <div className="h-px flex-1" style={{ backgroundColor: `${COMP_COLORS_M[level]}20` }} />
                        </div>
                        {/* Scenario cards at this level */}
                        <div className="space-y-2">
                          {scenarios.map(s => {
                            const isCurrent = s.uuid === selectedScenario.uuid;
                            // Sort codes by type priority: DRG > CPT > HCPCS > REV > DRUG/NDC > other
                            const TYPE_ORDER: Record<string, number> = { 'MS-DRG': 0, 'DRG': 0, 'APR-DRG': 1, 'CPT': 2, 'HCPCS': 3, 'REV': 4, 'DRUG': 5, 'NDC': 5 };
                            const codeSet = new Set<string>();
                            const sortedCodes = (s.procedures || [])
                              .map(p => ({ label: `${p.codeType}:${p.code}`, type: p.codeType, order: TYPE_ORDER[p.codeType] ?? 6 }))
                              .filter(c => { if (codeSet.has(c.label)) return false; codeSet.add(c.label); return true; })
                              .sort((a, b) => a.order - b.order);
                            // Pick a diverse sample: first of each type, then fill with CPTs
                            const seenTypes = new Set<number>();
                            const priorityCodes = sortedCodes.filter(c => { if (seenTypes.has(c.order)) return false; seenTypes.add(c.order); return true; });
                            const remaining = sortedCodes.filter(c => !priorityCodes.includes(c));
                            const displayCodes = [...priorityCodes, ...remaining].slice(0, 8);
                            const uniqueCodes = displayCodes.map(c => c.label);
                            const totalUnique = sortedCodes.length;
                            return (
                              <div
                                key={s.uuid}
                                onClick={() => { handleScenarioSelect(s); setShowFamilyModal(false); }}
                                className="flex items-start gap-4 p-3 rounded-lg transition-all hover:shadow-md"
                                style={{
                                  backgroundColor: isCurrent ? `${COMP_COLORS_M[level]}10` : (ahpDark ? '#0a1828' : '#fff'),
                                  border: isCurrent ? `2px solid ${COMP_COLORS_M[level]}` : `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.1)' : 'rgba(var(--civic-ink-rgb),0.06)'}`,
                                  cursor: 'pointer',
                                }}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm" style={{ color: ahpDark ? '#e5e7eb' : 'var(--civic-ink)' }}>{s.name}</span>
                                    {isCurrent && (
                                      <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--civic-red)', color: '#fff' }}>
                                        {t('scenarios:familyExplorer.viewing')}
                                      </span>
                                    )}
                                  </div>
                                  {s.familyVariantLabel && (
                                    <div className="text-xs italic mt-0.5" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>{s.familyVariantLabel === 'uncomplicated' ? t('common:costQuartile.noComplications') : s.familyVariantLabel}</div>
                                  )}
                                  {/* Code pills */}
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {displayCodes.slice(0, 8).map(c => {
                                      const pillColors: Record<number, { bg: string; color: string; border: string }> = ahpDark ? {
                                        0: { bg: 'rgba(var(--civic-gold-rgb),0.12)', color: 'var(--civic-gold)', border: 'rgba(var(--civic-gold-rgb),0.2)' }, // DRG
                                        1: { bg: 'rgba(var(--civic-gold-rgb),0.08)', color: '#b8965a', border: 'rgba(var(--civic-gold-rgb),0.15)' }, // APR-DRG
                                        2: { bg: 'rgba(255,255,255,0.06)', color: 'var(--civic-muted)', border: 'rgba(255,255,255,0.08)' }, // CPT
                                        3: { bg: 'rgba(255,255,255,0.06)', color: 'var(--civic-muted)', border: 'rgba(255,255,255,0.08)' }, // HCPCS
                                        4: { bg: 'rgba(var(--civic-money-rgb),0.15)', color: '#4caf82', border: 'rgba(var(--civic-money-rgb),0.25)' }, // REV
                                        5: { bg: 'rgba(107,76,138,0.15)', color: '#a07ac0', border: 'rgba(107,76,138,0.25)' }, // DRUG/NDC
                                      } : {
                                        0: { bg: 'rgba(var(--civic-navy-rgb),0.08)', color: 'var(--civic-navy)', border: 'rgba(var(--civic-navy-rgb),0.15)' }, // DRG
                                        1: { bg: 'rgba(var(--civic-navy-rgb),0.06)', color: 'var(--civic-navy)', border: 'rgba(var(--civic-navy-rgb),0.12)' }, // APR-DRG
                                        2: { bg: 'rgba(var(--civic-ink-rgb),0.04)', color: '#6b7280', border: 'rgba(var(--civic-ink-rgb),0.06)' }, // CPT
                                        3: { bg: 'rgba(var(--civic-ink-rgb),0.04)', color: '#6b7280', border: 'rgba(var(--civic-ink-rgb),0.06)' }, // HCPCS
                                        4: { bg: 'rgba(var(--civic-money-rgb),0.08)', color: 'var(--civic-money)', border: 'rgba(var(--civic-money-rgb),0.15)' }, // REV
                                        5: { bg: 'rgba(107,76,138,0.08)', color: '#6B4C8A', border: 'rgba(107,76,138,0.15)' }, // DRUG/NDC
                                      };
                                      const style = pillColors[c.order] || pillColors[2];
                                      return (
                                        <span key={c.label} className="px-1.5 py-0.5 rounded text-[9px] font-mono" style={{
                                          backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}`,
                                        }}>
                                          {c.label}
                                        </span>
                                      );
                                    })}
                                    {totalUnique > 8 && (
                                      <span className="text-[9px] self-center" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>{t('scenarios:familyExplorer.moreCodes', { count: totalUnique - 8 })}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  <div style={{ fontFamily: serifM, fontSize: 22, color: 'var(--civic-red)' }}>
                                    ${Math.round(s.estimatedCost).toLocaleString()}
                                  </div>
                                  <div className="text-[10px] mt-0.5" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#9ca3af' }}>{t('scenarios:card.procedures', { count: s.totalProcedures })}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* No-match empty state while filtering */}
                  {fq && matchCount === 0 && (
                    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                      <Search className="w-7 h-7" style={{ color: ahpDark ? '#445566' : '#d1d5db' }} />
                      <div className="text-sm font-medium" style={{ color: ahpDark ? 'var(--civic-muted)' : '#6b7280' }}>
                        {t('scenarios:familyExplorer.noVariantsMatch', { query: familySearch.trim() })}
                      </div>
                      <button onClick={() => setFamilySearch("")} className="text-xs font-medium px-3 py-1.5 rounded-lg" style={{ color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)', border: `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.3)' : 'rgba(var(--civic-red-rgb),0.3)'}` }}>
                        {t('scenarios:familyExplorer.clearSearch')}
                      </button>
                    </div>
                  )}

                  {/* CTA: Build custom scenario */}
                  <div className="mt-4 p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, var(--civic-navy) 0%, var(--civic-navy-deep) 100%)', border: '1px solid rgba(var(--civic-navy-rgb),0.3)' }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium text-sm" style={{ fontFamily: serifM, fontWeight: 400 }}>
                          {t('scenarios:familyExplorer.buildYourOwn')}
                        </div>
                        <div className="text-white/60 text-xs mt-0.5">
                          {t('scenarios:familyExplorer.buildYourOwnSubtitle')}
                        </div>
                      </div>
                      <button
                        onClick={() => { setShowFamilyModal(false); setShowCreateDialog(true); }}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all hover:opacity-90"
                        style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                      >
                        <Plus className="w-3.5 h-3.5" />
                        {t('scenarios:familyExplorer.startBuilding')}
                      </button>
                    </div>
                  </div>
                </div>
            </>
          );
          return isMobileViewport ? (
            <ScenarioSectionModal
              open={showFamilyModal}
              onClose={() => { setShowFamilyModal(false); setFamilyModalLevel(null); setFamilySearch(""); }}
              dark={ahpDark}
              scenarioName={selectedScenario.name}
              sectionTitle={t('scenarios:familyExplorer.title')}
              padded={false}
            >
              {body}
            </ScenarioSectionModal>
          ) : (
            <Dialog open={showFamilyModal} onOpenChange={(o) => { setShowFamilyModal(o); if (!o) { setFamilyModalLevel(null); setFamilySearch(""); } }}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" style={{ backgroundColor: ahpDark ? 'var(--civic-navy-deeper)' : undefined, border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : undefined }}>
                {body}
              </DialogContent>
            </Dialog>
          );
        })()}

        {/* Nested code modal — CPT/HCPCS/DRG/REV page rendered inside the native
            sheet so the breadcrumb chain (Scenario › Code Breakdown › CPT 99232)
            continues instead of navigating away. Mobile only. */}
        {isMobileViewport && codeModal && selectedScenario && (
          <ScenarioSectionModal
            open={!!codeModal}
            onClose={closeCodeModal}
            urlManaged
            elevated
            dark={ahpDark}
            scenarioName="Code Breakdown"
            sectionTitle={`${codeModal.rawType} ${codeModal.code}`}
            padded={false}
          >
            <Suspense fallback={<div className="py-16 text-center" style={{ color: ahpDark ? 'var(--civic-muted)' : '#6b7280' }}>Loading {codeModal.rawType} {codeModal.code}…</div>}>
              <ProcedureCodePageLazy codeType={codeModal.codeType} codeOverride={codeModal.code} embedded />
            </Suspense>
          </ScenarioSectionModal>
        )}

        {/* Browse Grid — visible when no scenario selected */}
        {!selectedScenario && browseFilteredScenarios.length > 0 && (
          <div className="w-full mb-6">
            {/* Category pills — on desktop, sticky and tucked directly under the
                pinned title/search block; on mobile a plain scrolling row. */}
            <div
              className={isMobileViewport
                ? "flex gap-2 overflow-x-auto pb-3"
                : "flex gap-2 overflow-x-auto pb-3 pt-2 sticky z-40 -mx-6 px-6 backdrop-blur-md"}
              style={isMobileViewport ? { scrollbarWidth: 'none' } : {
                scrollbarWidth: 'none',
                top: `calc(var(--ahp-chrome-h, 0px) + ${stickyHeadH}px)`,
                backgroundColor: ahpDark ? 'rgba(var(--civic-navy-ink-rgb),0.94)' : 'rgba(255,255,255,0.94)',
              }}
            >
              {SCENARIO_CATEGORIES.map((cat) => {
                const catCount = allScenarios.filter((s) => scenarioMatchesCategory(s.tags || [], cat)).length;
                if (cat.key !== 'all' && catCount === 0) return null;
                const isActive = browseCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => setBrowseCategory(cat.key)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all flex-shrink-0"
                    style={{
                      backgroundColor: isActive ? 'var(--civic-red)' : (ahpDark ? 'var(--civic-card-dark)' : '#fff'),
                      color: isActive ? '#fff' : (ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)'),
                      border: `1px solid ${isActive ? 'transparent' : (ahpDark ? 'rgba(var(--civic-gold-rgb),0.2)' : 'rgba(var(--civic-ink-rgb),0.12)')}`,
                      fontFamily: "'Instrument Sans', sans-serif",
                    }}
                  >
                    {(() => {
                      const CatIcon = CATEGORY_ICONS[cat.key];
                      return CatIcon ? (
                        <CatIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: isActive ? '#fff' : (cat.color || (ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)')) }} />
                      ) : null;
                    })()}
                    {cat.label}
                    <span className="text-[10px] px-1 py-0.5 rounded-full" style={{
                      backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : (ahpDark ? 'rgba(255,255,255,0.05)' : 'rgba(var(--civic-ink-rgb),0.06)'),
                      minWidth: 18, textAlign: 'center',
                    }}>
                      {cat.key === 'all' ? allScenarios.length : catCount}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Full scenario cards \u2014 same component the Search tab uses.
                Mobile: single column. Desktop (md+): 2-up grid. The top padding
                keeps the first row off the sticky pill row's translucent edge
                (Trent, 2026-09-05). */}
            {/* One location + radius for every card's "near you" prices. */}
            <div className="pt-3">
              <LocationChooser
                currentLocation={userLocation}
                onDetectLocation={detectLocation}
                onZipLookup={lookupZip}
                onClearLocation={clearLocation}
                isLocating={isLocating}
                error={locationError}
                radiusMiles={radiusMiles}
                onRadiusChange={setRadiusMiles}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 pt-3 md:pt-4">
              {browseFilteredScenarios.map((scenario) => {
                const cardData: ScenarioCardData = {
                  id: scenario.uuid,
                  name: scenario.name,
                  description: typeof scenario.description === 'string' ? scenario.description : '',
                  icon_name: ICON_COMP_TO_NAME.get(scenario.icon) || 'Activity',
                  color: scenario.color,
                  duration: scenario.duration,
                  total_procedures: scenario.totalProcedures,
                  phases: scenario.phases || [],
                  severity_level: scenario.severityLevel ?? null,
                  calculated_avg_cost: scenario.estimatedCost || 0,
                  calculated_median_cost: scenario.medianCost,
                  phaseBreakdown: scenario.phaseBreakdown || {},
                  phaseBreakdownMedian: scenario.phaseBreakdownMedian ?? null,
                  phaseProcedures: {},
                  tags: scenario.tags || [],
                  created_by: scenario.createdBy ?? null,
                  is_verified: scenario.isVerified ?? false,
                };
                return (
                  <div key={scenario.uuid} className={`[&>*]:h-full ${nearTableOpen.has(scenario.uuid) ? 'md:col-span-2' : ''}`}>
                  <ScenarioCard
                    scenario={cardData}
                    isStarred={bookmarkedScenarioIds.includes(scenario.uuid)}
                    theme={scenarioCardTheme}
                    onToggleBookmark={(id) => toggleBookmark(id, { preventDefault() {}, stopPropagation() {} } as any)}
                    nearYou={
                      <ScenarioCardNearYou
                        scenarioId={scenario.uuid}
                        nationalTotal={scenario.estimatedCost || 0}
                        band={{ p10: scenario.p10Cost ?? null, median: scenario.medianCost ?? null, avg: scenario.estimatedCost ?? null, p90: scenario.p90Cost ?? null }}
                        location={userLocation}
                        radius={radiusMiles}
                        dark={ahpDark}
                        onTableToggle={(open) => setNearTableOpen((prev) => { const n = new Set(prev); if (open) n.add(scenario.uuid); else n.delete(scenario.uuid); return n; })}
                      />
                    }
                  />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Location Chooser — desktop only here. On mobile this bar sat on the
            Overview where it had no visible purpose (its consumers — nearby
            hospitals, hospital pricing — live in section modals); it now
            renders inside the Hospitals modal where it's actually used. */}
        {selectedScenario && !isMobileViewport && (
          <LocationChooser
            currentLocation={userLocation}
            onDetectLocation={detectLocation}
            onZipLookup={lookupZip}
            onClearLocation={clearLocation}
            isLocating={isLocating}
            error={locationError}
            radiusMiles={radiusMiles}
            onRadiusChange={setRadiusMiles}
          />
        )}

        {/* Cost Timeline — inline on desktop; full-screen native modal (opened
            from the "Timeline" rail chip) on mobile. */}
        {(() => {
        const timelineEl = (
        <div id="cost-timeline" className={`w-full mb-8 scroll-mt-24 ${isTimelineFullscreen ? 'fixed inset-0 z-[10050] bg-background p-6 overflow-auto flex flex-col' : ''}`}>
          <Card
            className={`${isAhp ? '' : 'border-matrix-green/20 bg-black/40 backdrop-blur-sm'} ${isTimelineFullscreen ? 'flex-1 flex flex-col' : ''}`}
            style={isAhp
              ? { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' }
              : undefined
            }
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between mb-3">
                <div>
                    <CardTitle
                      className={isAhp ? "text-lg flex items-center gap-2" : "text-lg font-mono text-matrix-green flex items-center gap-2"}
                      style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)', fontWeight: 400 } : undefined}
                    >
                      <Calendar className="w-5 h-5" style={isAhp ? { color: ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)' } : undefined} />
                      Cost Timeline Comparison
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      {selectedScenario ? `${selectedScenario.name} - USA Average vs Hospital Pricing` : 'Select a scenario to view timeline'}
                    </CardDescription>
                  </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* DRG inpatient-bundle toggle — only for bundled inpatient
                      scenarios. Switches the chart + comparison between the single
                      DRG bundle price and the itemized per-code line-items. */}
                  {isDrgScenario && (() => {
                    const segActive = (on: boolean) => on === includeDrgBundle;
                    const activeBg = isAhp ? (ahpDark ? 'rgba(158,197,239,0.16)' : 'rgba(var(--civic-navy-rgb),0.08)') : 'rgba(34,197,94,0.15)';
                    const activeColor = isAhp ? (ahpDark ? '#9ec5ef' : 'var(--civic-navy)') : '#22c55e';
                    const idle = isAhp ? (ahpDark ? 'rgba(var(--civic-warm-rgb),0.55)' : 'rgba(var(--civic-ink-rgb),0.62)') : 'rgba(255,255,255,0.5)';
                    const wrapBorder = isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.25)' : 'rgba(var(--civic-ink-rgb),0.14)') : 'rgba(34,197,94,0.3)';
                    return (
                      <div
                        className="flex items-center rounded-full p-0.5"
                        style={{ border: `1px solid ${wrapBorder}` }}
                        title="Inpatient stays can be priced as a single bundled DRG or itemized code-by-code"
                      >
                        {[{ on: true, label: 'DRG bundle' }, { on: false, label: 'Itemized' }].map((seg) => (
                          <button
                            key={seg.label}
                            onClick={() => setIncludeDrgBundle(seg.on)}
                            className="flex items-center gap-1 px-2.5 py-1 rounded-full transition-colors"
                            style={{
                              backgroundColor: segActive(seg.on) ? activeBg : 'transparent',
                              color: segActive(seg.on) ? activeColor : idle,
                              fontFamily: isAhp ? "'Instrument Sans', sans-serif" : "'JetBrains Mono', monospace",
                              fontSize: 11.5,
                              fontWeight: segActive(seg.on) ? 700 : 500,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {seg.on && <Layers className="w-3.5 h-3.5" />}
                            {seg.label}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  {/* Fullscreen toggle is desktop-only — on mobile the section is
                      already a full-screen native modal. */}
                  {!isMobileViewport && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsTimelineFullscreen(!isTimelineFullscreen)}
                    className="gap-2"
                  >
                    {isTimelineFullscreen ? (
                      <>
                        <Minimize2 className="w-4 h-4" />
                        Exit Fullscreen
                      </>
                    ) : (
                      <>
                        <Maximize2 className="w-4 h-4" />
                        Fullscreen
                      </>
                    )}
                  </Button>
                  )}
                </div>
              </div>

              {/* Hospital Selector - Split Name + Location Autocomplete */}
                {selectedScenario && (
                  <div className="mb-4 relative">
                    <label className="text-xs text-muted-foreground mb-2 block">Compare with Hospital:</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                        <Input
                          ref={nameInputRef}
                          type="text"
                          placeholder="Hospital name..."
                          value={hospitalNameQuery}
                          onChange={(e) => {
                            setHospitalNameQuery(e.target.value);
                            if (selectedHospitalId) setSelectedHospitalId(null);
                            setShowHospitalDropdown(true);
                          }}
                          onFocus={() => setShowHospitalDropdown(true)}
                          className={isAhp
                            ? `w-full text-sm pl-10 pr-10 ${ahpDark ? 'border-civic-gold/30 bg-civic-card-dark text-civic-warm' : 'border-gray-200 bg-gray-50/50'}`
                            : "w-full bg-black/40 border-matrix-green/20 font-mono text-sm pl-10 pr-10"
                          }
                        />
                        {selectedHospitalId && (
                          <button
                            onClick={() => {
                              setSelectedHospitalId(null);
                              setHospitalNameQuery('');
                              setHospitalLocationQuery('');
                              setShowHospitalDropdown(false);
                            }}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-matrix-green transition-colors"
                            aria-label="Clear search"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <div className="relative w-[180px]">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                        <Input
                          ref={locationInputRef}
                          type="text"
                          placeholder="City or state..."
                          value={hospitalLocationQuery}
                          onChange={(e) => {
                            setHospitalLocationQuery(e.target.value);
                            if (selectedHospitalId) setSelectedHospitalId(null);
                            setShowHospitalDropdown(true);
                          }}
                          onFocus={() => setShowHospitalDropdown(true)}
                          className={isAhp
                            ? `w-full text-sm pl-10 ${ahpDark ? 'border-civic-gold/30 bg-civic-card-dark text-civic-warm' : 'border-gray-200 bg-gray-50/50'}`
                            : "w-full bg-black/40 border-matrix-green/20 font-mono text-sm pl-10"
                          }
                        />
                      </div>
                    </div>

                    {/* Dropdown Results */}
                    {showHospitalDropdown && (hospitals?.length || 0) > 0 && (
                      <div
                        ref={dropdownRef}
                        className={isAhp
                          ? `absolute z-50 w-full mt-1 ${ahpDark ? 'bg-civic-card-dark border border-civic-gold/30' : 'bg-white border border-gray-200'} rounded-lg shadow-xl max-h-[300px] overflow-y-auto thin-scrollbar`
                          : "absolute z-50 w-full mt-1 bg-black/95 border border-matrix-green/20 rounded-lg shadow-lg max-h-[300px] overflow-y-auto thin-scrollbar"
                        }
                      >
                        {(hospitals || []).map((hospital) => (
                          <button
                            key={hospital.id}
                            onClick={() => handleHospitalSelect(hospital.id)}
                            className={`w-full px-4 py-2 text-left font-mono text-sm transition-colors border-b last:border-b-0 ${
                              isAhp
                                ? `${ahpDark ? `hover:bg-civic-gold/10 border-civic-gold/10 ${selectedHospitalId === hospital.id ? 'bg-civic-gold/15' : ''}` : `hover:bg-gray-100 border-gray-100 ${selectedHospitalId === hospital.id ? 'bg-gray-100' : ''}`}`
                                : `hover:bg-matrix-green/10 border-matrix-green/10 ${selectedHospitalId === hospital.id ? 'bg-matrix-green/20' : ''}`
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className={isAhp ? `font-bold ${ahpDark ? 'text-civic-warm' : 'text-gray-900'} truncate` : "font-bold text-matrix-green truncate"}>{hospital.name}</div>
                                <div className="text-xs text-muted-foreground">
                                  {hospital.city}, {hospital.state}
                                </div>
                              </div>
                              <div className="flex-shrink-0 flex items-center gap-1.5 flex-wrap justify-end">
                                {selectedScenario && (hospital.blendedPrice || hospital.totalHospitalPrice) > 0 && (
                                  <div className="px-2 py-0.5 rounded text-xs font-bold bg-purple-500/20 border border-purple-500/30 text-purple-400">
                                    {hospital.matchedCodes < hospital.totalCodes ? '~' : ''}${Math.round(hospital.blendedPrice || hospital.totalHospitalPrice).toLocaleString()}
                                  </div>
                                )}
                                {selectedScenario && hospital.totalNationalPrice > 0 && hospital.totalHospitalPrice > 0 && (
                                  <div className={`px-2 py-0.5 rounded text-xs font-bold ${
                                    hospital.avgMarkupPercentage > 50
                                      ? 'bg-red-500/20 border border-red-500/30 text-red-400'
                                      : hospital.avgMarkupPercentage > 0
                                        ? 'bg-orange-500/20 border border-orange-500/30 text-orange-400'
                                        : 'bg-green-500/20 border border-green-500/30 text-green-400'
                                  }`}>
                                    {hospital.avgMarkupPercentage > 0 ? '+' : ''}{Math.round(hospital.avgMarkupPercentage)}% vs avg
                                  </div>
                                )}
                                {selectedScenario && hospital.totalCodes > 0 && (
                                  <div className={`px-2 py-0.5 rounded text-xs font-bold ${
                                    hospital.coveragePercentage >= 80
                                      ? 'bg-green-500/20 border border-green-500/30 text-green-400'
                                      : hospital.coveragePercentage >= 50
                                        ? 'bg-yellow-500/20 border border-yellow-500/30 text-yellow-400'
                                        : hospital.coveragePercentage > 0
                                          ? 'bg-orange-500/20 border border-orange-500/30 text-orange-400'
                                          : 'bg-gray-500/20 border border-gray-500/30 text-gray-400'
                                  }`}>
                                    {hospital.coveragePercentage}% match
                                  </div>
                                )}
                                <a
                                  href={`/hospital/${hospital.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    navigate(`/hospital/${hospital.id}`);
                                  }}
                                  title="View hospital profile"
                                  className={`shrink-0 p-1 rounded transition-colors ${isAhp ? (ahpDark ? 'hover:bg-white/10 text-muted-foreground hover:text-civic-warm' : 'hover:bg-gray-100 text-muted-foreground hover:text-gray-700') : 'hover:bg-white/10 text-gray-500 hover:text-matrix-green'}`}
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                </a>
                              </div>
                            </div>
                          </button>
                        ))}
                        {(hospitals?.length || 0) >= 50 && (
                          <div className={`px-4 py-2 text-xs text-muted-foreground text-center border-t ${isAhp ? (ahpDark ? 'border-civic-gold/20' : 'border-gray-200') : 'border-matrix-green/10'}`}>
                            Showing first 50 results. Keep typing to narrow down...
                          </div>
                        )}
                      </div>
                    )}

                    {/* No Results */}
                    {showHospitalDropdown && (hospitalNameQuery || hospitalLocationQuery) && (hospitals?.length || 0) === 0 && (
                      <div
                        ref={dropdownRef}
                        className={isAhp
                          ? `absolute z-50 w-full mt-1 ${ahpDark ? 'bg-civic-card-dark border border-civic-gold/30' : 'bg-white border border-gray-200'} rounded-lg shadow-lg p-4 text-center text-sm text-muted-foreground`
                          : "absolute z-50 w-full mt-1 bg-black/95 border border-matrix-green/20 rounded-lg shadow-lg p-4 text-center text-sm text-muted-foreground"
                        }
                      >
                        No hospitals found{hospitalNameQuery ? ` matching "${hospitalNameQuery}"` : ''}{hospitalLocationQuery ? ` in "${hospitalLocationQuery}"` : ''}
                      </div>
                    )}
                  </div>
                )}

                {/* Price Spread Summary */}
                {selectedScenario && selectedHospitalId && hospitalTotal > 0 && hospitalCoverage.percentage > 0 && (() => {
                  // (id anchors the Price-Breakdown jump from Nearby Hospitals)
                  // AHP-dark-aware value colors. The generic text-blue-400/green-400
                  // read as low-contrast blue-on-navy against the var(--civic-bg-dark) panel, so
                  // use brighter, tuned hues for AHP dark (and deeper ones for light).
                  const natColor = isAhp ? (ahpDark ? '#9ec5ef' : '#1d4ed8') : undefined;
                  const hospColor = isAhp ? (ahpDark ? 'var(--civic-money-soft)' : '#15803d') : undefined;
                  const diffColor = isAhp
                    ? (displayPriceDifference > 0 ? (ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)') : (ahpDark ? 'var(--civic-money-soft)' : '#15803d'))
                    : undefined;
                  return (
                  <div id="code-breakdown-hospital-summary" className={`scroll-mt-24 ${isAhp ? `mb-4 p-3 rounded-lg border ${ahpDark ? 'border-civic-gold/20 bg-civic-bg-dark' : 'border-gray-200 bg-gray-50'}` : "mb-4 p-3 bg-black/60 rounded-lg border border-matrix-green/20"}`}>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div>
                        <div className="text-muted-foreground mb-1">{t('scenarios:card.nationalAverage')}</div>
                        <div className={`font-mono font-bold ${isAhp ? '' : 'text-blue-400'}`} style={{ color: natColor }}>${Math.round(displayNationalTotal).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground mb-1">Hospital Price</div>
                        <div className={`font-mono font-bold ${isAhp ? '' : 'text-green-400'}`} style={{ color: hospColor }}>${Math.round(displayHospitalTotal).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground mb-1">Difference</div>
                        <div className={`font-mono font-bold ${isAhp ? '' : (displayPriceDifference > 0 ? 'text-red-400' : 'text-green-400')}`} style={{ color: diffColor }}>
                          {displayPriceDifference > 0 ? '+' : ''}{displayPriceDiffPercent.toFixed(0)}%
                        </div>
                      </div>
                    </div>
                    {isDrgScenario && (
                      <div className="mt-2 pt-2 text-[11px] leading-snug" style={{ borderTop: `1px solid ${isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(0,0,0,0.08)') : 'rgba(34,197,94,0.15)'}`, color: isAhp ? (ahpDark ? 'rgba(var(--civic-warm-rgb),0.6)' : 'rgba(var(--civic-ink-rgb),0.6)') : undefined }}>
                        {includeDrgBundle
                          ? <>Inpatient stay shown as one <strong>DRG bundle</strong>{drgInfo?.baseDrg ? <> (DRG {drgInfo.baseDrg})</> : null}. Toggle below to itemize each code instead.</>
                          : <>Inpatient stay shown as <strong>itemized line-items</strong> — summed per code, which typically runs higher than the bundled DRG price.</>}
                      </div>
                    )}
                    {/* Crosswalk-DRG bundle line: the scenario has no DRG procedure row, but
                        THIS hospital prices the inpatient core as a DRG bundle. Surface the
                        bundle as a countable line so Hospital Price reconciles on-page. */}
                    {!isDrgScenario && (() => {
                      const bundle = hospitalPrices?.get('__DRG_BUNDLE__') as { code: string; price: number } | undefined;
                      if (!bundle) return null;
                      const title = drgInfo?.baseDrgTitle
                        ? String(drgInfo.baseDrgTitle).replace(/^\s*\d+\s*[-–]\s*/, '').toLowerCase()
                        : null;
                      return (
                        <div className="mt-2 pt-2 text-[11px] leading-snug" style={{ borderTop: `1px solid ${isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(0,0,0,0.08)') : 'rgba(34,197,94,0.15)'}`, color: isAhp ? (ahpDark ? 'rgba(var(--civic-warm-rgb),0.75)' : 'rgba(var(--civic-ink-rgb),0.75)') : undefined }}>
                          This hospital prices the inpatient stay as one bundle — <strong>MS-DRG {bundle.code}</strong>{title ? <> ({title})</> : null}: <strong className="font-mono">${Math.round(bundle.price).toLocaleString()}</strong> from its published file. The Hospital Price above is this bundle plus the unbundled outpatient lines below; surgery and hospitalization codes are inside the bundle, not itemized.
                        </div>
                      );
                    })()}
                  </div>
                  );
                })()}
              </CardHeader>
              <CardContent className={isTimelineFullscreen ? 'flex-1 flex flex-col' : ''}>
                {selectedScenario && nationalTimelineData.length > 0 ? (
                  <div className={`space-y-3 ${isTimelineFullscreen ? 'flex-1 flex flex-col' : ''}`}>
                    {/* Data Coverage Warning */}
                    {selectedHospitalId && !loadingHospital && hospitalCoverage.percentage < 100 && hospitalCoverage.percentage > 0 && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 mb-4">
                        <div className="flex items-start gap-2">
                          <Badge variant="outline" className="text-xs font-mono border-yellow-500 text-yellow-500 bg-yellow-500/10 flex-shrink-0">
                            Incomplete Data
                          </Badge>
                          <p className="text-xs text-muted-foreground">
                            This hospital has pricing data for only <strong className="text-yellow-500">{hospitalCoverage.hasData} of {hospitalCoverage.total} procedures</strong> ({hospitalCoverage.percentage.toFixed(0)}% coverage).
                            The comparison may not reflect the full scenario cost. Missing procedures will use national averages.
                          </p>
                        </div>
                      </div>
                    )}

                    {selectedHospitalId && !loadingHospital && hospitalCoverage.percentage === 0 && (
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4">
                        <div className="flex items-start gap-2">
                          <Badge variant="outline" className="text-xs font-mono border-red-500 text-red-500 bg-red-500/10 flex-shrink-0">
                            No Data
                          </Badge>
                          <p className="text-xs text-muted-foreground">
                            This hospital has <strong className="text-red-500">no pricing data</strong> for any procedures in this scenario.
                            Try selecting a different hospital or scenario.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Base vs Complication Cost Split Summary */}
                    {baseProcedureCodes && selectedScenario && (() => {
                      const priceMap = selectedHospitalId && hospitalPrices ? hospitalPrices : nationalAverages;
                      const isHospData = !!(selectedHospitalId && hospitalPrices);
                      let baseCost = 0;
                      let addedCost = 0;
                      for (const proc of selectedScenario.procedures) {
                        const { unitPrice } = getProcedurePrice(proc, priceMap, isHospData, undefined, isHospData ? nationalAverages : null);
                        const total = unitPrice * proc.quantity;
                        if (baseProcedureCodes.has(`${proc.codeType}:${proc.code}`)) {
                          baseCost += total;
                        } else {
                          addedCost += total;
                        }
                      }
                      const totalCost = baseCost + addedCost;
                      const addedPct = totalCost > 0 ? ((addedCost / baseCost) * 100).toFixed(0) : '0';
                      return (
                        <div
                          className="mb-4 rounded-lg border overflow-hidden"
                          style={{
                            borderColor: isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.25)' : 'rgba(var(--civic-ink-rgb),0.12)') : 'rgba(34,197,94,0.2)',
                            backgroundColor: isAhp ? (ahpDark ? 'rgba(var(--civic-card-dark-rgb),0.8)' : 'rgba(249,250,251,0.8)') : 'rgba(0,0,0,0.4)',
                          }}
                        >
                          <div className="flex items-stretch divide-x" style={{ divideColor: isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(0,0,0,0.08)') : 'rgba(34,197,94,0.15)' }}>
                            <div className="flex-1 px-4 py-3">
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1" style={isAhp ? { fontFamily: "'Instrument Sans', sans-serif" } : undefined}>
                                Base Median
                              </div>
                              <div
                                className="text-lg font-bold font-mono"
                                style={{ color: isAhp ? (ahpDark ? 'rgba(var(--civic-navy-soft-rgb),0.7)' : 'rgba(var(--civic-navy-rgb),0.6)') : 'rgba(34,197,94,0.6)' }}
                              >
                                ${Math.round(baseCost).toLocaleString()}
                              </div>
                            </div>
                            <div className="flex-1 px-4 py-3">
                              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1" style={isAhp ? { fontFamily: "'Instrument Sans', sans-serif" } : undefined}>
                                Complication Adds
                              </div>
                              <div className="flex items-baseline gap-2">
                                <span
                                  className="text-lg font-bold font-mono"
                                  style={{ color: isAhp ? (ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)') : '#ef4444' }}
                                >
                                  ${Math.round(addedCost).toLocaleString()}
                                </span>
                                <span
                                  className="text-xs font-semibold"
                                  style={{ color: isAhp ? (ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)') : '#ef4444' }}
                                >
                                  +{addedPct}%
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Highcharts Stacked Bar Timeline - Grouped by Phase */}
                    {(() => {
                      // Use the bundle-aware timeline data within the chart. In ON
                      // mode these collapse the inpatient stay into one DRG-bundle
                      // bar; in OFF / non-DRG they are the raw itemized data.
                      const nationalTimelineData = chartNationalData;
                      const hospitalTimelineData = chartHospitalData;
                      // Phase color palette — distinct, accessible colors per phase
                      const phaseColors: Record<string, string> = {};
                      const phaseColorPalette = isAhp
                        ? (ahpDark
                          ? ['var(--civic-red-soft)', 'var(--civic-navy-soft)', 'var(--civic-gold)', '#5d8ab4', '#c45565', '#6d9ac0', '#c47a4d', '#6b9e6b']
                          : ['var(--civic-red)', 'var(--civic-navy)', 'var(--civic-gold)', 'var(--civic-navy-deep)', '#8B2232', '#3D5A80', '#A0522D', '#4A6741'])
                        : ['#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4', '#ef4444', '#10b981', '#f97316'];
                      const hospitalPhaseColorPalette = isAhp
                        ? (ahpDark
                          ? ['var(--civic-money-soft)', '#4db87e', '#7ed4a8', '#8bcc9e', '#9dd8b2', 'var(--civic-money-soft)', '#4db87e', '#7ed4a8']
                          : ['var(--civic-money)', '#1a7a52', '#3d8b6e', '#548c6b', '#6b9e7f', 'var(--civic-money)', '#1a7a52', '#3d8b6e'])
                        : ['#22c55e', '#a78bfa', '#fbbf24', '#f472b6', '#67e8f9', '#fca5a5', '#6ee7b7', '#fdba74'];

                      const showHospital = selectedHospitalId && hospitalTimelineData.length > 0 && hospitalCoverage.percentage > 0;
                      const chartMinHeight = isTimelineFullscreen ? 500 : 400;

                      // Collect all days (union of national + hospital)
                      const allDays = new Set<number>();
                      nationalTimelineData.forEach(d => allDays.add(d.day));
                      hospitalTimelineData.forEach(d => allDays.add(d.day));
                      const sortedDays = Array.from(allDays).sort((a, b) => a - b);
                      const categories = sortedDays.map(d => `Day ${d}`);

                      // Get phases in scenario order
                      const scenarioPhases = selectedScenario?.phases || [];
                      // Also collect any phases from data not in the scenario phases list
                      const allPhases = [...scenarioPhases];
                      const phaseSet = new Set(scenarioPhases);
                      nationalTimelineData.forEach(d => d.procedures.forEach(p => {
                        if (p.phase && !phaseSet.has(p.phase)) {
                          allPhases.push(p.phase);
                          phaseSet.add(p.phase);
                        }
                      }));

                      // Assign colors to phases
                      allPhases.forEach((phase, idx) => {
                        phaseColors[phase] = phaseColorPalette[idx % phaseColorPalette.length];
                      });
                      // The DRG bundle bar gets a distinct "national navy / gold"
                      // hue so it reads clearly as the single bundled inpatient stay.
                      if (phaseColors[DRG_BUNDLE_PHASE]) {
                        phaseColors[DRG_BUNDLE_PHASE] = isAhp ? (ahpDark ? '#9ec5ef' : 'var(--civic-navy)') : '#3b82f6';
                      }

                      // Build national series — one per phase, stacked
                      // When baseProcedureCodes is set, split each phase into base (muted) and additional (vivid)
                      let nationalSeries: Highcharts.SeriesColumnOptions[];
                      if (baseProcedureCodes) {
                        nationalSeries = allPhases.flatMap((phase, idx) => {
                          const baseColor = `${phaseColors[phase]}59`; // ~35% opacity hex suffix
                          const addedColor = phaseColors[phase];
                          return [
                            {
                              type: 'column' as const,
                              name: `${phase} (Base)`,
                              id: `nat-phase-${idx}-base`,
                              stack: 'national',
                              color: baseColor,
                              legendIndex: idx * 2,
                              data: sortedDays.map(day => {
                                const dayData = nationalTimelineData.find(d => d.day === day);
                                if (!dayData) return 0;
                                const procs = dayData.procedures.filter(p => p.phase === phase && baseProcedureCodes.has(`${p.codeType}:${p.code}`));
                                return Math.round(procs.reduce((s, p) => s + p.cost, 0));
                              }),
                            },
                            {
                              type: 'column' as const,
                              name: `${phase} (Complication)`,
                              id: `nat-phase-${idx}-added`,
                              stack: 'national',
                              color: addedColor,
                              legendIndex: idx * 2 + 1,
                              data: sortedDays.map(day => {
                                const dayData = nationalTimelineData.find(d => d.day === day);
                                if (!dayData) return 0;
                                const procs = dayData.procedures.filter(p => p.phase === phase && !baseProcedureCodes.has(`${p.codeType}:${p.code}`));
                                return Math.round(procs.reduce((s, p) => s + p.cost, 0));
                              }),
                            },
                          ];
                        });
                      } else {
                        nationalSeries = allPhases.map((phase, idx) => ({
                          type: 'column' as const,
                          name: phase,
                          id: `nat-phase-${idx}`,
                          stack: 'national',
                          color: phaseColors[phase],
                          data: sortedDays.map(day => {
                            const dayData = nationalTimelineData.find(d => d.day === day);
                            if (!dayData) return 0;
                            const phaseProcs = dayData.procedures.filter(p => p.phase === phase);
                            return Math.round(phaseProcs.reduce((s, p) => s + p.cost, 0));
                          }),
                        }));
                      }

                      // Build hospital series — same phases, different stack
                      let hospitalSeries: Highcharts.SeriesColumnOptions[] = [];
                      if (showHospital) {
                        // Collect hospital phases (may have extras)
                        const hospPhaseSet = new Set<string>();
                        hospitalTimelineData.forEach(d => d.procedures.forEach(p => {
                          if (p.phase) hospPhaseSet.add(p.phase);
                        }));

                        hospitalSeries = allPhases
                          .filter(phase => hospPhaseSet.has(phase))
                          .map((phase, idx) => {
                            const natIdx = allPhases.indexOf(phase);
                            return {
                              type: 'column' as const,
                              name: `${phase} (Hospital)`,
                              stack: 'hospital',
                              color: hospitalPhaseColorPalette[natIdx % hospitalPhaseColorPalette.length],
                              linkedTo: baseProcedureCodes ? `nat-phase-${natIdx}-base` : `nat-phase-${natIdx}`,
                              data: sortedDays.map(day => {
                                const dayData = hospitalTimelineData.find(d => d.day === day);
                                if (!dayData) return 0;
                                const phaseProcs = dayData.procedures.filter(p => p.phase === phase);
                                return Math.round(phaseProcs.reduce((s, p) => s + p.cost, 0));
                              }),
                            };
                          });
                      }

                      const isDark = !isAhp || ahpDark;

                      const chartOptions: Highcharts.Options = {
                        chart: {
                          type: 'column',
                          height: chartMinHeight,
                          backgroundColor: 'transparent',
                          style: { fontFamily: isAhp ? "'Instrument Sans', sans-serif" : "'JetBrains Mono', monospace" },
                        },
                        title: { text: undefined },
                        subtitle: showHospital ? {
                          text: 'Left stack = USA Average  |  Right stack = Hospital',
                          style: { color: isDark ? '#6b7280' : '#9ca3af', fontSize: '11px' },
                        } : undefined,
                        xAxis: {
                          categories,
                          labels: { style: { color: isDark ? '#9ca3af' : '#6b7280', fontSize: '11px' } },
                          lineColor: isDark ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.2)' : 'rgba(34,197,94,0.2)') : '#e5e7eb',
                          tickLength: 0,
                        },
                        yAxis: {
                          title: { text: undefined },
                          labels: {
                            style: { color: isDark ? '#9ca3af' : '#6b7280', fontSize: '11px' },
                            formatter: function() {
                              const v = this.value as number;
                              if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
                              if (v >= 1000) return `$${(v / 1000).toFixed(0)}k`;
                              return `$${v}`;
                            },
                          },
                          gridLineColor: isDark ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.1)' : 'rgba(34,197,94,0.1)') : 'rgba(0,0,0,0.06)',
                          stackLabels: {
                            enabled: true,
                            style: {
                              color: isDark ? '#e5e7eb' : '#374151',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              textOutline: isDark ? '1px contrast' : 'none',
                            },
                            formatter: function() {
                              const v = this.total || 0;
                              if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
                              if (v >= 1000) return `$${Math.round(v / 1000)}k`;
                              return `$${v}`;
                            },
                          },
                        },
                        legend: {
                          enabled: true,
                          itemStyle: { color: isDark ? '#d1d5db' : '#374151', fontSize: '12px', fontWeight: 'normal' },
                          itemHoverStyle: { color: isDark ? '#fff' : '#111' },
                          backgroundColor: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)',
                          borderRadius: 6,
                          padding: 12,
                          align: 'center',
                          verticalAlign: 'bottom',
                          layout: 'horizontal',
                          symbolRadius: 4,
                        },
                        tooltip: {
                          shared: false,
                          backgroundColor: isDark ? 'rgba(0,0,0,0.95)' : '#ffffff',
                          borderColor: isDark ? 'rgba(59,130,246,0.5)' : '#e5e7eb',
                          borderRadius: 8,
                          shadow: true,
                          style: { color: isDark ? '#fff' : '#111', fontSize: '12px' },
                          useHTML: true,
                          formatter: function() {
                            const stackName = (this.series.options as any).stack;
                            const category = this.x !== undefined ? categories[this.x as number] : '';
                            const dayNum = sortedDays[this.x as number];
                            const isNat = stackName === 'national';
                            const phaseName = this.series.name.replace(' (Hospital)', '');
                            const phaseColor = phaseColors[phaseName] || '#888';

                            // Get all procedures for this day from the timeline data
                            const timelineSource = isNat ? nationalTimelineData : hospitalTimelineData;
                            const dayData = timelineSource.find(d => d.day === dayNum);
                            const dayTotal = dayData ? dayData.procedures.reduce((s, p) => s + p.cost, 0) : 0;
                            const phaseProcs = dayData ? dayData.procedures.filter(p => p.phase === phaseName) : [];
                            const phaseTotal = phaseProcs.reduce((s, p) => s + p.cost, 0);

                            let html = `<div style="padding:4px 2px;min-width:220px">`;
                            html += `<b style="font-size:13px">${category}</b>`;
                            html += `<span style="float:right;color:${isDark ? '#9ca3af' : '#6b7280'};font-size:11px">${isNat ? 'USA Average' : 'Hospital'}</span><br/>`;
                            html += `<div style="margin:6px 0 4px;padding:4px 0;border-bottom:2px solid ${phaseColor}">`;
                            html += `<span style="color:${phaseColor};font-weight:bold;font-size:12px">${phaseName}</span>`;
                            html += `<span style="float:right;font-weight:bold">$${Math.round(phaseTotal).toLocaleString()}</span>`;
                            html += `</div>`;
                            html += `<div style="max-height:180px;overflow-y:auto">`;

                            phaseProcs.forEach(p => {
                              const label = p.consumerName || p.description;
                              const isAvg = !isNat && !(p.hasHospitalData ?? true);
                              const isBase = baseProcedureCodes?.has(`${p.codeType}:${p.code}`);
                              const baseAddedTag = baseProcedureCodes ? (isBase ? ' <span style="color:var(--civic-gold);font-size:9px">(Base)</span>' : ' <span style="color:var(--civic-red-soft);font-size:9px">(Added)</span>') : '';
                              html += `<div style="display:flex;justify-content:space-between;gap:12px;margin:2px 0;font-size:11px">`;
                              html += `<span style="opacity:0.85${isAvg ? ';font-style:italic' : ''}">${label}${baseAddedTag}${isAvg ? ' <span style="color:#eab308">(avg)</span>' : ''}</span>`;
                              html += `<span style="font-weight:600;white-space:nowrap">$${Math.round(p.cost).toLocaleString()}</span>`;
                              html += `</div>`;
                            });

                            html += `</div>`;

                            // Day total footer
                            html += `<div style="margin-top:6px;padding-top:6px;border-top:1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}">`;
                            html += `<div style="display:flex;justify-content:space-between;font-weight:bold;font-size:12px">`;
                            html += `<span>Day Total</span><span>$${Math.round(dayTotal).toLocaleString()}</span>`;
                            html += `</div>`;

                            // Show comparison if hospital stack
                            if (!isNat) {
                              const natDay = nationalTimelineData.find(d => d.day === dayNum);
                              const natTotal = natDay ? natDay.procedures.reduce((s, p) => s + p.cost, 0) : 0;
                              if (natTotal > 0) {
                                const pctDiff = ((dayTotal - natTotal) / natTotal * 100).toFixed(0);
                                const sign = dayTotal > natTotal ? '+' : '';
                                const diffColor = dayTotal > natTotal ? '#ef4444' : '#22c55e';
                                html += `<div style="font-weight:bold;color:${diffColor};font-size:11px;margin-top:2px">${sign}${pctDiff}% vs national average</div>`;
                              }
                            }
                            html += `</div></div>`;
                            return html;
                          },
                        },
                        plotOptions: {
                          column: {
                            stacking: 'normal',
                            borderWidth: 0,
                            borderRadius: 3,
                            groupPadding: showHospital ? 0.15 : 0.25,
                            pointPadding: 0.05,
                          },
                        },
                        series: [...nationalSeries, ...hospitalSeries],
                        credits: { enabled: false },
                        responsive: {
                          rules: [{
                            condition: { maxWidth: 500 },
                            chartOptions: {
                              legend: { layout: 'horizontal', align: 'center', verticalAlign: 'bottom' },
                              yAxis: { labels: { style: { fontSize: '9px' } } },
                              xAxis: { labels: { style: { fontSize: '9px' } } },
                            },
                          }],
                        },
                      };

                      return (
                        <div className={isTimelineFullscreen ? 'flex-1' : ''} style={{ width: '100%', minHeight: chartMinHeight }}>
                          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <div className="max-w-md mx-auto">
                      <Calendar className="w-16 h-16 mx-auto mb-4 opacity-20 text-muted-foreground" style={isAhp ? { color: ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)' } : undefined} />
                      <h3
                        className={isAhp ? "text-2xl font-bold mb-3" : "text-2xl font-bold text-matrix-green mb-3 font-mono"}
                        style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)' } : undefined}
                      >Get Started</h3>
                      <p className="text-base text-muted-foreground mb-8">
                        Choose a medical scenario above to see the complete cost breakdown and timeline
                      </p>
                      <div className="flex flex-col items-center gap-3">
                        <div
                          className={`text-6xl opacity-50 animate-bounce leading-none ${isAhp ? '' : 'text-matrix-green'}`}
                          style={isAhp ? { animationDuration: '2s', color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-red)' } : { animationDuration: '2s' }}
                        >↑</div>
                        <div
                          className={`text-sm uppercase tracking-wider font-semibold ${isAhp ? '' : 'font-mono text-matrix-green/70'}`}
                          style={isAhp ? { fontFamily: "'Instrument Sans', sans-serif", color: ahpDark ? 'rgba(var(--civic-gold-rgb),0.7)' : 'rgba(var(--civic-red-rgb),0.7)' } : undefined}
                        >
                          Select Scenario
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
          </Card>
        </div>
        );
        // Hybrid: inline for the continuous scroll + rail-launched modal on mobile.
        return (
          <>
            {timelineEl}
            {isMobileViewport && (
              <ScenarioSectionModal open={openSection === 'cost-timeline'} onClose={closeSection} urlManaged dark={ahpDark} scenarioName={selectedScenario?.name} sectionTitle="Cost Timeline">
                {timelineEl}
              </ScenarioSectionModal>
            )}
          </>
        );
        })()}

        {/* Detailed Breakdown Section */}
        <div className="w-full">
          {selectedScenario && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Procedure Code Breakdown. Inline on desktop;
                  on mobile it's launched as a full-screen native modal (with
                  breadcrumbs) from the "Code Breakdown" chip in the section rail. */}
              {selectedScenario && (() => {
                const codeBreakdownEl = (
              <Card
                className={isAhp ? '' : 'border-matrix-green/20 bg-black/40 backdrop-blur-sm'}
                style={isAhp ? { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' } : undefined}
              >
                <CardHeader>
                  <CardTitle
                    className={isAhp ? "text-2xl flex items-center gap-3" : "text-2xl font-mono text-matrix-green flex items-center gap-3"}
                    style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)', fontWeight: 400 } : undefined}
                  >
                    <selectedScenario.icon className="w-8 h-8" style={{ color: isAhp ? (ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)') : selectedScenario.color }} />
                    {selectedScenario.name} - {t('scenarios:detail.procedureBreakdown')}
                  </CardTitle>
                  <CardDescription>
                    {t('scenarios:detail.procedureBreakdown')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* When the selected hospital prices the inpatient block as a DRG
                      bundle, explain that in plain language before the line items. */}
                  <DrgBundleExplainer
                    scenarioId={selectedScenario.id}
                    selectedHospitalId={selectedHospitalId}
                    hospitalName={hospitalNameQuery || undefined}
                    dark={ahpDark}
                    bundleRow={hospitalPrices?.get('__DRG_BUNDLE__') || null}
                  />
                  {(() => {
                    // Derive phases from procedure data if phases array is empty
                    const declaredPhases = Array.isArray(selectedScenario.phases) && selectedScenario.phases.length > 0
                      ? selectedScenario.phases
                      : [...new Set((selectedScenario.procedures || []).map(p => p.phase || 'Procedures').filter(Boolean))];
                    return declaredPhases;
                  })().map((phase, phaseIdx) => {
                    const phaseProcedures = selectedScenario.procedures
                      .filter(p => (p.phase || 'Procedures') === phase)
                      .sort((a, b) => (a.dayNumber || 0) - (b.dayNumber || 0)); // Sort by day number
                    // Use hospital prices if selected, otherwise national averages
                    const priceMap = selectedHospitalId && hospitalPrices ? hospitalPrices : nationalAverages;
                    const isHospitalData = !!(selectedHospitalId && hospitalPrices);
                    // Phase total. For DRG scenarios it's mode-aware: in bundle
                    // mode the DRG line counts and the covered-phase itemized codes
                    // fold to zero; in itemized mode the DRG line is dropped.
                    const phaseTotal = isDrgScenario
                      ? phaseProcedures.reduce((sum, p) => {
                          const setting = settingOverrides.get(`${p.codeType}:${p.code}`) || p.setting || 'unspecified';
                          const price = getProcedurePrice(p, priceMap, isHospitalData, setting, isHospitalData ? nationalAverages : null).unitPrice * p.quantity;
                          if (isDrgLineProc(p)) return includeDrgBundle ? sum + price : sum;
                          if (coveredPhases.has(p.phase)) return includeDrgBundle ? sum : sum + price;
                          return sum + price;
                        }, 0)
                      : getPhaseTotal(selectedScenario, phase, priceMap, isHospitalData, isHospitalData ? nationalAverages : null);
                    const isExpanded = !collapsedPhases.has(phase);

                    // Phase color matching the chart
                    const phaseChartColors = isAhp
                      ? (ahpDark
                        ? ['var(--civic-red-soft)', 'var(--civic-navy-soft)', 'var(--civic-gold)', '#5d8ab4', '#c45565', '#6d9ac0', '#c47a4d', '#6b9e6b']
                        : ['var(--civic-red)', 'var(--civic-navy)', 'var(--civic-gold)', 'var(--civic-navy-deep)', '#8B2232', '#3D5A80', '#A0522D', '#4A6741'])
                      : ['#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899', '#06b6d4', '#ef4444', '#10b981', '#f97316'];
                    const phaseColor = phaseChartColors[phaseIdx % phaseChartColors.length];

                    return (
                      <div key={phase} className={isAhp ? `border ${ahpDark ? 'border-civic-gold/20' : 'border-gray-200'} rounded-lg overflow-hidden` : "border border-matrix-green/20 rounded-lg overflow-hidden"} style={{ borderLeftWidth: 4, borderLeftColor: phaseColor }}>
                        <button
                          onClick={() => togglePhase(phase)}
                          className={isAhp
                            ? `w-full px-6 py-4 flex items-center justify-between ${ahpDark ? 'bg-civic-bg-dark hover:bg-civic-card-dark' : 'bg-gray-50 hover:bg-gray-100'} transition-colors`
                            : "w-full px-6 py-4 flex items-center justify-between bg-black/60 hover:bg-black/80 transition-colors"
                          }
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: phaseColor }} />
                            <div className="text-left">
                              <div className={isAhp ? "font-semibold" : "font-bold text-matrix-green font-mono"} style={isAhp ? { color: ahpDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)' } : undefined}>{phase}</div>
                              <div className="text-xs" style={{ color: ahpDark ? 'var(--civic-muted-2)' : undefined }}>
                                {t('scenarios:card.procedures', { count: phaseProcedures.length })}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <div className={isAhp ? "text-xl font-bold" : "text-xl font-bold text-matrix-green font-mono"} style={isAhp ? { color: ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)', fontFamily: "'Instrument Serif', Georgia, serif" } : undefined}>
                                ${phaseTotal.toLocaleString()}
                              </div>
                              <div className="text-xs" style={{ color: ahpDark ? 'var(--civic-muted-2)' : undefined }}>
                                {(() => {
                                  const actualTotal = selectedHospitalId && hospitalTotal > 0 ? hospitalTotal : nationalTotal;
                                  return actualTotal > 0 ? ((phaseTotal / actualTotal) * 100).toFixed(0) : '0';
                                })()}% of total
                              </div>
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5" style={isAhp ? { color: ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)' } : undefined} />
                            ) : (
                              <ChevronDown className="w-5 h-5" style={isAhp ? { color: ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)' } : undefined} />
                            )}
                          </div>
                        </button>

                        {isExpanded && (
                          <div style={isAhp ? { backgroundColor: ahpDark ? 'rgba(var(--civic-card-dark-rgb),0.5)' : 'rgba(0,0,0,0.02)' } : undefined} className={isAhp ? '' : 'bg-secondary/20'}>
                            <div className={isAhp ? `divide-y ${ahpDark ? 'divide-civic-gold/10' : 'divide-gray-100'}` : 'divide-y divide-matrix-green/10'}>
                              {phaseProcedures.map((proc, idx) => {
                                // DRG toggle: in itemized mode hide the DRG bundle
                                // line; in bundle mode the covered-phase itemized
                                // codes fold into the bundle (shown muted, no price).
                                const isDrgLine = isDrgLineProc(proc);
                                if (isDrgScenario && !includeDrgBundle && isDrgLine) return null;
                                const foldedIntoBundle = isDrgScenario && includeDrgBundle && !isDrgLine && coveredPhases.has(proc.phase);

                                const availableSettings = getAvailableSettings(proc, priceMap);
                                const overrideKey = `${proc.codeType}:${proc.code}`;
                                const currentSetting = settingOverrides.get(overrideKey) || proc.setting || 'unspecified';

                                const { unitPrice, foundInDatabase, usedNationalFallback, hospitalCount, dataSetting, consumerName } = getProcedurePrice(proc, priceMap, isHospitalData, currentSetting, isHospitalData ? nationalAverages : null);
                                const totalPrice = unitPrice * proc.quantity;
                                const isEstimate = !foundInDatabase;
                                const isVariantView = baseProcedureCodes !== null;
                                const isBaseProc = baseProcedureCodes?.has(`${proc.codeType}:${proc.code}`) ?? false;

                                // Get national price for modal (always needed for comparison)
                                const nationalData = nationalAverages ? getProcedurePrice(proc, nationalAverages, false, currentSetting) : null;
                                const nationalUnitPrice = nationalData?.unitPrice ?? null;
                                const nationalHospitalCount = nationalData?.hospitalCount ?? 0;
                                const hospitalUnitPrice = isHospitalData && !usedNationalFallback && foundInDatabase ? unitPrice : null;
                                const displayHospitalCount = nationalData?.hospitalCount ?? hospitalCount;

                                // Build code page link
                                const codePathMap: Record<string, string> = { 'CPT': 'cpt', 'HCPCS': 'hcpcs', 'MS-DRG': 'drg', 'DRG': 'drg', 'REV': 'rev', 'RC': 'rev', 'NDC': 'ndc' };
                                const codePrefix = codePathMap[proc.codeType.toUpperCase()];

                                return (
                                  <div
                                    key={idx}
                                    className={isAhp ? 'px-5 py-3 transition-colors cursor-pointer' : 'px-5 py-3 hover:bg-matrix-green/5 transition-colors cursor-pointer'}
                                    onMouseEnter={isAhp ? (e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = ahpDark ? 'rgba(var(--civic-gold-rgb),0.06)' : 'rgba(var(--civic-navy-rgb),0.04)'; } : undefined}
                                    onMouseLeave={isAhp ? (e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = ''; } : undefined}
                                    style={foldedIntoBundle ? { opacity: 0.55 } : isVariantView && !isBaseProc ? {
                                      backgroundColor: isAhp ? (ahpDark ? 'rgba(var(--civic-red-soft-rgb),0.08)' : 'rgba(var(--civic-red-rgb),0.04)') : 'rgba(239,68,68,0.06)',
                                    } : undefined}
                                    onClick={() => setProcedureModal({
                                      proc,
                                      hospitalPrice: hospitalUnitPrice,
                                      nationalPrice: nationalUnitPrice,
                                      hospitalCount: nationalHospitalCount,
                                      consumerName: consumerName || nationalData?.consumerName || '',
                                    })}
                                  >
                                    {/* Row 1: Code + Price */}
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                                        <Badge variant="outline" className="text-[10px] font-mono px-1.5 py-0" style={ahpDark ? { color: 'var(--civic-muted)', borderColor: 'rgba(var(--civic-muted-rgb),0.3)', backgroundColor: 'rgba(var(--civic-muted-rgb),0.08)' } : undefined}>
                                          {proc.codeType}
                                        </Badge>
                                        <span className="font-mono font-bold text-sm" style={isAhp ? { color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)' } : { color: 'hsl(var(--matrix-green))' }}>
                                          {proc.code}
                                        </span>
                                        {isVariantView && (
                                          <Badge variant="outline" className="text-[10px] px-1.5 py-0"
                                            style={isBaseProc
                                              ? { color: 'var(--civic-gold)', borderColor: 'rgba(var(--civic-gold-rgb),0.4)', backgroundColor: 'rgba(var(--civic-gold-rgb),0.08)' }
                                              : { color: isAhp ? (ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)') : '#ef4444', borderColor: isAhp ? (ahpDark ? 'rgba(var(--civic-red-soft-rgb),0.4)' : 'rgba(var(--civic-red-rgb),0.4)') : 'rgba(239,68,68,0.4)', backgroundColor: isAhp ? (ahpDark ? 'rgba(var(--civic-red-soft-rgb),0.1)' : 'rgba(var(--civic-red-rgb),0.06)') : 'rgba(239,68,68,0.1)' }
                                            }
                                          >
                                            {isBaseProc ? 'Base' : 'Added'}
                                          </Badge>
                                        )}
                                        {proc.quantity > 1 && (
                                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0" style={ahpDark ? { backgroundColor: 'rgba(var(--civic-gold-rgb),0.15)', color: 'var(--civic-gold)', border: 'none' } : undefined}>
                                            ×{proc.quantity}
                                          </Badge>
                                        )}
                                        {availableSettings.length > 1 && (
                                          <Select value={currentSetting} onValueChange={(value) => { setSettingOverrides(new Map(settingOverrides.set(overrideKey, value))); }}>
                                            <SelectTrigger className="w-[110px] h-5 text-[10px]"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                              {availableSettings.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      </div>
                                      <div className="text-right flex-shrink-0">
                                        {foldedIntoBundle ? (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: isAhp ? (ahpDark ? 'rgba(158,197,239,0.14)' : 'rgba(var(--civic-navy-rgb),0.08)') : 'rgba(59,130,246,0.15)', color: isAhp ? (ahpDark ? '#9ec5ef' : 'var(--civic-navy)') : '#3b82f6' }}>
                                            in DRG bundle
                                          </span>
                                        ) : (
                                          <>
                                            <span className="font-mono font-bold text-sm" style={isAhp ? { color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)' } : { color: 'hsl(var(--matrix-green))' }}>
                                              ${totalPrice.toLocaleString()}
                                            </span>
                                            {proc.quantity > 1 && (
                                              <span className="text-[10px] ml-1" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#999' }}>
                                                (${unitPrice.toLocaleString()}&times;{proc.quantity})
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </div>

                                    {/* Row 2: Description */}
                                    <p className="text-xs mt-1 truncate" style={{ color: ahpDark ? '#99aabb' : '#666' }}>
                                      {consumerName || String(proc.description || '')}
                                    </p>

                                    {/* Row 3: Metadata chips + View link */}
                                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                      {/* Setting badge */}
                                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{
                                        backgroundColor: (dataSetting || currentSetting || '').toLowerCase().includes('inpatient')
                                          ? (ahpDark ? 'rgba(var(--civic-red-soft-rgb),0.12)' : 'rgba(var(--civic-red-rgb),0.08)')
                                          : (ahpDark ? 'rgba(var(--civic-navy-soft-rgb),0.12)' : 'rgba(var(--civic-navy-rgb),0.08)'),
                                        color: (dataSetting || currentSetting || '').toLowerCase().includes('inpatient')
                                          ? (ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)')
                                          : (ahpDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)'),
                                      }}>
                                        {(dataSetting || currentSetting || proc.setting || 'unspecified').replace('both', 'IP/OP')}
                                      </span>

                                      {/* Hospital count */}
                                      {displayHospitalCount > 0 && (
                                        <span className="text-[10px]" style={{ color: ahpDark ? 'var(--civic-muted-2)' : '#999' }}>
                                          {displayHospitalCount.toLocaleString()} hospitals
                                        </span>
                                      )}

                                      {isEstimate && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(234,179,8,0.1)', color: '#eab308' }}>est.</span>
                                      )}

                                      {!isEstimate && selectedHospitalId && !usedNationalFallback && (
                                        <span className="text-[10px]" style={{ color: ahpDark ? 'var(--civic-money-soft)' : '#4ade80' }}>Hospital Price</span>
                                      )}
                                      {!isEstimate && (selectedHospitalId ? usedNationalFallback : true) && (
                                        <span className="text-[10px]" style={{ color: ahpDark ? 'var(--civic-navy-soft)' : '#60a5fa' }}>{costStat === 'median' ? 'Natl Median' : 'Natl Avg'}</span>
                                      )}

                                      {/* Spacer */}
                                      <span className="flex-1" />

                                      {/* View code page — on mobile opens a nested
                                          native modal (keeps breadcrumbs); on desktop
                                          navigates to the full code page. */}
                                      {codePrefix && (
                                        <button
                                          className="text-[10px] font-semibold px-2 py-0.5 rounded transition-colors"
                                          style={{
                                            color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)',
                                            backgroundColor: ahpDark ? 'rgba(var(--civic-gold-rgb),0.1)' : 'rgba(var(--civic-navy-rgb),0.06)',
                                            border: `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.25)' : 'rgba(var(--civic-navy-rgb),0.15)'}`,
                                          }}
                                          title={`View ${proc.codeType} ${proc.code} pricing details`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (isMobileViewport && CODE_MODAL_TYPE[proc.codeType.toUpperCase()]) {
                                              openCodeModal(proc.codeType, proc.code);
                                            } else {
                                              navigate(`/${codePrefix}/${proc.code}`);
                                            }
                                          }}
                                        >
                                          View {proc.codeType} →
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Total Summary */}
                  <div
                    className={isAhp ? "border-2 rounded-lg px-6 py-4" : "border-2 border-matrix-green/40 rounded-lg bg-black/60 px-6 py-4"}
                    style={isAhp ? { borderColor: ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)', backgroundColor: ahpDark ? 'rgba(var(--civic-red-soft-rgb),0.08)' : 'rgba(var(--civic-red-rgb),0.04)' } : undefined}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className={isAhp ? "text-lg font-bold" : "text-lg font-bold text-matrix-green"} style={isAhp ? { color: ahpDark ? 'var(--civic-navy-soft)' : 'var(--civic-navy)' } : undefined}>
                            {selectedHospitalId
                              ? t('scenarios:card.estimatedCost')
                              : `${t('scenarios:card.estimatedCost')} (${t(costStat === 'median' ? 'scenarios:card.nationalMedian' : 'scenarios:card.nationalAverage')})`}
                          </div>
                          {/* Avg / Median toggle. Hidden in hospital-specific mode
                              because hospital totals come from per-hospital blended
                              pricing where avg/median isn't a meaningful split. */}
                          {!selectedHospitalId && (
                            <div
                              role="tablist"
                              aria-label={t('scenarios:costSummary.ariaCostStat')}
                              className="inline-flex items-center rounded-md p-0.5"
                              style={{
                                background: ahpDark ? 'rgba(255,255,255,0.06)' : isAhp ? 'rgba(var(--civic-red-rgb),0.06)' : 'rgba(34,197,94,0.08)',
                                border: `1px solid ${ahpDark ? 'rgba(255,255,255,0.10)' : isAhp ? 'rgba(var(--civic-red-rgb),0.18)' : 'rgba(34,197,94,0.30)'}`,
                              }}
                            >
                              {(['avg', 'median'] as const).map((stat) => {
                                const active = costStat === stat;
                                return (
                                  <button
                                    key={stat}
                                    type="button"
                                    role="tab"
                                    aria-selected={active}
                                    onClick={() => handleCostStatChange(stat)}
                                    className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded transition-colors"
                                    style={{
                                      background: active
                                        ? (ahpDark ? 'var(--civic-gold)' : isAhp ? 'var(--civic-red)' : '#22c55e')
                                        : 'transparent',
                                      color: active
                                        ? (ahpDark ? '#1a1a1a' : '#fff')
                                        : (ahpDark ? '#aaa' : isAhp ? '#1B1B1B99' : '#22c55e'),
                                      cursor: 'pointer',
                                    }}
                                    title={stat === 'avg'
                                      ? t('scenarios:costSummary.tooltipAvg')
                                      : t('scenarios:costSummary.tooltipMedian')}
                                  >
                                    {stat === 'avg' ? t('scenarios:costSummary.avg') : t('scenarios:costSummary.median')}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="text-xs mt-1" style={{ color: ahpDark ? 'var(--civic-muted-2)' : undefined }}>
                          {selectedHospitalId
                            ? (hospitalCoverage.percentage < 100 && hospitalCoverage.percentage > 0
                              ? t('scenarios:costSummary.subtitleHospitalPartial', { count: hospitalCoverage.total - hospitalCoverage.hasData })
                              : t('scenarios:costSummary.subtitleHospital'))
                            : (costStat === 'median'
                              ? t('scenarios:costSummary.subtitleMedian')
                              : t('scenarios:costSummary.subtitleAvg'))}
                        </div>
                      </div>
                      <div
                        className={isAhp ? "text-3xl font-bold" : "text-3xl font-bold text-matrix-green font-mono"}
                        style={isAhp ? { color: ahpDark ? 'var(--civic-red-soft)' : 'var(--civic-red)', fontFamily: "'Instrument Serif', Georgia, serif" } : undefined}
                      >
                        ${Math.round(selectedHospitalId && hospitalTotal > 0 ? displayHospitalTotal : displayNationalTotal).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Frequency Breakdown - Only show if scenario has recurring costs */}
                  {selectedScenario && selectedScenario.procedures.some(p => p.frequency && p.frequency !== 'one-time') && (
                    <div className="border-2 border-blue-400/40 rounded-lg bg-black/60 px-6 py-4">
                      <div className="text-lg font-bold text-blue-400 mb-3">Cost Breakdown by Frequency</div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {(() => {
                          const priceMap = selectedHospitalId ? hospitalPrices : nationalAverages;
                          const isHospitalData = !!selectedHospitalId;

                          const fbMap = isHospitalData ? nationalAverages : null;

                          const oneTimeCost = selectedScenario.procedures
                            .filter(p => !p.frequency || p.frequency === 'one-time')
                            .reduce((sum, p) => {
                              const { unitPrice } = getProcedurePrice(p, priceMap, isHospitalData, undefined, fbMap);
                              return sum + (unitPrice * p.quantity);
                            }, 0);

                          const monthlyCost = selectedScenario.procedures
                            .filter(p => p.frequency === 'monthly')
                            .reduce((sum, p) => {
                              const { unitPrice } = getProcedurePrice(p, priceMap, isHospitalData, undefined, fbMap);
                              return sum + (unitPrice * p.quantity);
                            }, 0);

                          const quarterlyCost = selectedScenario.procedures
                            .filter(p => p.frequency === 'quarterly')
                            .reduce((sum, p) => {
                              const { unitPrice } = getProcedurePrice(p, priceMap, isHospitalData, undefined, fbMap);
                              return sum + (unitPrice * p.quantity);
                            }, 0);

                          const semiAnnualCost = selectedScenario.procedures
                            .filter(p => p.frequency === 'semi-annual')
                            .reduce((sum, p) => {
                              const { unitPrice } = getProcedurePrice(p, priceMap, isHospitalData, undefined, fbMap);
                              return sum + (unitPrice * p.quantity);
                            }, 0);

                          const annualCost = selectedScenario.procedures
                            .filter(p => p.frequency === 'annual')
                            .reduce((sum, p) => {
                              const { unitPrice } = getProcedurePrice(p, priceMap, isHospitalData, undefined, fbMap);
                              return sum + (unitPrice * p.quantity);
                            }, 0);

                          return (
                            <>
                              {oneTimeCost > 0 && (
                                <div className="bg-secondary/20 rounded-lg p-3">
                                  <div className="text-xs text-muted-foreground mb-1">One-Time Costs</div>
                                  <div className="font-mono font-bold text-green-400">${oneTimeCost.toLocaleString()}</div>
                                </div>
                              )}
                              {monthlyCost > 0 && (
                                <div className="bg-secondary/20 rounded-lg p-3">
                                  <div className="text-xs text-muted-foreground mb-1">📅 Monthly</div>
                                  <div className="font-mono font-bold text-red-400">${monthlyCost.toLocaleString()}/mo</div>
                                </div>
                              )}
                              {quarterlyCost > 0 && (
                                <div className="bg-secondary/20 rounded-lg p-3">
                                  <div className="text-xs text-muted-foreground mb-1">📅 Quarterly</div>
                                  <div className="font-mono font-bold text-orange-400">${quarterlyCost.toLocaleString()}/qtr</div>
                                </div>
                              )}
                              {semiAnnualCost > 0 && (
                                <div className="bg-secondary/20 rounded-lg p-3">
                                  <div className="text-xs text-muted-foreground mb-1">📅 Semi-Annual</div>
                                  <div className="font-mono font-bold text-yellow-400">${semiAnnualCost.toLocaleString()}/6mo</div>
                                </div>
                              )}
                              {annualCost > 0 && (
                                <div className="bg-secondary/20 rounded-lg p-3">
                                  <div className="text-xs text-muted-foreground mb-1">📅 Annual</div>
                                  <div className="font-mono font-bold text-blue-400">${annualCost.toLocaleString()}/yr</div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
                );
                // Hybrid: inline for the continuous scroll + rail-launched modal on mobile.
                return (
                  <>
                    <div id="code-breakdown" className="scroll-mt-24">{codeBreakdownEl}</div>
                    {isMobileViewport && (
                      <ScenarioSectionModal open={openSection === 'code-breakdown'} onClose={closeSection} urlManaged dark={ahpDark} scenarioName={selectedScenario.name} sectionTitle="Code Breakdown">
                        {codeBreakdownEl}
                      </ScenarioSectionModal>
                    )}
                  </>
                );
              })()}

              {/* Right Column - Price Comparison. On mobile this is its own
                  "Price Comparison" rail tab (full-screen modal); selecting a
                  hospital opens it showing the comparison + that hospital's
                  pricing. Inline two-up on desktop. */}
              <div className="space-y-4">
                {selectedScenario && (() => {
                const priceComparisonEl = (
                <div className="space-y-4">
                {/* USA Average Pricing + National Rankings (combined) */}
                {selectedScenario && (
                  <ScenarioRankings
                    scenarioId={selectedScenario.id}
                    scenarioName={selectedScenario.name}
                    nationalTotal={nationalTotal}
                    costStat={costStat}
                    // Both stats so the card can show avg + median together —
                    // the toggled one dominant, the other beside it. The avg
                    // figure honors the capped/raw scope the same way
                    // nationalTotal does in avg mode.
                    nationalAvgTotal={costScope === 'raw' && cacheAvgRawTotal > 0 ? cacheAvgRawTotal : cacheAvgTotal}
                    nationalMedianTotal={cacheMedianTotal}
                    onCostStatChange={handleCostStatChange}
                    procedureCount={selectedScenario.procedures.length}
                    userLocation={userLocation}
                    radiusMiles={radiusMiles}
                    onHospitalSelect={(hospitalId, hospitalName) => {
                      setSelectedHospitalId(hospitalId);
                      setHospitalNameQuery(hospitalName);
                      setShowHospitalDropdown(false);
                    }}
                    onDetectLocation={detectLocation}
                    onZipLookup={lookupZip}
                    isLocating={isLocating}
                    onRankingsReady={setSeoRankings}
                  />
                )}

                {/* DRG-bundle explainer — rendered OUTSIDE the itemized-coverage
                    gate below, because DRG-only hospitals have sparse itemized
                    coverage (they bill the bundle), so the card below hides for
                    exactly the hospitals this explains. Self-gates on pm='drg'. */}
                <DrgBundleExplainer
                  scenarioId={selectedScenario?.id}
                  selectedHospitalId={selectedHospitalId}
                  hospitalName={hospitalNameQuery || undefined}
                  dark={ahpDark}
                  bundleRow={hospitalPrices?.get('__DRG_BUNDLE__') || null}
                />

                {/* Selected Hospital Pricing */}
                {selectedHospitalId && hospitalCoverage.percentage > 0 ? (
                  <Card
                    id="selected-hospital-pricing"
                    className={isAhp ? 'scroll-mt-24' : 'scroll-mt-24 border-green-400/20 bg-black/40 backdrop-blur-sm'}
                    style={isAhp ? { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' } : undefined}
                  >
                    <CardHeader>
                      <CardTitle
                        className={isAhp ? "text-lg flex items-center gap-2" : "text-lg font-mono text-green-400 flex items-center gap-2"}
                        style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-money-soft)' : 'var(--civic-money)', fontWeight: 400 } : undefined}
                      >
                        <Building2 className="w-5 h-5" style={isAhp ? { color: ahpDark ? 'var(--civic-money-soft)' : 'var(--civic-money)' } : undefined} />
                        Selected Hospital Pricing
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm text-muted-foreground">Total Cost</span>
                            {hospitalCoverage.percentage < 100 && hospitalCoverage.percentage > 0 && (
                              <div className="text-[10px] text-muted-foreground">
                                Includes USA avg for {hospitalCoverage.total - hospitalCoverage.hasData} missing procedures
                              </div>
                            )}
                          </div>
                          <span
                            className={isAhp ? "text-2xl font-bold" : "text-2xl font-bold text-green-400 font-mono"}
                            style={isAhp ? { color: ahpDark ? 'var(--civic-money-soft)' : 'var(--civic-money)', fontFamily: "'Instrument Serif', Georgia, serif" } : undefined}
                          >
                            {displayHospitalTotal > 0 ? `$${Math.round(displayHospitalTotal).toLocaleString()}` : '—'}
                          </span>
                        </div>

                        {/* A zero/absent blended total means this hospital's file didn't
                            overlap the scenario enough to price it — never render that as
                            "$0" with a bogus "100% cheaper" savings banner. */}
                        {displayHospitalTotal <= 0 && (
                          <div className="border rounded-lg p-3 border-amber-500/30 bg-amber-500/10 text-xs text-muted-foreground">
                            This hospital's published file doesn't include enough of this scenario's
                            billing codes to compute a reliable total. Its itemized matches are shown
                            below; use the USA average as the reference figure.
                          </div>
                        )}

                        {/* Savings/Extra Cost */}
                        {displayHospitalTotal > 0 && (() => {
                          const difference = displayHospitalTotal - displayNationalTotal;
                          const isHigher = difference > 0;
                          const percentDiff = displayNationalTotal > 0 ? Math.abs((difference / displayNationalTotal) * 100) : 0;

                          return (
                            <div className={`border rounded-lg p-3 ${
                              isHigher
                                ? 'border-red-500/30 bg-red-500/10'
                                : 'border-green-500/30 bg-green-500/10'
                            }`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold flex items-center gap-1">
                                  {isHigher ? (
                                    <>
                                      <TrendingUp className="w-4 h-4 text-red-500" />
                                      <span className="text-red-500">Higher than Average</span>
                                    </>
                                  ) : (
                                    <>
                                      <TrendingDown className="w-4 h-4 text-green-500" />
                                      <span className="text-green-500">Lower than Average</span>
                                    </>
                                  )}
                                </span>
                                <span className={`text-lg font-bold font-mono ${
                                  isHigher ? 'text-red-500' : 'text-green-500'
                                }`}>
                                  {isHigher ? '+' : '-'}${Math.round(Math.abs(difference)).toLocaleString()}
                                </span>
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {percentDiff.toFixed(1)}% {isHigher ? 'more expensive' : 'cheaper'} than USA average
                              </div>
                            </div>
                          );
                        })()}

                        <div className="text-xs text-muted-foreground">
                          Coverage: {hospitalCoverage.hasData} of {hospitalCoverage.total} procedures ({hospitalCoverage.percentage.toFixed(0)}%)
                        </div>

                        {/* Selected Hospital Pricing — statement-style table.
                            Master 2026-07-12: DRG bundle rows grouped on top,
                            inpatient/outpatient setting shown, cornerstone codes
                            starred with the real ranking-qualification rule,
                            cleaner substituted-price states, invoice-style
                            totals, CSV export. */}
                        {selectedScenario && (() => {
                          type Row = {
                            proc: ScenarioProcedure;
                            hospitalTotal: number; nationalTotal: number;
                            hasHospitalData: boolean; hasNationalData: boolean;
                            priceDiff: number; percentDiff: number;
                          };
                          const buildRow = (proc: ScenarioProcedure): Row => {
                            const normalizedCodeType = proc.codeType.toUpperCase();
                            const setting = proc.setting || 'unspecified';
                            const keyWithSetting = `${normalizedCodeType}:${proc.code}:${setting}`;
                            const keyUnspecified = `${normalizedCodeType}:${proc.code}:unspecified`;
                            const hospitalUnitPrice = hospitalPrices?.get(keyWithSetting) || hospitalPrices?.get(keyUnspecified) || 0;
                            const hasHospitalData = hospitalUnitPrice > 0;
                            const nationalPriceData = nationalAverages?.get(keyWithSetting) || nationalAverages?.get(keyUnspecified);
                            const nationalUnitPrice = nationalPriceData?.price || 0;
                            const hasNationalData = nationalUnitPrice > 0;
                            // Real data only — no AI-seeded fallbacks (em-dash instead).
                            const displayHospitalUnitPrice = hasHospitalData ? hospitalUnitPrice : (hasNationalData ? nationalUnitPrice : 0);
                            const hospitalTotal = displayHospitalUnitPrice * proc.quantity;
                            const nationalTotal = (hasNationalData ? nationalUnitPrice : 0) * proc.quantity;
                            const priceDiff = hospitalTotal - nationalTotal;
                            const percentDiff = nationalTotal > 0 ? ((priceDiff / nationalTotal) * 100) : 0;
                            return { proc, hospitalTotal, nationalTotal, hasHospitalData, hasNationalData, priceDiff, percentDiff };
                          };
                          const isDrg = (p: ScenarioProcedure) => ['DRG', 'MS-DRG', 'APR-DRG'].includes(p.codeType.toUpperCase());
                          const drgRows = selectedScenario.procedures.filter(isDrg).map(buildRow);
                          const otherRows = selectedScenario.procedures.filter(p => !isDrg(p)).map(buildRow);
                          const allRows = [...drgRows, ...otherRows];
                          const hasCornerstones = selectedScenario.procedures.some(p => p.is_required);

                          // ── Statement palette (AHP) / matrix (luigi) ──────
                          const stInk = isAhp ? (ahpDark ? 'var(--civic-parchment)' : 'var(--civic-ink)') : undefined;
                          const stMuted = isAhp ? (ahpDark ? 'rgba(var(--civic-parchment-rgb),0.55)' : 'rgba(var(--civic-ink-rgb),0.55)') : undefined;
                          const stRule = isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.18)' : 'rgba(var(--civic-ink-rgb),0.10)') : undefined;
                          const stHeadBg = isAhp ? (ahpDark ? 'rgba(var(--civic-navy-deeper-rgb),0.55)' : 'rgba(var(--civic-navy-rgb),0.05)') : undefined;
                          const stGold = 'var(--civic-gold)';
                          const stRed = ahpDark ? '#f87171' : 'var(--civic-red)';
                          const stGreen = ahpDark ? 'var(--civic-money-soft)' : '#0F8A5F';
                          const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

                          const exportCsv = () => {
                            const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
                            const rows = [
                              ['Code', 'Type', 'Description', 'Setting', 'Cornerstone', 'Qty', 'Hospital Price', 'USA Average', 'Difference %', 'Price Source'],
                              ...allRows.map(r => [
                                r.proc.code, r.proc.codeType, r.proc.description || '', r.proc.setting || 'unspecified',
                                r.proc.is_required ? 'yes' : '', r.proc.quantity,
                                r.hasHospitalData ? Math.round(r.hospitalTotal) : '',
                                r.hasNationalData ? Math.round(r.nationalTotal) : '',
                                r.hasHospitalData && r.nationalTotal > 0 ? r.percentDiff.toFixed(1) : '',
                                r.hasHospitalData ? 'hospital chargemaster' : (r.hasNationalData ? 'USA average (not in chargemaster)' : 'no published data'),
                              ]),
                              ['TOTAL', '', selectedScenario.name, '', '', '', Math.round(displayHospitalTotal), Math.round(displayNationalTotal), '', ''],
                            ];
                            const csv = rows.map(r => r.map(esc).join(',')).join('\n');
                            const a = document.createElement('a');
                            a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                            a.download = `${selectedScenario.slug || 'scenario'}-hospital-pricing.csv`;
                            a.click();
                            URL.revokeObjectURL(a.href);
                          };

                          const settingChip = (setting?: string) => {
                            const st = (setting || 'unspecified').toLowerCase();
                            if (st === 'unspecified') return null;
                            const inp = st === 'inpatient';
                            return (
                              <span className="inline-block px-1.5 py-px rounded text-[9px] font-semibold uppercase tracking-wide align-middle"
                                style={isAhp ? { color: inp ? 'var(--civic-navy-soft)' : stGreen, border: `1px solid ${inp ? '#7da6d466' : stGreen + '55'}` } : undefined}>
                                {inp ? 'Inpatient' : 'Outpatient'}
                              </span>
                            );
                          };

                          const renderRow = (r: Row, idx: number, inBundle: boolean) => {
                            const { proc } = r;
                            const pfx = proc.codeType === 'CPT' ? 'cpt' : proc.codeType === 'HCPCS' ? 'hcpcs' : (proc.codeType === 'DRG' || proc.codeType === 'MS-DRG') ? 'drg' : proc.codeType === 'REV' ? 'revenue' : 'cpt';
                            return (
                              <tr key={`${proc.codeType}:${proc.code}:${idx}`}
                                style={isAhp ? { borderTop: `1px solid ${stRule}`, background: inBundle ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.05)' : 'rgba(var(--civic-gold-rgb),0.06)') : undefined } : undefined}
                                className={isAhp ? '' : `transition-colors ${r.hasHospitalData ? (r.priceDiff > 0 ? 'bg-red-950/30' : r.priceDiff < 0 ? 'bg-green-950/30' : '') : ''}`}>
                                <td className="px-3 py-2 align-top">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    {proc.is_required && (
                                      <span title="Cornerstone code — defines this scenario for ranking" aria-label="Cornerstone code"
                                        style={{ color: stGold, fontSize: 11 }}>★</span>
                                    )}
                                    <button
                                      className={isAhp ? 'font-mono font-semibold hover:underline' : 'font-mono font-bold text-matrix-green hover:underline'}
                                      style={isAhp ? { color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)', fontSize: 12 } : undefined}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isMobileViewport && CODE_MODAL_TYPE[proc.codeType.toUpperCase()]) openCodeModal(proc.codeType, proc.code);
                                        else navigate(`/${pfx}/${proc.code}`);
                                      }}>
                                      {proc.code}
                                    </button>
                                    {settingChip(proc.setting)}
                                    {proc.quantity > 1 && (
                                      <span className="text-[10px] font-semibold" style={isAhp ? { color: stMuted } : undefined}>×{proc.quantity}</span>
                                    )}
                                  </div>
                                  <div className="text-[10px] leading-snug mt-0.5 max-w-[210px]" style={isAhp ? { color: stMuted } : undefined} title={String(proc.description || '')}>
                                    {String(proc.description || '')}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-right align-top tabular-nums">
                                  {r.hasHospitalData ? (
                                    <span className="font-semibold" style={isAhp ? { color: stInk } : undefined}>{money(r.hospitalTotal)}</span>
                                  ) : r.hasNationalData ? (
                                    <div>
                                      <span style={isAhp ? { color: stMuted } : undefined}>{money(r.hospitalTotal)}</span>
                                      <div className="text-[9px] mt-0.5">
                                        <span className="px-1.5 py-px rounded-full font-semibold uppercase tracking-wide"
                                          style={{ color: stGold, border: `1px solid ${stGold}55` }}>USA avg used</span>
                                      </div>
                                    </div>
                                  ) : (
                                    <div>
                                      <span style={isAhp ? { color: stMuted } : undefined}>—</span>
                                      <div className="text-[9px] mt-0.5">
                                        <span className="px-1.5 py-px rounded-full uppercase tracking-wide" style={{ color: stMuted, border: `1px solid ${stRule}` }}>no data</span>
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right align-top tabular-nums" style={isAhp ? { color: stMuted } : undefined}>
                                  {r.nationalTotal > 0 ? money(r.nationalTotal) : '—'}
                                </td>
                                <td className="px-3 py-2 text-right align-top tabular-nums">
                                  {r.hasHospitalData && r.nationalTotal > 0 ? (
                                    <span className="font-semibold" style={{ color: r.priceDiff > 0 ? stRed : r.priceDiff < 0 ? stGreen : stMuted }}>
                                      {r.priceDiff > 0 ? '+' : ''}{r.percentDiff.toFixed(0)}%
                                    </span>
                                  ) : (
                                    <span style={{ color: stMuted }}>—</span>
                                  )}
                                </td>
                              </tr>
                            );
                          };

                          return (
                            <div className="mt-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: stMuted }}>
                                  Itemized statement · {allRows.length} line item{allRows.length === 1 ? '' : 's'}
                                </span>
                                <button onClick={exportCsv}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors"
                                  style={isAhp ? { color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)', border: `1px solid ${ahpDark ? 'rgba(var(--civic-gold-rgb),0.4)' : 'rgba(var(--civic-navy-rgb),0.3)'}` } : undefined}>
                                  <Download className="w-3 h-3" /> Export CSV
                                </button>
                              </div>
                              <div className="rounded-lg overflow-hidden" style={isAhp ? { border: `1px solid ${stRule}` } : undefined}>
                                <div className="max-h-96 overflow-y-auto thin-scrollbar">
                                  <table className="w-full text-xs">
                                    <thead className="sticky top-0 z-10">
                                      <tr style={isAhp ? { background: ahpDark ? 'var(--civic-navy-deep)' : '#eef1f6' } : undefined} className={isAhp ? '' : 'bg-black/60'}>
                                        {['Code / Procedure', 'This Hospital', 'USA Average', 'Diff'].map((h, i) => (
                                          <th key={h} className={`px-3 py-2 text-[9px] uppercase tracking-[0.12em] font-bold ${i === 0 ? 'text-left' : 'text-right'}`}
                                            style={isAhp ? { color: stMuted } : undefined}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {drgRows.length > 0 && (
                                        <tr>
                                          <td colSpan={4} className="px-3 py-1.5"
                                            style={{ background: ahpDark ? 'rgba(var(--civic-gold-rgb),0.12)' : 'rgba(var(--civic-gold-rgb),0.14)', borderTop: `1px solid ${stRule}` }}>
                                            <span className="text-[9px] uppercase tracking-[0.14em] font-bold" style={{ color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-gold-ink)' }}>
                                              DRG bundle — covers the inpatient stay (room, nursing, and facility charges are inside the bundle, not double-counted)
                                            </span>
                                          </td>
                                        </tr>
                                      )}
                                      {drgRows.map((r, i) => renderRow(r, i, true))}
                                      {drgRows.length > 0 && otherRows.length > 0 && (
                                        <tr>
                                          <td colSpan={4} className="px-3 py-1.5" style={{ background: stHeadBg, borderTop: `1px solid ${stRule}` }}>
                                            <span className="text-[9px] uppercase tracking-[0.14em] font-bold" style={{ color: stMuted }}>
                                              Itemized services outside the bundle
                                            </span>
                                          </td>
                                        </tr>
                                      )}
                                      {otherRows.map((r, i) => renderRow(r, i + drgRows.length, false))}
                                      {/* Invoice-style totals */}
                                      <tr style={{ borderTop: `2px solid ${isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.45)' : 'rgba(var(--civic-navy-rgb),0.35)') : 'rgba(0,255,65,0.3)'}` }}>
                                        <td className="px-3 py-2.5 font-bold text-[11px] uppercase tracking-wide" style={{ color: stInk }}>Total</td>
                                        {/* Reliable-total suppression: a zero hospital total means the file
                                            didn't overlap this scenario — show "—" like the summary card above,
                                            never "$0 / −100%". */}
                                        <td className="px-3 py-2.5 text-right font-bold tabular-nums" style={{ color: stInk, fontSize: 13 }}>{displayHospitalTotal > 0 ? money(displayHospitalTotal) : '—'}</td>
                                        <td className="px-3 py-2.5 text-right font-semibold tabular-nums" style={{ color: stMuted, fontSize: 13 }}>{money(displayNationalTotal)}</td>
                                        <td className="px-3 py-2.5 text-right font-bold tabular-nums"
                                          style={{ color: displayHospitalTotal > displayNationalTotal ? stRed : stGreen, fontSize: 13 }}>
                                          {displayHospitalTotal > 0 && displayNationalTotal > 0 ? `${displayHospitalTotal > displayNationalTotal ? '+' : ''}${(((displayHospitalTotal - displayNationalTotal) / displayNationalTotal) * 100).toFixed(0)}%` : '—'}
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                              {hasCornerstones && (
                                <p className="text-[10px] leading-relaxed mt-2" style={{ color: stMuted }}>
                                  <span style={{ color: stGold }}>★ Cornerstone codes</span> define this scenario. A hospital
                                  only qualifies for this scenario's rankings when its published chargemaster prices
                                  <strong> every cornerstone code</strong> and at least <strong>50% of the full code list</strong> —
                                  rows marked "USA avg used" fill the gaps with national averages so totals stay comparable.
                                </p>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card
                    className={isAhp ? 'opacity-60' : 'border-matrix-green/20 bg-black/40 backdrop-blur-sm opacity-60'}
                    style={isAhp ? { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.1)' : '1px solid rgba(var(--civic-ink-rgb),0.08)', boxShadow: 'none' } : undefined}
                  >
                    <CardContent className="py-8">
                      <div className="text-center space-y-2">
                        <Building2 className="w-8 h-8 mx-auto text-muted-foreground/50" />
                        <p className={`text-sm font-medium ${ahpDark ? 'text-gray-400' : 'text-muted-foreground'}`}>
                          Compare Hospital Pricing
                        </p>
                        <p className={`text-xs ${ahpDark ? 'text-gray-500' : 'text-muted-foreground/70'}`}>
                          Pick a hospital from the Hospitals tab to see how its prices compare here
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
                </div>
                );
                // Hybrid: inline for the continuous scroll + rail-launched modal on mobile.
                return (
                  <>
                    <div id="price-comparison" className="scroll-mt-24">{priceComparisonEl}</div>
                    {isMobileViewport && (
                      <ScenarioSectionModal open={openSection === 'price-comparison'} onClose={closeSection} urlManaged dark={ahpDark} scenarioName={selectedScenario?.name} sectionTitle="Price Comparison">
                        {priceComparisonEl}
                      </ScenarioSectionModal>
                    )}
                  </>
                );
                })()}

                {/* Nearby Hospitals — hybrid: inline block on every viewport
                    (continuous scroll; mobile adds the location bar since the
                    page-level chooser is desktop-only) + the rail-launched
                    full-screen modal on mobile. */}
                <div id="nearby-hospitals" className="scroll-mt-24">
                  {isMobileViewport && (
                    <LocationChooser
                      currentLocation={userLocation}
                      onDetectLocation={detectLocation}
                      onZipLookup={lookupZip}
                      onClearLocation={clearLocation}
                      isLocating={isLocating}
                      error={locationError}
                      radiusMiles={radiusMiles}
                      onRadiusChange={setRadiusMiles}
                    />
                  )}
                  <NearbyHospitalsTable
                    scenarioId={selectedScenario?.id}
                    userLocation={userLocation}
                    radiusMiles={radiusMiles}
                    nationalTotal={nationalTotal}
                    selectedHospitalId={selectedHospitalId}
                    onHospitalSelect={(hospitalId, name) => {
                      setSelectedHospitalId(hospitalId);
                      setHospitalNameQuery(name);
                      setShowHospitalDropdown(false);
                    }}
                    onOpenSection={jumpToHospitalSection}
                  />
                </div>
                {isMobileViewport && (
                  <ScenarioSectionModal
                    open={openSection === 'nearby-hospitals'}
                    onClose={closeSection}
                    urlManaged
                    dark={ahpDark}
                    scenarioName={selectedScenario?.name}
                    sectionTitle="Nearby Hospitals"
                  >
                    {/* Location bar lives here on mobile (moved off the
                        Overview, where it had nothing to act on). */}
                    <LocationChooser
                      currentLocation={userLocation}
                      onDetectLocation={detectLocation}
                      onZipLookup={lookupZip}
                      onClearLocation={clearLocation}
                      isLocating={isLocating}
                      error={locationError}
                      radiusMiles={radiusMiles}
                      onRadiusChange={setRadiusMiles}
                    />
                    <NearbyHospitalsTable
                      scenarioId={selectedScenario?.id}
                      userLocation={userLocation}
                      radiusMiles={radiusMiles}
                      nationalTotal={nationalTotal}
                      selectedHospitalId={selectedHospitalId}
                      fillHeight
                      onHospitalSelect={(hospitalId, name) => {
                        setSelectedHospitalId(hospitalId);
                        setHospitalNameQuery(name);
                        setShowHospitalDropdown(false);
                        // Card tap = jump to Price Comparison, auto-scrolled to
                        // the selected-hospital block (it sits below the
                        // national content). Row buttons pick their own section.
                        jumpToHospitalSection('price-comparison');
                      }}
                      onOpenSection={jumpToHospitalSection}
                    />
                  </ScenarioSectionModal>
                )}

                {/* Dignity's Guide — inline (desktop) / full-screen modal (mobile).
                    AHP only: Dignity is AHP's mascot, not the sibling brands'. */}
                {selectedScenario && brand.key === 'ahp' && (() => {
                  const el = (
                    <DignityEducationTab
                      scenarioId={selectedScenario.id}
                      title={selectedScenario.name}
                      aiConsumerMessage={selectedScenario.aiConsumerMessage}
                      aiClinicalSummary={selectedScenario.aiClinicalSummary}
                      aiCostAnalysis={selectedScenario.aiCostAnalysis}
                      aiPreparationGuide={selectedScenario.aiPreparationGuide}
                      templateContext={scenarioContext(selectedScenario)}
                      relatedCodes={selectedScenario.procedures?.map(p => {
                        const priceMap = selectedHospitalId && hospitalPrices ? hospitalPrices : nationalAverages;
                        const isHospData = !!(selectedHospitalId && hospitalPrices);
                        const { unitPrice, hospitalCount, consumerName } = getProcedurePrice(p, priceMap, isHospData, undefined, isHospData ? nationalAverages : null);
                        const nationalData = nationalAverages ? getProcedurePrice(p, nationalAverages, false) : null;
                        return {
                          code: p.code,
                          type: p.codeType,
                          description: consumerName || p.description,
                          setting: p.setting,
                          hospitalCount: nationalData?.hospitalCount ?? hospitalCount,
                          price: unitPrice * p.quantity,
                          quantity: p.quantity,
                        };
                      })}
                    />
                  );
                  // "What You Should Know" (AdvisorNotes) now lives inside the
                  // Dignity's Guide section rather than floating in the overview.
                  const guideEl = (
                    <div className="space-y-4">
                      {el}
                      <AdvisorNotes scenarioId={selectedScenario.id} />
                    </div>
                  );
                  // Always a modal (mobile + desktop): the full guide is launched
                  // on demand from the Overview "Dignity's Guide" chip / rail, so it
                  // no longer renders as a permanent row that duplicates the top CTA.
                  return (
                    <ScenarioSectionModal open={openSection === 'dignity-guide'} onClose={closeSection} urlManaged dark={ahpDark} scenarioName={selectedScenario.name} sectionTitle="Dignity's Guide">{guideEl}</ScenarioSectionModal>
                  );
                })()}

                {/* International Comparison — inline (desktop) / modal (mobile).
                    Rendered only when this scenario has international rows; otherwise the
                    section, its anchor and its nav entry all stay out of the page. */}
                {selectedScenario && hasIntlData(selectedScenario.id, selectedScenario.uuid) && (() => {
                  const el = (
                    <ScenarioInternationalComparison
                      scenarioId={selectedScenario.id}
                      scenarioName={selectedScenario.name}
                      scenarioSlug={selectedScenario.slug || selectedScenario.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
                      usNationalTotal={nationalTotal}
                      procedures={selectedScenario.procedures}
                    />
                  );
                  // Hybrid: inline for the continuous scroll + rail-launched modal on mobile.
                  return (
                    <>
                      <div id="international" className="scroll-mt-24">{el}</div>
                      {isMobileViewport && (
                        <ScenarioSectionModal open={openSection === 'international'} onClose={closeSection} urlManaged dark={ahpDark} scenarioName={selectedScenario.name} sectionTitle="International Pricing">{el}</ScenarioSectionModal>
                      )}
                    </>
                  );
                })()}

              </div>
            </div>
          )}

          {/* Care Resources & Support Groups — inline (desktop) / modal (mobile) */}
          {selectedScenario && (() => {
            const el = (
              <ScenarioCareResources
                scenarioTags={selectedScenario.tags || []}
                scenarioName={selectedScenario.name}
                userState={userLocation?.state}
              />
            );
            // Hybrid: inline for the continuous scroll + rail-launched modal on mobile.
            return (
              <>
                <ScenarioClassVideo scenarioId={selectedScenario.uuid || selectedScenario.id} dark={ahpDark} />
                <div id="care-resources" className="scroll-mt-24">{el}</div>
                {isMobileViewport && (
                  <ScenarioSectionModal open={openSection === 'care-resources'} onClose={closeSection} urlManaged dark={ahpDark} scenarioName={selectedScenario.name} sectionTitle="Connect to Care">{el}</ScenarioSectionModal>
                )}
              </>
            );
          })()}

          {/* Info Section */}
          {!selectedScenario && (
            <Card
              className={isAhp ? '' : 'border-matrix-green/20 bg-black/40 backdrop-blur-sm'}
              style={isAhp ? { backgroundColor: ahpDark ? 'var(--civic-card-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.2)' : '1px solid rgba(var(--civic-ink-rgb),0.12)', boxShadow: ahpDark ? '0 2px 16px rgba(0,0,0,0.4)' : '0 2px 16px rgba(0,0,0,0.06)' } : undefined}
            >
              <CardHeader>
                <CardTitle
                  className={isAhp ? 'text-xl' : 'text-xl font-mono text-matrix-green'}
                  style={isAhp ? { fontFamily: "'Instrument Serif', Georgia, serif", color: ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)', fontWeight: 400 } : undefined}
                >
                  {t('scenarios:pageTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                {/* On mobile this 5-paragraph explainer is collapsed behind a
                    tap-to-expand summary so it doesn't bury the scenario list;
                    expanded by default on desktop where there's room. */}
                <details open={!isMobileViewport}>
                  <summary className="md:hidden cursor-pointer font-medium list-none flex items-center gap-1.5" style={isAhp ? { color: ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)' } : undefined}>
                    How scenario pricing works
                    <ChevronDown className="w-4 h-4" />
                  </summary>
                  <div className="space-y-4 mt-3 md:mt-0">
                <p>
                  <strong className={isAhp ? '' : 'text-matrix-green'} style={isAhp ? { color: ahpDark ? 'var(--civic-money-soft)' : 'var(--civic-money)' } : undefined}>Real Treatment Journeys:</strong> Each scenario represents
                  a complete medical treatment from start to finish, including all the procedures, tests, medications,
                  and hospital stays you'd actually experience.
                </p>
                <p>
                  <strong className="text-matrix-green">Real Pricing Data:</strong> Costs are calculated from actual
                  hospital chargemaster data. The USA Average shows the national average price from all hospitals in
                  our database. Select a specific hospital to see what that same treatment would cost there.
                </p>
                <p>
                  <strong className="text-matrix-green">Price Spread Analysis:</strong> See exactly how much more (or less)
                  you'd pay at a specific hospital compared to the national average. Price differences can be shocking -
                  the same procedure can cost 2x-10x more at some hospitals.
                </p>
                <p>
                  <strong className="text-matrix-green">Timeline Visualization:</strong> The cost timeline shows you
                  exactly when charges occur during treatment, with different colors representing different code types
                  (CPT, DRG, REV codes). Compare USA Average vs Hospital side-by-side.
                </p>
                <p>
                  <strong className="text-matrix-green">Phases of Care:</strong> Each scenario is broken down by
                  treatment phase (like "Emergency Response" → "Surgery" → "Recovery") so you can see when costs
                  accumulate throughout your care journey.
                </p>
                  </div>
                </details>
                <p className="text-xs text-muted-foreground pt-3 border-t border-matrix-green/20">
                  💡 <strong>Note:</strong> Select a scenario and then choose a hospital to see the complete price comparison.
                  Not all hospitals have pricing data for all procedures - we show what's available.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Plan Summary Floating Widget */}
      <PlanSummaryFloatingWidget />

      {/* Assign-to-family-member picker — all localStorage, nothing sent to a server */}
      <Dialog open={!!assignTarget} onOpenChange={(o) => { if (!o) setAssignTarget(null); }}>
        <DialogContent className="max-w-sm p-0 overflow-hidden border-0" style={isAhp ? { backgroundColor: ahpDark ? 'var(--civic-bg-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.3)' : '1px solid rgba(var(--civic-navy-rgb),0.15)' } : undefined}>
          {assignTarget && (() => {
            const members = familyStorage.getMembers();
            const currentMemberId = assignedNames.get(assignTarget.name) ?? null;
            const relLabel: Record<string, string> = {
              self: t('scenarios:assign.rel.self', { defaultValue: 'You' }),
              spouse: t('scenarios:assign.rel.spouse', { defaultValue: 'Spouse' }),
              child: t('scenarios:assign.rel.child', { defaultValue: 'Child' }),
              parent: t('scenarios:assign.rel.parent', { defaultValue: 'Parent' }),
              sibling: t('scenarios:assign.rel.sibling', { defaultValue: 'Sibling' }),
              other: t('scenarios:assign.rel.other', { defaultValue: 'Family' }),
            };
            return (
              <>
                <div className="px-5 py-4" style={{ backgroundColor: isAhp ? 'var(--civic-navy)' : undefined }}>
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-white/80" />
                    <p className="text-sm font-semibold text-white">
                      {t('scenarios:assign.title', { defaultValue: 'Assign to family member' })}
                    </p>
                  </div>
                  <p className="text-xs text-white/70 mt-1 leading-snug">{assignTarget.name}</p>
                </div>
                <div className="p-3 space-y-1.5 max-h-72 overflow-y-auto">
                  {members.map((m) => {
                    const isCurrent = currentMemberId === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => handleAssignToMember(assignTarget, m.id)}
                        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-left transition-colors"
                        style={{
                          backgroundColor: isCurrent ? 'rgba(184,134,11,0.14)' : (ahpDark ? 'rgba(255,255,255,0.04)' : 'rgba(var(--civic-ink-rgb),0.03)'),
                          border: `1px solid ${isCurrent ? 'rgba(184,134,11,0.5)' : (ahpDark ? 'rgba(255,255,255,0.08)' : 'rgba(var(--civic-ink-rgb),0.08)')}`,
                        }}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <User className="w-4 h-4 shrink-0" style={{ color: isCurrent ? '#B8860B' : (ahpDark ? 'var(--civic-muted)' : '#6b7280') }} />
                          <span className="text-sm font-medium truncate" style={{ color: ahpDark ? '#e5e7eb' : 'var(--civic-ink)' }}>
                            {familyStorage.getDisplayName(m)}
                          </span>
                          <span className="text-[10px] uppercase tracking-wider shrink-0" style={{ color: ahpDark ? 'var(--civic-muted)' : '#9ca3af' }}>
                            {relLabel[m.relationship] || m.relationship}
                          </span>
                        </span>
                        {isCurrent && <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: '#B8860B' }} />}
                      </button>
                    );
                  })}
                  {currentMemberId && (
                    <button
                      type="button"
                      onClick={() => handleAssignToMember(assignTarget, null)}
                      className="w-full px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors"
                      style={{ color: '#b91c1c', backgroundColor: 'rgba(185,28,28,0.06)', border: '1px solid rgba(185,28,28,0.25)' }}
                    >
                      {t('scenarios:assign.unassign', { defaultValue: 'Remove from plan' })}
                    </button>
                  )}
                </div>
                <div className="px-4 pb-4 pt-1 flex items-center justify-between">
                  <p className="text-[10px]" style={{ color: ahpDark ? 'var(--civic-muted)' : '#9ca3af' }}>
                    {t('scenarios:assign.localOnly', { defaultValue: 'Saved on this device only' })}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setAssignTarget(null); navigate('/family'); }}
                    className="text-xs font-semibold inline-flex items-center gap-1"
                    style={{ color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)') : undefined }}
                  >
                    {t('scenarios:assign.manageFamily', { defaultValue: 'Manage family' })}
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Procedure Detail Modal */}
      <Dialog open={!!procedureModal} onOpenChange={() => setProcedureModal(null)}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-0" style={isAhp ? { backgroundColor: ahpDark ? 'var(--civic-bg-dark)' : '#fff', border: ahpDark ? '1px solid rgba(var(--civic-gold-rgb),0.3)' : '1px solid rgba(var(--civic-navy-rgb),0.15)' } : undefined}>
          {procedureModal && (() => {
            const { proc, hospitalPrice, nationalPrice, hospitalCount, consumerName } = procedureModal;
            const diff = hospitalPrice != null && nationalPrice != null ? hospitalPrice - nationalPrice : null;
            const pctDiff = diff != null && nationalPrice ? (diff / nationalPrice) * 100 : null;
            return (
              <>
                {/* Header bar */}
                <div className="px-5 py-4" style={{ backgroundColor: isAhp ? 'var(--civic-navy)' : undefined }}>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs font-mono border-white/30 text-white/90">{proc.codeType}</Badge>
                    <span className="font-mono font-bold text-white text-lg">{proc.code}</span>
                  </div>
                  <p className="text-sm text-white/80 leading-snug">{consumerName || proc.description}</p>
                  {consumerName && proc.description !== consumerName && (
                    <p className="text-xs text-white/50 mt-1 font-mono">{String(proc.description || '')}</p>
                  )}
                  {proc.quantity > 1 && (
                    <p className="text-xs text-white/60 mt-1">Quantity: ×{proc.quantity}</p>
                  )}
                </div>

                {/* Price comparison */}
                <div className="px-5 py-5 space-y-4">
                  {/* Hospital Price */}
                  {hospitalPrice != null ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)') : undefined }}>Hospital Rate</p>
                        <p className="text-xs text-muted-foreground">This hospital's sticker price</p>
                      </div>
                      <span className="text-2xl font-bold font-mono" style={{ color: isAhp ? (ahpDark ? 'var(--civic-warm)' : 'var(--civic-ink)') : undefined }}>
                        ${hospitalPrice.toLocaleString()}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)') : undefined }}>Hospital Rate</p>
                        <p className="text-xs text-muted-foreground">Not in this hospital's chargemaster</p>
                      </div>
                      <span className="text-lg text-muted-foreground">—</span>
                    </div>
                  )}

                  <div className="border-t" style={{ borderColor: isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(var(--civic-navy-rgb),0.1)') : undefined }} />

                  {/* National Average */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: isAhp ? (ahpDark ? 'var(--civic-gold)' : 'var(--civic-navy)') : undefined }}>National Average</p>
                      <p className="text-xs text-muted-foreground">
                        {hospitalCount > 0 ? `Based on ${hospitalCount.toLocaleString()} hospital${hospitalCount !== 1 ? 's' : ''}` : 'No published pricing'}
                      </p>
                    </div>
                    <span className="text-2xl font-bold font-mono" style={{ color: isAhp ? 'var(--civic-blue)' : undefined }}>
                      {nationalPrice != null ? `$${nationalPrice.toLocaleString()}` : '—'}
                    </span>
                  </div>

                  {/* Difference badge */}
                  {diff != null && pctDiff != null && (
                    <>
                      <div className="border-t" style={{ borderColor: isAhp ? (ahpDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(var(--civic-navy-rgb),0.1)') : undefined }} />
                      <div className="flex items-center justify-between rounded-lg px-4 py-3" style={{
                        backgroundColor: diff > 0
                          ? (isAhp ? 'rgba(var(--civic-red-rgb),0.1)' : 'rgba(239,68,68,0.1)')
                          : diff < 0
                          ? 'rgba(34,197,94,0.1)'
                          : (ahpDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)')
                      }}>
                        <div className="flex items-center gap-2">
                          {diff > 0 ? (
                            <TrendingUp className="w-5 h-5" style={{ color: isAhp ? 'var(--civic-red)' : '#ef4444' }} />
                          ) : diff < 0 ? (
                            <TrendingDown className="w-5 h-5 text-green-500" />
                          ) : null}
                          <span className="text-sm font-medium" style={{ color: diff > 0 ? (isAhp ? 'var(--civic-red)' : '#ef4444') : diff < 0 ? '#22c55e' : undefined }}>
                            {diff > 0 ? 'Above Average' : diff < 0 ? 'Below Average' : 'At Average'}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-lg font-bold font-mono" style={{ color: diff > 0 ? (isAhp ? 'var(--civic-red)' : '#ef4444') : diff < 0 ? '#22c55e' : undefined }}>
                            {diff > 0 ? '+' : ''}{pctDiff.toFixed(0)}%
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {diff > 0 ? '+' : '-'}${Math.abs(diff).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Create Scenario Dialog */}
      <CreateScenarioDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onScenarioCreated={(scenarioId) => {
          // Navigate to the new scenario
          const userScenarios = scenarioStorage.getUserScenarios();
          const newScenario = userScenarios.find(s => s.id === scenarioId);

          if (newScenario) {
            navigate(`/scenarios/${newScenario.uuid}/${newScenario.slug}`);
          }
        }}
      />
      </div>
    </>
  );
};

export default Scenarios;
